import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, FileText, DollarSign, Wrench, BarChart3, Calculator, Mail, Settings, LogOut, Building2, Calendar, Search, Filter, Plus, Eye, Edit, Trash2, Download, Send, ChevronDown, ChevronLeft, ChevronRight, TrendingUp, AlertCircle, CheckCircle, Check, Clock, X, User, Users, Save, Bell, MessageSquare, Upload, Image, Sun, Moon, Receipt, Menu, CreditCard, RefreshCw } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { authService, userService, contractsService, paymentsService, incidentsService, increasesService, indicesService, receiptsService, logoService, messagesService, boletasService, mpService, remindersService } from './services/api';
import { exportToExcel, exportContractsToExcel, exportPaymentsToExcel } from './utils/excelExport';
  
// Datos mock - Í­ndices del BCRA
const mockIndices = [
  { date: '2024-01', ICL: 15.10, IPC: 3150 },
  { date: '2024-02', ICL: 15.55, IPC: 3400 },
  { date: '2024-03', ICL: 16.02, IPC: 3730 },
  { date: '2024-04', ICL: 16.50, IPC: 4020 },
  { date: '2024-05', ICL: 16.99, IPC: 4270 },
  { date: '2024-06', ICL: 17.50, IPC: 4480 },
  { date: '2024-07', ICL: 18.03, IPC: 4700 },
  { date: '2024-08', ICL: 18.57, IPC: 4950 },
  { date: '2024-09', ICL: 19.13, IPC: 5210 },
  { date: '2024-10', ICL: 19.71, IPC: 5470 },
  { date: '2024-11', ICL: 20.30, IPC: 5750 },
  { date: '2024-12', ICL: 20.91, IPC: 6030 },
  { date: '2025-01', ICL: 21.54, IPC: 6310 },
  { date: '2025-02', ICL: 22.19, IPC: 6590 },
  { date: '2025-03', ICL: 22.85, IPC: 6870 },
  { date: '2025-04', ICL: 23.54, IPC: 7150 },
  { date: '2025-05', ICL: 24.24, IPC: 7430 },
  { date: '2025-06', ICL: 24.97, IPC: 7720 },
  { date: '2025-07', ICL: 25.72, IPC: 8010 },
  { date: '2025-08', ICL: 26.49, IPC: 8310 },
  { date: '2025-09', ICL: 27.28, IPC: 8610 },
  { date: '2025-10', ICL: 28.10, IPC: 8920 },
  { date: '2025-11', ICL: 28.94, IPC: 9230 },
  { date: '2025-12', ICL: 29.81, IPC: 9550 },
  { date: '2026-01', ICL: 30.56, IPC: 10413 },
  { date: '2026-02', ICL: 31.33, IPC: 10720 },
  { date: '2026-03', ICL: 31.94, IPC: 10935 },
];

// ============================================
// FUNCIONES AUXILIARES PARA CÁLCULOS DEL DASHBOARD
// ============================================

// Calcular contratos activos
const getActiveContractsCount = (contracts) => {
  return contracts.filter(c => c.status === 'ACTIVO').length;
};

// Calcular contratos próximos a vencer (dentro de los próximos 30 días)
const getExpiringContractsCount = (contracts) => {
  const today = new Date();
  const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  
  return contracts.filter(c => {
    if (c.status !== 'ACTIVO' || !c.endDate) return false;
    const endDate = new Date(c.endDate);
    return endDate >= today && endDate <= thirtyDaysFromNow;
  }).length;
};

// Calcular ingresos mensuales totales (pagos del mes actual con status PAGADO)
const getMonthlyIncome = (payments) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  return payments
    .filter(p => {
      if (p.status !== 'PAGADO' || !p.paidDate) return false;
      const paidDate = new Date(p.paidDate);
      return paidDate.getMonth() === currentMonth && paidDate.getFullYear() === currentYear;
    })
    .reduce((sum, p) => {
      const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
};

// Calcular total de pagos cobrados
const getTotalPaidAmount = (payments) => {
  return payments
    .filter(p => p.status === 'PAGADO')
    .reduce((sum, p) => {
      const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
};

// Calcular pagos pendientes (PENDIENTE o VENCIDO)
const getPendingPaymentsCount = (payments) => {
  return payments.filter(p => p.status === 'PENDIENTE' || p.status === 'VENCIDO').length;
};

// Calcular total pendiente de cobro
const getTotalPendingAmount = (payments) => {
  return payments
    .filter(p => p.status === 'PENDIENTE' || p.status === 'VENCIDO')
    .reduce((sum, p) => {
      const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
};

// Calcular honorarios generados (comisiones de pagos cobrados)
const getTotalCommissions = (payments, contracts) => {
  return payments
    .filter(p => p.status === 'PAGADO')
    .reduce((sum, p) => {
      const contract = contracts.find(c => c.id === p.contractId);
      const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
      const validAmount = isNaN(amount) ? 0 : amount;
      const commissionPercent = parseFloat(contract?.commissionPercent || 0);
      const commission = contract ? (validAmount * (isNaN(commissionPercent) ? 0 : commissionPercent) / 100) : 0;
      return sum + commission;
    }, 0);
};

// Calcular pagos vencidos
const getOverduePaymentsCount = (payments) => {
  return payments.filter(p => p.status === 'VENCIDO').length;
};

// Agrupar pagos por mes para el LineChart de ingresos
const getMonthlyIncomeData = (payments) => {
  const monthlyData = {};
  
  payments
    .filter(p => p.status === 'PAGADO' && p.paidDate)
    .forEach(p => {
      const date = new Date(p.paidDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          ingresos: 0
        };
      }
      
      const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
      monthlyData[monthKey].ingresos += (isNaN(amount) ? 0 : amount);
    });
  
  // Convertir a array y ordenar por fecha
  return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
};

// Agrupar contratos por tipo de propiedad para BarChart
const getContractsByPropertyType = (contracts) => {
  const typeCount = {};
  
  contracts
    .filter(c => c.status === 'ACTIVO')
    .forEach(c => {
      const type = c.propertyType || 'Sin definir';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });
  
  return Object.entries(typeCount).map(([type, count]) => ({
    type,
    cantidad: count
  }));
};

// Agrupar pagos por estado para PieChart
const getPaymentsByStatus = (payments) => {
  const statusCount = {};

  payments.forEach(p => {
    const status = p.status || 'DESCONOCIDO';
    statusCount[status] = (statusCount[status] || 0) + 1;
  });

  // Orden fijo con colores semánticos (verde=pagado, azul=pendiente, amarillo=parcial, rojo=vencido)
  const config = [
    { name: 'PAGADO',      color: '#10b981' }, // verde
    { name: 'PAGO PARCIAL',color: '#f59e0b' }, // amarillo
    { name: 'PENDIENTE',   color: '#3b82f6' }, // azul
    { name: 'VENCIDO',     color: '#ef4444' }, // rojo
  ];

  return config
    .filter(s => statusCount[s.name] > 0)
    .map(s => ({ name: s.name, value: statusCount[s.name], color: s.color }));
};

const App = () => {
  // Estados principales — se restauran desde localStorage al recargar
  const navigate = useNavigate();
  const location = useLocation();

  // Mapeo slug URL ↔ view ID
  const SLUG_TO_VIEW = {
    '':              'dashboard',
    'dashboard':     'dashboard',
    'contratos':     'contracts',
    'pagos':         'payments',
    'incidencias':   'incidents',
    'boletas':          'boletas',
    'notificaciones':   'notifications',
    'reportes':         'reports',
    'indices':          'indices',
    'usuarios':         'users',
    'suscripcion':      'suscripcion',
    'configuracion':    'settings',
  };
  const VIEW_TO_SLUG = {
    'dashboard':      'dashboard',
    'contracts':      'contratos',
    'payments':       'pagos',
    'incidents':      'incidencias',
    'boletas':        'boletas',
    'notifications':  'notificaciones',
    'reports':        'reportes',
    'indices':        'indices',
    'users':          'usuarios',
    'suscripcion':    'suscripcion',
    'settings':       'configuracion',
  };

  // Leer la sección inicial desde la URL
  const getViewFromPath = (path) => {
    const slug = path.replace('/app', '').replace(/^\//, '');
    return SLUG_TO_VIEW[slug] || 'dashboard';
  };

  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('token'));
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('user');
    try {
      const u = saved ? JSON.parse(saved) : null;
      if (!u) return null;
      // Normalizar campos legacy (name→nombre, role→rol, ADMIN→admin)
      return {
        ...u,
        nombre: u.nombre || u.name || '',
        rol: (u.rol || u.role || '').toLowerCase(),
      };
    } catch { return null; }
  });
  const [currentView, setCurrentView] = useState(() => getViewFromPath(window.location.pathname));

  // Sincronizar URL → currentView cuando el usuario navega con back/forward
  useEffect(() => {
    const view = getViewFromPath(location.pathname);
    setCurrentView(view);
  }, [location.pathname]);

  // Función para cambiar de sección actualizando también la URL
  const navigateTo = useCallback((view) => {
    const slug = VIEW_TO_SLUG[view] || 'dashboard';
    setCurrentView(view);
    navigate('/app/' + slug, { replace: false });
  }, [navigate]);
  const [showModal, setShowModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedContractForPayments, setSelectedContractForPayments] = useState(null);

  // Notificación de retorno desde MercadoPago
  const [mpNotification, setMpNotification] = useState(null); // { type: 'success'|'error'|'pending', msg }

  // Función para refrescar datos del usuario desde el servidor (ej: tras pago exitoso)
  const refreshCurrentUser = useCallback(async () => {
    try {
      const data = await mpService.refreshMe();
      if (data.success && data.user) {
        const updated = {
          ...currentUser,
          plan_code: data.user.plan_code,
          fecha_vencimiento: data.user.fecha_vencimiento,
          in_grace_period: data.user.in_grace_period,
        };
        setCurrentUser(updated);
        localStorage.setItem('user', JSON.stringify(updated));
      }
    } catch (err) {
      console.error('Error refrescando datos del usuario:', err);
    }
  }, [currentUser]);

  // Detectar retorno desde MercadoPago (?pago=exitoso|fallido|pendiente)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pagoStatus = params.get('pago');
    if (!pagoStatus) return;

    // Limpiar el query param de la URL sin recargar la página
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);

    if (pagoStatus === 'exitoso') {
      setMpNotification({
        type: 'success',
        msg: '¡Pago recibido! Tu suscripción se actualizará en los próximos minutos. Si no se refleja, recargá la página.'
      });
      // Refrescar datos del usuario tras un breve delay (el webhook puede tardar unos segundos)
      setTimeout(() => refreshCurrentUser(), 3000);
    } else if (pagoStatus === 'fallido') {
      setMpNotification({
        type: 'error',
        msg: 'El pago no pudo procesarse. Podés intentarlo de nuevo desde Planes y precios.'
      });
    } else if (pagoStatus === 'pendiente') {
      setMpNotification({
        type: 'pending',
        msg: 'Tu pago está pendiente de confirmación. Te avisaremos cuando se acredite.'
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tema oscuro/claro
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('rentiaTheme') !== 'light');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modeToggleKey, setModeToggleKey] = useState(0);

  // Recordatorios de pago (manuales del backend)
  const [reminders, setReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const pendingReminders = reminders.filter(r => !r.completed);

  // Alertas descartadas (persistidas en localStorage)
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rentia_alerts_dismissed') || '{}'); }
    catch { return {}; }
  });

  const dismissPaymentAlert = useCallback((key, wasAviso) => {
    const updated = { ...dismissedAlerts, [key]: { at: Date.now(), wasAviso } };
    setDismissedAlerts(updated);
    localStorage.setItem('rentia_alerts_dismissed', JSON.stringify(updated));
  }, [dismissedAlerts]);

  const undismissPaymentAlert = useCallback((key) => {
    const updated = { ...dismissedAlerts };
    delete updated[key];
    setDismissedAlerts(updated);
    localStorage.setItem('rentia_alerts_dismissed', JSON.stringify(updated));
  }, [dismissedAlerts]);

  const loadReminders = async () => {
    try {
      const data = await remindersService.getAll();
      if (data.success) setReminders(data.reminders);
    } catch {}
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadReminders();
      const interval = setInterval(loadReminders, 5 * 60 * 1000); // cada 5 min
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  const handleCompleteReminder = async (id) => {
    try {
      await remindersService.complete(id);
      setReminders(prev => prev.map(r => r.id === id ? { ...r, completed: true, completed_at: new Date() } : r));
    } catch {}
  };

  const handleDeleteReminder = async (id) => {
    try {
      await remindersService.delete(id);
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch {}
  };
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('rentiaTheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);
  const toggleMode = () => { setDarkMode(d => !d); setModeToggleKey(k => k + 1); };

  // Estados para envío de mensajes
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageConfig, setMessageConfig] = useState({
    type: '', // 'email' o 'whatsapp'
    contract: null,
    subject: 'Recordatorio de Pago',
    message: ''
  });

  // Contratos reales desde PostgreSQL
  const [contracts, setContracts] = useState([]);
  const [deletedContracts, setDeletedContracts] = useState([]);
  const [contractIncreases, setContractIncreases] = useState({});
  // Flag: true cuando contratos Y pagos ya cargaron (evita alertas falsas durante carga)
  const [alertsReady, setAlertsReady] = useState(false);

  // Funciones para actualizar listas de contratos
  const refreshContracts = async () => {
    try {
      const data = await contractsService.getAll();
      setContracts(data.contracts || []);
    } catch (err) {
      console.error('Error cargando contratos:', err);
    }
  };

  const refreshDeleted = async () => {
    try {
      const data = await contractsService.getDeleted();
      setDeletedContracts(data.contracts || []);
    } catch (err) {
      console.error('Error cargando contratos eliminados:', err);
    }
  };
// Función para cargar pagos desde PostgreSQL
const refreshPayments = async () => {
  try {
    const data = await paymentsService.getAll();
    setPayments(data.payments || []);
  } catch (err) {
    console.error('Error cargando pagos:', err);
  }
};

// Función para cargar incidencias desde PostgreSQL
const refreshIncidents = async () => {
  try {
    const data = await incidentsService.getAll();
    setIncidents(data.incidents || []);
  } catch (err) {
    console.error('Error cargando incidencias:', err);
  }
};

// NUEVO: Función para cargar índices del BCRA
const refreshIndices = async () => {
  try {
    console.log('Cargando índices del BCRA...');
    const data = await indicesService.getHistorico(24); // Últimos 24 meses

    if (data.success && data.indices && data.indices.length > 0) {
      setIndices(data.indices);
      setIndicesSource(data.source); // 'bcra', 'mock', o 'mixed'
      setIndicesLastUpdate(data.lastUpdate || new Date().toISOString());
      console.log(`${data.indices.length} meses de índices cargados desde: ${data.source}`);
    } else {
      console.warn('No se pudieron cargar índices, usando valores mock');
      setIndices(mockIndices);
      setIndicesSource('mock');
      setIndicesLastUpdate(new Date().toISOString());
    }
  } catch (err) {
    console.error('Error cargando índices:', err);
    console.warn('Usando índices mock por error');
    setIndices(mockIndices);
    setIndicesSource('mock');
    setIndicesLastUpdate(new Date().toISOString());
  }
};

useEffect(() => {
  if (isLoggedIn) {
    setAlertsReady(false);
    // Cargar contratos y pagos juntos antes de mostrar alertas
    Promise.all([refreshContracts(), refreshPayments()]).then(() => {
      setAlertsReady(true);
    });
    refreshDeleted();
    refreshIncidents();
    refreshIndices();
    loadEmailConfig();
  }
}, [isLoggedIn]);

// Calcular aumentos sugeridos para todos los contratos usando Argly daily ICL (backend)
useEffect(() => {
  if (!contracts.length) return;
  const fetchAll = async () => {
    const results = {};
    await Promise.all(
      contracts.map(async (contract) => {
        try {
          const response = await increasesService.calculateAvailable(contract.id);
          if (response.success && response.canApply && response.data) {
            results[contract.id] = {
              percentage: parseFloat(response.data.percentage).toFixed(2),
              newAmount: response.data.newAmount,
              lastUpdate: response.data.currentDate,
            };
          } else if (response.success && !response.canApply) {
            results[contract.id] = {
              percentage: 0,
              newAmount: contract.baseRent,
              lastUpdate: null,
              message: response.message,
            };
          }
        } catch { /* silently skip */ }
      })
    );
    setContractIncreases(results);
  };
  fetchAll();
}, [contracts]);

// Función para cargar la configuración de email del usuario
const loadEmailConfig = async () => {
  try {
    const response = await userService.getEmailConfig();
    if (response.success) {
      setEmailConfig({ ...response.emailConfig, email_password: '' });
    }
  } catch (err) {
    console.error('Error cargando configuración de email:', err);
  }
};

// Función para guardar la configuración de email
const saveEmailConfig = async () => {
  try {
    // Validaciones
    if (emailConfig.email_enabled) {
      if (!emailConfig.email_user) {
        alert('Debes ingresar un email para enviar');
        return;
      }
      if (!emailConfig.email_password && !emailConfig.hasPassword) {
        alert('Debes ingresar una contraseña de aplicación');
        return;
      }
    }

    const response = await userService.updateEmailConfig(emailConfig);
    if (response.success) {
      alert('Configuración de email guardada exitosamente');
      // Recargar para actualizar hasPassword
      await loadEmailConfig();
    } else {
      alert('Error al guardar configuración: ' + (response.error || 'Error desconocido'));
    }
  } catch (err) {
    console.error('Error guardando configuración de email:', err);
    alert('Error al guardar configuración de email');
  }
};

  // Guardar contrato nuevo o actualizar existente
const saveContract = async (formData) => {
  if (!formData.propertyAddress || !formData.tenantName || !formData.ownerName || !formData.baseRent) {
    alert('Por favor complete los campos obligatorios');
    return;
  }

  const payload = {
    propertyAddress: formData.propertyAddress,
    propertyType: formData.propertyType || 'Departamento',
    ownerName: formData.ownerName,
    ownerDNI: formData.ownerDNI || '',
    ownerEmail: formData.ownerEmail || '',
    ownerPhone: formData.ownerPhone || '',
    tenantName: formData.tenantName,
    tenantDNI: formData.tenantDNI || '',
    tenantEmail: formData.tenantEmail || '',
    tenantPhone: formData.tenantPhone || '',
    startDate: formData.startDate,
    endDate: formData.endDate,
    baseRent: Number(formData.baseRent),
    currency: formData.currency || 'ARS',
    indexType: formData.indexType || 'ICL',
    increaseFrequencyMonths: Number(formData.increaseFrequencyMonths || 12),
    paymentFrequencyMonths: Number(formData.paymentFrequencyMonths || 1),
    commissionPercent: Number(formData.commissionPercent ?? 10),
    status: formData.status || 'ACTIVO',
  };

  try {
    if (editingItem) {
      await contractsService.update(editingItem.id, payload);
      alert('Contrato actualizado correctamente');
    } else {
      await contractsService.create(payload);
      alert('Contrato creado correctamente');
    }

    await refreshContracts();
    await refreshDeleted();
    setShowModal(null);
    setEditingItem(null);
  } catch (error) {
    console.error('Error al guardar el contrato:', error);
    alert('Hubo un error al guardar el contrato. Ver consola.');
  }
};

// Función para generar recibo de pago
const generateReceipt = async (payment) => {
  const contract = contracts.find(c => c.id === payment.contractId);
  if (!contract) {
    alert('Error: No se encontró el contrato asociado');
    return;
  }

  try {
    // Generate PDF receipt
    const result = await receiptsService.generatePDF(payment.id);

    if (result.success) {
      // Download the generated PDF
      const blob = await receiptsService.downloadReceipt(payment.id, 'pdf');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recibo-${payment.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Recibo generado y descargado exitosamente');

      // Refresh payments to get updated receipt_url
      await refreshPayments();
    }
  } catch (error) {
    console.error('Error generating receipt:', error);
    alert('Error al generar el recibo: ' + error.message);
  }
};

// Función para subir foto de recibo
const uploadReceiptPhoto = async (payment) => {
  // Create file input
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';

  input.onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 10MB.');
      return;
    }

    try {
      const result = await receiptsService.uploadPhoto(payment.id, file);

      if (result.success) {
        alert('Foto de recibo subida exitosamente');
        // Refresh payments to show the new photo
        await refreshPayments();
      }
    } catch (error) {
      console.error('Error uploading receipt photo:', error);
      alert('Error al subir la foto: ' + error.message);
    }
  };

  input.click();
};

// Función para ver foto de recibo
const viewReceiptPhoto = async (payment) => {
  if (!payment.receiptPhoto) {
    alert('No hay foto de recibo disponible');
    return;
  }

  try {
    const photoUrl = receiptsService.getPhotoUrl(payment.receiptPhoto);
    window.open(photoUrl, '_blank');
  } catch (error) {
    console.error('Error viewing receipt photo:', error);
    alert('Error al ver la foto del recibo');
  }
};

// Función para eliminar foto de recibo
const deleteReceiptPhoto = async (payment) => {
  if (!payment.receiptPhoto) {
    alert('No hay foto de recibo para eliminar');
    return;
  }

  if (!window.confirm('¿Está seguro que desea eliminar la foto del recibo?')) {
    return;
  }

  try {
    const result = await receiptsService.deletePhoto(payment.id);

    if (result.success) {
      alert('Foto de recibo eliminada exitosamente');
      await refreshPayments();
    }
  } catch (error) {
    console.error('Error deleting receipt photo:', error);
    alert('Error al eliminar la foto: ' + error.message);
  }
};

// Función para enviar recordatorios
const sendReminder = (contract, type) => {
  const defaultMessage = type === 'email'
    ? `Estimado/a ${contract.tenantName},\n\nLe recordamos que tiene pagos pendientes para la propiedad ubicada en ${contract.propertyAddress}.\n\nPor favor, regularice su situación a la brevedad.\n\nSaludos cordiales.`
    : `Hola ${contract.tenantName}! \n\nTe recordamos que tenés pagos pendientes para ${contract.propertyAddress}.\n\nPor favor, regularizá tu situación a la brevedad. ¡Gracias! `;

  setMessageConfig({
    type,
    contract,
    subject: 'Recordatorio de Pago - ' + contract.propertyAddress,
    message: defaultMessage
  });
  setShowMessageModal(true);
};

// Función para enviar mensajes (Email y WhatsApp)
const handleSendMessage = async () => {
  const { type, contract, subject, message } = messageConfig;

  if (!message.trim()) {
    alert('Por favor, ingrese un mensaje');
    return;
  }

  if (type === 'whatsapp') {
    // Enviar por WhatsApp usando WhatsApp Web
    const phone = contract.tenantPhone.replace(/\D/g, ''); // Remover todo excepto números
    const text = encodeURIComponent(message);
    const url = `https://wa.me/${phone}?text=${text}`;
    window.open(url, '_blank');
    alert(`WhatsApp abierto para ${contract.tenantName}`);
  } else if (type === 'email') {
    // Enviar por Email usando el backend
    try {
      const response = await messagesService.sendEmail(
        contract.tenantEmail,
        subject,
        message,
        contract.id
      );

      if (response.success) {
        alert(`Email enviado a ${contract.tenantEmail}`);
      } else {
        alert(`Error al enviar email: ${response.error}`);
      }
    } catch (error) {
      alert(`Error al enviar email: ${error.message}`);
    }
  }

  setShowMessageModal(false);
};

const [payments, setPayments] = useState([]);
const [increaseHistory, setIncreaseHistory] = useState([]);
const [incidents, setIncidents] = useState([]);
const [boletas, setBoletas] = useState([]);

  // Alertas generadas automáticamente desde la frecuencia de cobro de cada contrato
  const paymentAlerts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const AVISO_DAYS = 7;    // Aviso urgente X días antes del vencimiento
    const H24 = 24 * 60 * 60 * 1000;
    const alertsList = [];
    let alertId = 1;

    // Para cada contrato activo, calcular fechas de cobro según frecuencia
    contracts
      .filter(c => c.status === 'ACTIVO' && c.startDate)
      .forEach(contract => {
        const freq = Number(contract.paymentFrequencyMonths) || 1;
        const contractStart = new Date(contract.startDate);
        const contractEnd = contract.endDate ? new Date(contract.endDate) : null;

        // Mirar hasta 6 períodos atrás (para no perder vencidos viejos)
        const lookback = new Date(today);
        lookback.setMonth(lookback.getMonth() - freq * 6);

        // Límite máximo: 12 meses adelante (techo de seguridad)
        const maxFuture = new Date(today);
        maxFuture.setFullYear(maxFuture.getFullYear() + 1);

        // Helper para agregar una alerta a la lista
        const addAlert = (dueDate) => {
          const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
          const yr = dueDate.getFullYear();
          const mo = String(dueDate.getMonth() + 1).padStart(2, '0');
          const alertKey = `${contract.id}-${yr}-${mo}`;

          const paymentDone = payments.some(p => {
            if (Number(p.contractId) !== Number(contract.id)) return false;
            if (p.status !== 'PAGADO' && p.status !== 'PAGO PARCIAL') return false;
            const pd = new Date(p.dueDate || p.paidDate);
            return pd.getFullYear() === yr && (pd.getMonth() + 1) === Number(mo);
          });

          if (paymentDone) return false; // pagado → no agregar

          const dismissInfo = dismissedAlerts[alertKey];
          const isWithin24h = dismissInfo && (Date.now() - dismissInfo.at < H24);
          const isOld = dismissInfo && (Date.now() - dismissInfo.at >= H24);
          const isOverdue = daysUntilDue < 0;
          const suppressedAsUrgente = isOld && dismissInfo?.wasAviso;

          if (suppressedAsUrgente) return false;

          if (isWithin24h) {
            alertsList.push({
              id: alertId++, key: alertKey,
              type: 'PAGO_REQUERIDO', isDone: true,
              contractId: contract.id,
              message: contract.propertyAddress,
              tenant: contract.tenantName || '',
              date: dueDate.toISOString(),
              daysUntilDue,
              amount: contract.baseRent || 0,
              priority: 'DONE'
            });
          } else {
            const priority = isOverdue ? 'ALTA' : daysUntilDue <= AVISO_DAYS ? 'MEDIA' : 'BAJA';
            alertsList.push({
              id: alertId++, key: alertKey,
              type: 'PAGO_REQUERIDO', isDone: false,
              contractId: contract.id,
              message: contract.propertyAddress,
              tenant: contract.tenantName || '',
              date: dueDate.toISOString(),
              daysUntilDue,
              amount: contract.baseRent || 0,
              priority
            });
          }
          return true;
        };

        // Iterar todos los períodos desde el inicio del contrato
        let dueDate = new Date(contractStart);
        dueDate.setMonth(dueDate.getMonth() + freq);

        let nextFutureAdded = false; // solo agregar UN período futuro por contrato

        while (true) {
          if (contractEnd && dueDate > contractEnd) break;
          if (dueDate > maxFuture) break;

          const isOverdue = dueDate < today;
          const isFuture = dueDate >= today;

          if (isOverdue && dueDate >= lookback) {
            // Agregar todos los vencidos dentro del lookback
            addAlert(dueDate);
          } else if (isFuture && !nextFutureAdded) {
            // Agregar solo el PRÓXIMO cobro pendiente futuro
            const added = addAlert(dueDate);
            if (added) nextFutureAdded = true;
            // Si el pago ya está hecho (added=false), seguir buscando el próximo
            if (!added) {
              // nada, continúa el loop para encontrar el próximo unpaid
            }
          } else if (isFuture && nextFutureAdded) {
            break; // ya encontramos el próximo, salir
          }

          const next = new Date(dueDate);
          next.setMonth(next.getMonth() + freq);
          dueDate = next;
        }
      });

    // Contratos próximos a vencer (60 días)
    const sixtyDays = new Date(today.getTime() + 60 * H24);
    contracts
      .filter(c => {
        if (c.status !== 'ACTIVO' || !c.endDate) return false;
        const ed = new Date(c.endDate);
        ed.setHours(0, 0, 0, 0);
        return ed >= today && ed <= sixtyDays;
      })
      .forEach(contract => {
        const ed = new Date(contract.endDate);
        ed.setHours(0, 0, 0, 0);
        const daysLeft = Math.ceil((ed - today) / H24);
        alertsList.push({
          id: alertId++, key: `contrato-fin-${contract.id}`,
          type: 'CONTRATO_POR_VENCER', isDone: false,
          contractId: contract.id,
          message: contract.propertyAddress,
          tenant: contract.tenantName || '',
          date: contract.endDate,
          daysUntilDue: daysLeft,
          amount: null,
          priority: daysLeft <= 15 ? 'ALTA' : 'MEDIA'
        });
      });

    // Ordenar: done al final, luego por prioridad y fecha
    const priorOrder = { ALTA: 0, MEDIA: 1, BAJA: 2, DONE: 3 };
    alertsList.sort((a, b) => {
      if (priorOrder[a.priority] !== priorOrder[b.priority])
        return priorOrder[a.priority] - priorOrder[b.priority];
      return new Date(a.date) - new Date(b.date);
    });

    return alertsList;
  }, [payments, contracts, dismissedAlerts]);

  const [indices, setIndices] = useState(mockIndices);
  const [indicesSource, setIndicesSource] = useState('mock'); // 'bcra', 'mock', o 'mixed'
  const [indicesLastUpdate, setIndicesLastUpdate] = useState(null);
  const [customIndices, setCustomIndices] = useState([]);

  // Email configuration state
  const [emailConfig, setEmailConfig] = useState({
    email_user: '',
    email_password: '',
    email_service: 'gmail',
    email_enabled: false,
    hasPassword: false
  });
  const [showEmailPassword, setShowEmailPassword] = useState(false);

  // Login Screen
  const LoginScreen = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

const handleLogin = async () => {
  if (!email || !password) {
    setError('Por favor ingresa email y contraseña');
    return;
  }

  setLoading(true);
  setError('');

  try {
    const data = await authService.login(email, password);

    // Agregar delay para asegurar que el token se guarde
    await new Promise(resolve => setTimeout(resolve, 100));

    setCurrentUser({
      name: data.user.nombre,
      email: data.user.email,
      role: data.user.rol.toUpperCase(),
      plan_code: data.user.plan_code || 'TRIAL',
      fecha_vencimiento: data.user.fecha_vencimiento,
      in_grace_period: data.user.in_grace_period || false
    });
    setIsLoggedIn(true);
    if (sessionStorage.getItem('pendingPlan')) {
      window.location.href = '/info-contacto';
    }
  } catch (err) {
    setError(err.message || 'Error al iniciar sesión');
    console.error('Error de login:', err);
  } finally {
    setLoading(false);
  }
};
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${darkMode ? 'bg-gray-900' : 'bg-slate-100'}`}>
        {/* Toggle en la esquina superior derecha */}
        <button
          onClick={toggleMode}
          className={`fixed top-4 right-4 p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-slate-600 hover:bg-slate-200 shadow'}`}
        >
          <span key={modeToggleKey} className="mode-icon">
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </span>
        </button>

        <div className={`border rounded-2xl p-8 w-full max-w-md shadow-xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <img
                src={darkMode ? "/logorentiacompletonaranjablanco.png?v=2" : "/logorentiacompletonaranja.png?v=4"}
                alt="Rentia"
                className="h-20 w-auto object-contain"
              />
            </div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Sistema de Gestión Inmobiliaria</p>
          </div>
          <div className="text-center mb-6">
            <a href="/registro" className="text-[#ed6f28] text-sm hover:underline">
              ¿No tenés cuenta? Probá gratis 14 días →
            </a>
          </div>

          {error && (
            <div className={`mb-4 border rounded-lg px-4 py-3 text-sm ${darkMode ? 'bg-red-900/50 border-red-500 text-red-300' : 'bg-red-50 border-red-300 text-red-700'}`}>
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className={`text-sm block mb-2 ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="admin@rentia.com"
                disabled={loading}
                className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#ed6f28] disabled:opacity-50 ${darkMode ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400'}`}
              />
            </div>

            <div>
              <label className={`text-sm block mb-2 ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="••••••••"
                disabled={loading}
                className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#ed6f28] disabled:opacity-50 ${darkMode ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400'}`}
              />
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-[#ed6f28] hover:bg-[#c45e1e] text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>

            <div className="text-center">
              <a href="/recuperar-password" className={`text-sm hover:underline ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-slate-500 hover:text-slate-700'}`}>
                ¿Olvidaste tu contraseña?
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Header
  const Header = () => {
    const pendingIncidents = incidents.filter(i => i.status !== 'RESUELTA').length;
    const overduePayments = getOverduePaymentsCount(payments);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const pendingAlerts = paymentAlerts.filter(a => !a.isDone).length;
    const initials = (currentUser?.nombre || 'U')
      .split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

    const viewTitles = {
      dashboard: 'Dashboard', contracts: 'Contratos', payments: 'Pagos',
      incidents: 'Incidencias', boletas: 'Boletas', notifications: 'Notificaciones',
      reports: 'Reportes', indices: 'Índices', users: 'Usuarios',
      suscripcion: 'Suscripción', settings: 'Configuración',
    };

    const today = new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    return (
      <div className="rentia-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Mobile hamburger */}
          <button
            className="t-btn md:hidden"
            onClick={() => setSidebarOpen(true)}
            style={{ display: 'none' }}
          >
            <Menu style={{ width: 16, height: 16 }} />
          </button>
          <button className="t-btn" style={{ display: 'flex' }} onClick={() => setSidebarOpen(v => !v)}>
            <Menu style={{ width: 16, height: 16 }} />
          </button>
          <div>
            <div className="topbar-title">{viewTitles[currentView] || 'Rentia'}</div>
            <div className="topbar-date" style={{ textTransform: 'capitalize' }}>{today}</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Bell — alertas de pago */}
          <div style={{ position: 'relative' }}>
            <button
              className="t-btn"
              onClick={() => setShowReminders(v => !v)}
              title="Alertas de pago"
            >
              <Bell style={{ width: 16, height: 16 }} />
              {pendingAlerts > 0 && (
                <span style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 6, height: 6, background: 'var(--red)',
                  borderRadius: '50%', border: '2px solid var(--bg)'
                }} />
              )}
            </button>
            {showReminders && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowReminders(false)} />
                <div style={{
                  position: 'absolute', right: 0, top: 42, zIndex: 200,
                  width: 380, maxHeight: '80vh', overflowY: 'auto',
                  background: 'var(--surf)', border: '1px solid var(--borderB)',
                  borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.5)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontWeight: 650, fontSize: 13, color: 'var(--t1)' }}>Alertas de pago</span>
                    <button onClick={() => setShowReminders(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)' }}>
                      <X style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                  {paymentAlerts.length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--t3)', fontSize: 12 }}>No hay alertas pendientes</div>
                  ) : (
                    paymentAlerts.map(a => (
                      <div key={a.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: a.priority === 'ALTA' ? 'var(--red)' : a.priority === 'MEDIA' ? 'var(--yellow)' : 'var(--t3)', marginTop: 4, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.message}</p>
                          {a.tenant && <p style={{ fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>{a.tenant}</p>}
                          <div style={{ display: 'flex', gap: 10, marginTop: 3, fontSize: 11 }}>
                            <span style={{ color: a.priority === 'ALTA' ? 'var(--red)' : 'var(--yellow)', fontWeight: 600 }}>
                              {new Date(a.date).toLocaleDateString('es-AR')}
                            </span>
                            {a.amount && <span style={{ color: 'var(--t2)' }}>${parseFloat(a.amount).toLocaleString('es-AR')}</span>}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
                    <button
                      onClick={() => { navigateTo('notifications'); setShowReminders(false); }}
                      style={{ width: '100%', textAlign: 'center', fontSize: 12, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Ver todas las notificaciones →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Toggle light/dark */}
          <button
            className="t-btn"
            onClick={toggleMode}
            title={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {darkMode ? <Sun style={{ width: 15, height: 15 }} /> : <Moon style={{ width: 15, height: 15 }} />}
          </button>

          {/* Alert circle — incidencias + vencidos */}
          <button
            className="t-btn"
            onClick={() => navigateTo('payments')}
            title="Pagos vencidos e incidencias"
            style={{ position: 'relative' }}
          >
            <AlertCircle style={{ width: 16, height: 16 }} />
            {(pendingIncidents + overduePayments) > 0 && (
              <span style={{
                position: 'absolute', top: 6, right: 6,
                width: 6, height: 6, background: 'var(--red)',
                borderRadius: '50%', border: '2px solid var(--bg)'
              }} />
            )}
          </button>

          {/* User avatar */}
          <div style={{ position: 'relative' }}>
            <button
              className="t-avatar"
              onClick={() => setShowUserMenu(v => !v)}
              title="Cuenta"
            >
              {initials}
            </button>
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                <div style={{
                  position: 'absolute', right: 0, top: 40, zIndex: 200,
                  width: 270, background: 'var(--surf)', border: '1px solid var(--borderB)',
                  borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.5)', overflow: 'hidden'
                }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', background: 'var(--surf2)' }}>
                    <div className="t-avatar" style={{ width: 36, height: 36, fontSize: 13, flexShrink: 0 }}>{initials}</div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 650, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.nombre}</p>
                      <p style={{ fontSize: 11, color: 'var(--t3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email}</p>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { label: 'Rol', value: currentUser?.rol },
                      { label: 'Plan', value: currentUser?.plan_code || 'TRIAL' },
                      ...(currentUser?.fecha_vencimiento ? [{ label: 'Vence', value: new Date(currentUser.fecha_vencimiento).toLocaleDateString('es-AR') }] : []),
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: 'var(--t3)' }}>{label}</span>
                        <span style={{ color: 'var(--t1)', fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                    {currentUser?.in_grace_period && (
                      <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600, display: 'flex', gap: 5, alignItems: 'center' }}>
                        <AlertCircle style={{ width: 12, height: 12 }} /> Período de gracia activo
                      </div>
                    )}
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4, display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => { refreshCurrentUser(); setShowUserMenu(false); }}
                        style={{ flex: 1, fontSize: 11.5, padding: '7px 10px', background: 'var(--surf2)', color: 'var(--t2)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                      >
                        <RefreshCw style={{ width: 12, height: 12 }} /> Actualizar
                      </button>
                      <button
                        onClick={() => { navigateTo('suscripcion'); setShowUserMenu(false); }}
                        style={{ flex: 1, fontSize: 11.5, padding: '7px 10px', background: 'var(--orDim)', color: 'var(--orange)', border: '1px solid rgba(237,111,40,0.25)', borderRadius: 6, cursor: 'pointer', fontWeight: 650, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                      >
                        <CreditCard style={{ width: 12, height: 12 }} /> Suscripción
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Sidebar
  const TrialBanner = () => {
    const plan = currentUser?.plan_code || 'TRIAL';
    const vencimiento = currentUser?.fecha_vencimiento;
    const inGrace = currentUser?.in_grace_period;

    if (!vencimiento) return null;

    const hoy = new Date();
    const exp = new Date(vencimiento);
    const diasRestantes = Math.ceil((exp - hoy) / (1000 * 60 * 60 * 24));

    // Grace period — cuenta bloqueada próximamente
    if (inGrace) {
      return (
        <div className="bg-red-900/80 border-b border-red-700 px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-red-100 text-sm font-medium">
            Tu cuenta está en período de gracia. Regularizá tu suscripción para no perder el acceso.
          </p>
          <button
            onClick={() => navigateTo('suscripcion')}
            className="shrink-0 bg-red-500 hover:bg-red-400 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors"
          >
            Ver planes
          </button>
        </div>
      );
    }

    // Trial con pocos días
    if (plan === 'TRIAL' && diasRestantes <= 15 && diasRestantes > 0) {
      return (
        <div className="bg-amber-900/70 border-b border-amber-700/60 px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-amber-100 text-sm">
            <span className="font-semibold">Tu período de prueba vence en {diasRestantes} día{diasRestantes !== 1 ? 's' : ''}.</span>
            {' '}Suscribite para mantener el acceso a todos tus datos.
          </p>
          <button
            onClick={() => navigateTo('suscripcion')}
            className="shrink-0 bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors"
          >
            Ver planes
          </button>
        </div>
      );
    }

    // Trial con tiempo de sobra (recordatorio suave > 15 días)
    if (plan === 'TRIAL' && diasRestantes > 15) {
      return (
        <div className="bg-[#ed6f28]/10 border-b border-[#ed6f28]/20 px-4 py-2 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-orange-300 text-xs">
            Período de prueba gratuito — {diasRestantes} días restantes.
          </p>
          <button
            onClick={() => navigateTo('suscripcion')}
            className="shrink-0 text-[#ed6f28] hover:text-orange-300 text-xs font-semibold transition-colors"
          >
            Ver planes →
          </button>
        </div>
      );
    }

    return null;
  };

  const Sidebar = () => {
    const navGroups = [
      { label: 'Principal', items: [
        { id: 'dashboard',    icon: Home,      label: 'Dashboard' },
        { id: 'contracts',   icon: Building2,  label: 'Contratos' },
        { id: 'payments',    icon: DollarSign, label: 'Pagos' },
      ]},
      { label: 'Operaciones', items: [
        { id: 'incidents',   icon: Wrench,     label: 'Incidencias' },
        { id: 'boletas',     icon: Receipt,    label: 'Boletas' },
        { id: 'notifications',icon: Bell,      label: 'Notificaciones' },
      ]},
      { label: 'Análisis', items: [
        { id: 'reports',     icon: BarChart3,  label: 'Reportes' },
        { id: 'indices',     icon: TrendingUp, label: 'Índices' },
      ]},
      ...(currentUser?.rol === 'admin' ? [{ label: 'Admin', items: [
        { id: 'users',       icon: User,       label: 'Usuarios' },
      ]}] : []),
      { label: 'Cuenta', items: [
        { id: 'suscripcion', icon: CreditCard, label: 'Suscripción' },
        { id: 'settings',    icon: Settings,   label: 'Configuración' },
      ]},
    ];

    const pendingAlerts = paymentAlerts.filter(a => !a.isDone).length;
    const initials = (currentUser?.nombre || 'U')
      .split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

    return (
      <>
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}
        <aside className={`rentia-sidebar ${sidebarOpen ? '' : 'max-md:-translate-x-full'}`} style={{ transition: 'transform 0.3s' }}>
          {/* Logo */}
          <div className="s-logo" onClick={() => { window.location.href = '/info-contacto#precios'; }}>
            <img
              src={darkMode ? "/logorentiacompletonaranjablanco.png?v=2" : "/logorentiacompletonaranja.png?v=4"}
              alt="Rentia"
              style={{ height: 28, width: 'auto', objectFit: 'contain' }}
            />
          </div>

          {/* Navigation */}
          <nav className="s-nav">
            {navGroups.map((group) => (
              <div key={group.label}>
                <div className="s-group-label">{group.label}</div>
                {group.items.map((item) => (
                  <div
                    key={item.id}
                    className={`s-item ${currentView === item.id ? 'on' : ''}`}
                    onClick={() => {
                      navigateTo(item.id);
                      setSidebarOpen(false);
                      if (item.id !== 'payments') setSelectedContractForPayments(null);
                    }}
                  >
                    <item.icon style={{ width: 14, height: 14, flexShrink: 0 }} />
                    {item.label}
                    {item.id === 'notifications' && pendingAlerts > 0 && (
                      <span className="s-badge">{pendingAlerts}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="s-footer">
            <div className="s-user" onClick={() => navigateTo('suscripcion')}>
              <div className="s-avatar">{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="s-uname" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentUser?.nombre}
                </div>
                <span className="s-plan">{currentUser?.plan_code || 'TRIAL'}</span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); toggleMode(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)', padding: '2px', display: 'flex', alignItems: 'center' }}
                title={darkMode ? 'Modo claro' : 'Modo oscuro'}
              >
                <span key={modeToggleKey} className="mode-icon">
                  {darkMode ? <Sun style={{ width: 14, height: 14 }} /> : <Moon style={{ width: 14, height: 14 }} />}
                </span>
              </button>
            </div>
            <div
              className="s-logout"
              onClick={() => { authService.logout(); setIsLoggedIn(false); setCurrentUser(null); window.location.href = '/'; }}
            >
              <LogOut style={{ width: 13, height: 13 }} />
              <span>Cerrar Sesión</span>
            </div>
          </div>
        </aside>
      </>
    );
  };

// ============================================
  // ÁREA CHART (SVG custom)
  // ============================================
  const AreaChartSVG = ({ data, format, color }) => {
    const [hover, setHover] = React.useState(null);
    const W=580, H=130, pl=52, pr=16, pt=8, pb=28;
    const cW=W-pl-pr, cH=H-pt-pb;
    if (!data || data.length < 2) {
      return <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)', fontSize: 12 }}>Sin datos</div>;
    }
    const maxV = Math.max(...data.map(d => d.v));
    const minV = Math.min(...data.map(d => d.v));
    const range = maxV - minV || 1;
    const xs = i => pl + (i / (data.length - 1)) * cW;
    const ys = v => pt + cH - ((v - minV) / range) * cH;
    const pts = data.map((d, i) => [xs(i), ys(d.v)]);
    const line = pts.map((p, i) => `${i===0?'M':'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    const area = line + ` L${pts[pts.length-1][0].toFixed(1)} ${pt+cH} L${pl} ${pt+cH} Z`;
    const fmt = format || (v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`);
    const c = color || '#ed6f28';
    const ytks = [minV, (minV+maxV)/2, maxV];
    const seg = cW / (data.length - 1);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible', display: 'block' }}>
        <defs>
          <linearGradient id="rentia-ag" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity=".22" />
            <stop offset="100%" stopColor={c} stopOpacity=".01" />
          </linearGradient>
        </defs>
        {ytks.map((v, i) => {
          const y = ys(v);
          return (
            <g key={i}>
              <line x1={pl} y1={y} x2={W-pr} y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <text x={pl-7} y={y+4} fontSize="9.5" fill="#56556a" textAnchor="end">{fmt(v)}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          if (i % Math.max(1, Math.floor(data.length / 6)) !== 0 && i !== data.length - 1) return null;
          return <text key={i} x={xs(i)} y={H-4} fontSize="9.5" fill="#56556a" textAnchor="middle">{d.m}</text>;
        })}
        <path d={area} fill="url(#rentia-ag)" />
        <path d={line} fill="none" stroke={c} strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="4" fill={c} stroke="#111116" strokeWidth="2" />
        {data.map((d, i) => (
          <rect key={i} x={xs(i)-seg/2} y={pt} width={seg} height={cH}
            fill="transparent"
            onMouseEnter={() => setHover({ i, x: xs(i), y: ys(d.v), d })}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {hover && (
          <g>
            <line x1={hover.x} y1={pt} x2={hover.x} y2={pt+cH} stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={hover.x} cy={hover.y} r="4" fill={c} stroke="#111116" strokeWidth="2" />
            {(() => {
              const tx = Math.min(Math.max(hover.x, 60), W-60);
              return (
                <g>
                  <rect x={tx-38} y={hover.y-28} width="76" height="20" rx="4" fill="#1f1f28" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                  <text x={tx} y={hover.y-14} fontSize="11" fill="#efeff6" textAnchor="middle" fontWeight="600">{fmt(hover.d.v)}</text>
                </g>
              );
            })()}
          </g>
        )}
      </svg>
    );
  };

  // ============================================
  // DONUT CHART (SVG custom)
  // ============================================
  const DonutChartSVG = ({ slices, centerLabel, centerSub }) => {
    const r = 52, cx = 65, cy = 65, sw = 13, circ = 2 * Math.PI * r;
    let off = 0;
    return (
      <svg viewBox="0 0 130 130" style={{ width: 118, height: 118 }}>
        {slices.map((s, i) => {
          const dash = (s.pct / 100) * circ;
          const gap = circ - dash;
          const el = (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color}
              strokeWidth={sw} strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-off} transform={`rotate(-90 ${cx} ${cy})`} />
          );
          off += dash;
          return el;
        })}
        <text x={cx} y={cy-5} textAnchor="middle" fontSize="21" fontWeight="760" fill="#efeff6">{centerLabel}</text>
        <text x={cx} y={cy+11} textAnchor="middle" fontSize="9.5" fill="#56556a" letterSpacing="0.06em">{centerSub}</text>
      </svg>
    );
  };

  // ============================================
  // DASHBOARD — NUEVO DISEÑO
  // ============================================
  const Dashboard = () => {
    const activeContracts = getActiveContractsCount(contracts);
    const expiringContracts = getExpiringContractsCount(contracts);
    const totalPaid = getTotalPaidAmount(payments);
    const totalPending = getTotalPendingAmount(payments);
    const totalCommissions = getTotalCommissions(payments, contracts);
    const overduePayments = getOverduePaymentsCount(payments);
    const pendingIncidents = incidents.filter(i => i.status !== 'RESUELTA').length;

    const monthlyIncomeData = getMonthlyIncomeData(payments);
    const contractsByPropertyType = getContractsByPropertyType(contracts);
    const paymentsByStatus = getPaymentsByStatus(payments);

    const recentPayments = payments
      .filter(p => p.status === 'PAGADO')
      .sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate))
      .slice(0, 5);

    const upcomingPayments = payments
      .filter(p => p.status === 'PENDIENTE' || p.status === 'VENCIDO')
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 5);

    const fmtAmt = v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`;

    // Area chart data: monthly income
    const areaData = monthlyIncomeData.map(d => {
      const [yr, mo] = d.month.split('-');
      const mNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return { m: mNames[parseInt(mo)-1] + ' ' + yr.slice(2), v: d.ingresos };
    });

    // Property type bars
    const propMax = contractsByPropertyType.length > 0 ? Math.max(...contractsByPropertyType.map(p => p.cantidad)) : 1;
    const propColors = ['#ed6f28', '#60a5fa', '#34d399', '#f5ba45', '#a78bfa', '#f87171'];

    // Donut slices from paymentsByStatus
    const totalPay = paymentsByStatus.reduce((s, e) => s + e.value, 0);
    const donutSlices = paymentsByStatus.map(s => ({
      pct: totalPay > 0 ? (s.value / totalPay) * 100 : 0,
      color: s.color,
      label: s.name,
      count: s.value,
    }));
    const paidCount = payments.filter(p => p.status === 'PAGADO').length;

    const now = new Date();
    const mesActual = now.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

    return (
      <div className="rentia-page">
        <div className="sec-label">Resumen · {mesActual}</div>

        {/* KPI Grid */}
        <div className="kpi-grid">
          {/* Contratos activos */}
          <div className="kpi-card" style={{ '--kpi-color': 'var(--blue)', '--kpi-ibg': 'var(--blDim)' }}>
            <div className="kpi-ico"><Building2 style={{ width: 13, height: 13, color: 'var(--blue)' }} /></div>
            <div className="kpi-label">Contratos Activos</div>
            <div className="kpi-val">{activeContracts}</div>
            <div className="kpi-foot">
              {expiringContracts > 0 && <span className="rtag rtag-y">{expiringContracts} por vencer</span>}
              <span>{expiringContracts > 0 ? 'próx. 30 días' : 'sin vencimientos'}</span>
            </div>
          </div>

          {/* Total cobrado */}
          <div className="kpi-card" style={{ '--kpi-color': 'var(--green)', '--kpi-ibg': 'var(--grDim)' }}>
            <div className="kpi-ico"><CheckCircle style={{ width: 13, height: 13, color: 'var(--green)' }} /></div>
            <div className="kpi-label">Total Cobrado</div>
            <div className="kpi-val">{fmtAmt(totalPaid)}</div>
            <div className="kpi-foot">
              <span className="rtag rtag-g">{paidCount} pagos</span>
              <span>registrados</span>
            </div>
          </div>

          {/* Honorarios */}
          <div className="kpi-card" style={{ '--kpi-color': 'var(--orange)', '--kpi-ibg': 'var(--orDim)' }}>
            <div className="kpi-ico"><TrendingUp style={{ width: 13, height: 13, color: 'var(--orange)' }} /></div>
            <div className="kpi-label">Honorarios</div>
            <div className="kpi-val">{fmtAmt(totalCommissions)}</div>
            <div className="kpi-foot">
              <span className="rtag rtag-o">comisiones</span>
              <span>de cobros</span>
            </div>
          </div>

          {/* Pendiente */}
          <div className="kpi-card" style={{ '--kpi-color': 'var(--yellow)', '--kpi-ibg': 'var(--ylDim)' }}>
            <div className="kpi-ico"><Clock style={{ width: 13, height: 13, color: 'var(--yellow)' }} /></div>
            <div className="kpi-label">Pendiente de Cobro</div>
            <div className="kpi-val">{fmtAmt(totalPending)}</div>
            <div className="kpi-foot">
              <span className="rtag rtag-y">{payments.filter(p=>p.status==='PENDIENTE').length} pagos</span>
              <span>sin cobrar</span>
            </div>
          </div>

          {/* Vencidos */}
          <div className={`kpi-card ${overduePayments > 0 ? 'urgent' : ''}`} style={{ '--kpi-color': 'var(--red)', '--kpi-ibg': 'var(--rDim)' }}>
            <div className="kpi-ico"><AlertCircle style={{ width: 13, height: 13, color: 'var(--red)' }} /></div>
            <div className="kpi-label">Pagos Vencidos</div>
            <div className={`kpi-val ${overduePayments > 0 ? 'red' : ''}`}>{overduePayments}</div>
            <div className="kpi-foot">
              {overduePayments > 0
                ? <span className="rtag rtag-r">¡Acción requerida!</span>
                : <span className="rtag rtag-g">Al día</span>
              }
              {pendingIncidents > 0 && <span>{pendingIncidents} incidencias</span>}
            </div>
          </div>
        </div>

        {/* Charts row */}
        <div className="charts-row-new">
          {/* Area chart — ingresos */}
          <div className="rcard">
            <div className="rcard-head">
              <div>
                <div className="rcard-title">Ingresos Mensuales</div>
                <div className="rcard-sub">Cobros registrados · historial completo</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 760, letterSpacing: '-0.6px', color: 'var(--t1)' }}>{fmtAmt(totalPaid)}</span>
                <span className="rtag rtag-g" style={{ borderRadius: 5 }}>cobrado</span>
              </div>
            </div>
            <div className="rcard-body" style={{ paddingTop: 10 }}>
              <AreaChartSVG data={areaData} />
            </div>
          </div>

          {/* Bar chart — tipos de propiedad */}
          <div className="rcard">
            <div className="rcard-head">
              <div>
                <div className="rcard-title">Por Tipo de Propiedad</div>
                <div className="rcard-sub">Distribución de contratos activos</div>
              </div>
              <span style={{ fontSize: 18, fontWeight: 760, letterSpacing: '-0.4px', color: 'var(--t1)' }}>{activeContracts}</span>
            </div>
            <div className="rcard-body">
              {contractsByPropertyType.length === 0 ? (
                <p style={{ color: 'var(--t3)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>Sin contratos activos</p>
              ) : contractsByPropertyType.map((p, i) => (
                <div key={i} className="bar-row-new">
                  <div className="bar-top-new">
                    <span className="bar-lbl">{p.type}</span>
                    <span className="bar-num">{p.cantidad}</span>
                  </div>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${(p.cantidad / propMax) * 100}%`, background: propColors[i % propColors.length] }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom grid */}
        <div className="bot-grid-new">
          {/* Donut — estado de pagos */}
          <div className="rcard">
            <div className="rcard-head">
              <div className="rcard-title">Estado de Pagos</div>
            </div>
            <div className="rcard-body">
              {donutSlices.length === 0 ? (
                <p style={{ color: 'var(--t3)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>Sin pagos registrados</p>
              ) : (
                <div className="donut-wrap">
                  <DonutChartSVG
                    slices={donutSlices}
                    centerLabel={paidCount}
                    centerSub="PAGADOS"
                  />
                  <div className="donut-legend">
                    {donutSlices.map((s, i) => (
                      <div key={i} className="dl-row">
                        <div className="dl-dot" style={{ background: s.color }} />
                        <span className="dl-lbl">{s.label}</span>
                        <span className="dl-val">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming — próximos vencimientos */}
          <div className="rcard">
            <div className="rcard-head">
              <div className="rcard-title">Próximos Vencimientos</div>
              <button
                onClick={() => navigateTo('payments')}
                style={{ fontSize: 11, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Ver todos →
              </button>
            </div>
            <div className="rcard-body">
              {upcomingPayments.length === 0 ? (
                <p style={{ color: 'var(--t3)', fontSize: 12 }}>No hay pagos pendientes</p>
              ) : upcomingPayments.map((payment) => {
                const contract = contracts.find(c => c.id === payment.contractId);
                const days = Math.ceil((new Date(payment.dueDate) - now) / (1000*60*60*24));
                const isOverdue = days < 0;
                const barColor = isOverdue ? 'var(--red)' : days <= 5 ? 'var(--yellow)' : 'var(--green)';
                return (
                  <div key={payment.id} className="up-item">
                    <div className="up-bar" style={{ background: barColor }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="up-name">{contract?.tenantName || '—'}</div>
                      <div className="up-addr">{contract?.propertyAddress || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div className="up-amt">${parseFloat(payment.adjustedAmount || payment.baseAmount || 0).toLocaleString('es-AR')}</div>
                      <div className="up-days" style={{ background: isOverdue ? 'var(--rDim)' : 'var(--surf3)', color: isOverdue ? 'var(--red)' : 'var(--t3)' }}>
                        {isOverdue ? `${Math.abs(days)}d vencido` : days === 0 ? 'Hoy' : `${days}d`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent — últimos pagos cobrados */}
          <div className="rcard">
            <div className="rcard-head">
              <div className="rcard-title">Últimos Cobros</div>
              <button
                onClick={() => navigateTo('payments')}
                style={{ fontSize: 11, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Ver todos →
              </button>
            </div>
            <div className="rcard-body">
              {recentPayments.length === 0 ? (
                <p style={{ color: 'var(--t3)', fontSize: 12 }}>No hay pagos registrados aún</p>
              ) : recentPayments.map((payment) => {
                const contract = contracts.find(c => c.id === payment.contractId);
                const name = contract?.tenantName || '—';
                const initials = name.split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={payment.id} className="pay-item">
                    <div className="pay-av">{initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="pay-name">{name}</div>
                      <div className="pay-addr">{contract?.propertyAddress || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="pay-amt">+${parseFloat(payment.adjustedAmount || payment.baseAmount || 0).toLocaleString('es-AR')}</div>
                      <div className="pay-date">{payment.paidDate ? new Date(payment.paidDate).toLocaleDateString('es-AR') : '—'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Contracts View
  const ContractsView = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('TODOS');
    const [showDeleted, setShowDeleted] = useState(false);
    const [selectedContracts, setSelectedContracts] = useState(new Set());
    const [showNuevoModal, setShowNuevoModal] = useState(false);
    const lastSelectedIndexRef = React.useRef(null);

    const displayedContracts = showDeleted ? deletedContracts : contracts;

    const filteredContracts = displayedContracts.filter(contract => {
      const matchesSearch = 
        contract.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.tenantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.ownerName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'TODOS' || contract.status === filterStatus;
      
      return matchesSearch && matchesStatus;
    });

    // Funciones conectadas al backend PostgreSQL
const handleDelete = async (id) => {
  if (!window.confirm('¿Mover este contrato a la papelera?')) return;
  try {
    await contractsService.softDelete(id);
    await refreshContracts();
    await refreshDeleted();
    alert('Contrato movido a la papelera');
  } catch (err) {
    console.error(err);
    alert('Error al eliminar contrato');
  }
};

const handleContractSelect = (e, contractId, index) => {
  if (e.ctrlKey || e.metaKey) {
    setSelectedContracts(prev => {
      const next = new Set(prev);
      next.has(contractId) ? next.delete(contractId) : next.add(contractId);
      return next;
    });
    lastSelectedIndexRef.current = index;
  } else if (e.shiftKey && lastSelectedIndexRef.current !== null) {
    const start = Math.min(lastSelectedIndexRef.current, index);
    const end = Math.max(lastSelectedIndexRef.current, index);
    setSelectedContracts(prev => {
      const next = new Set(prev);
      filteredContracts.slice(start, end + 1).forEach(c => next.add(c.id));
      return next;
    });
  } else {
    setSelectedContracts(prev => {
      const next = new Set(prev);
      next.has(contractId) ? next.delete(contractId) : next.add(contractId);
      return next;
    });
    lastSelectedIndexRef.current = index;
  }
};

const handleBulkDelete = async () => {
  if (!window.confirm(`¿Mover ${selectedContracts.size} contrato(s) a la papelera?`)) return;
  try {
    await Promise.all([...selectedContracts].map(id => contractsService.softDelete(id)));
    await refreshContracts();
    await refreshDeleted();
    setSelectedContracts(new Set());
  } catch (err) {
    alert('Error al eliminar contratos seleccionados');
  }
};

const handleRestore = async (id) => {
  if (!window.confirm('¿Restaurar este contrato?')) return;
  try {
    await contractsService.restore(id);
    await refreshContracts();
    await refreshDeleted();
    alert('Contrato restaurado');
  } catch (err) {
    console.error(err);
    alert('Error al restaurar contrato');
  }
};

const handlePermanentDelete = async (id) => {
  if (!window.confirm('¿Estás seguro de eliminar permanentemente este contrato?\n\nEsta acción NO SE PUEDE DESHACER y eliminará:\n- El contrato\n- Todos los pagos asociados\n- Todas las incidencias\n- Todos los aumentos\n\n¿Deseas continuar?')) {
    return;
  }

  try {
    await contractsService.permanentDelete(id);
    await refreshDeleted();
    alert('Contrato eliminado permanentemente');
  } catch (err) {
    console.error(err);
    alert('Error al eliminar contrato: ' + (err.message || 'Error desconocido'));
  }
};

const handleDuplicate = async (contract) => {
  try {
    await contractsService.duplicate(contract.id);
    await refreshContracts();
    alert('Contrato duplicado exitosamente');
  } catch (err) {
    console.error(err);
    alert('Error al duplicar contrato');
  }
};


    const getTimeSinceDeleted = (deletedAt) => {
      const now = new Date();
      const deleted = new Date(deletedAt);
      const diffMs = now - deleted;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
      if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
      return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    };

    // KPI strip data
    const activeContracts = contracts.filter(c => c.status === 'ACTIVO');
    const contractsWithOverdue = new Set(payments.filter(p => p.status === 'VENCIDO').map(p => p.contractId));
    const contractsWithPending = new Set(payments.filter(p => p.status === 'PENDIENTE').map(p => p.contractId));
    const contractsUpToDate = activeContracts.filter(c => !contractsWithOverdue.has(c.id) && !contractsWithPending.has(c.id)).length;

    return (
      <div className="rentia-page">
        {/* Mini KPI strip */}
        {!showDeleted && (
          <div className="ct-kpis">
            <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}>
              <div className="ct-kpi-n">{activeContracts.length}</div>
              <div className="ct-kpi-l">Contratos activos</div>
            </div>
            <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}>
              <div className="ct-kpi-n">{contractsUpToDate}</div>
              <div className="ct-kpi-l">Al día</div>
            </div>
            <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}>
              <div className="ct-kpi-n">{payments.filter(p=>p.status==='PENDIENTE').length}</div>
              <div className="ct-kpi-l">Pagos pendientes</div>
            </div>
            <div className="ct-kpi" style={{'--ck-c':'var(--red)'}}>
              <div className="ct-kpi-n">{payments.filter(p=>p.status==='VENCIDO').length}</div>
              <div className="ct-kpi-l">Pagos vencidos</div>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          {/* Search */}
          <div style={{ flex: 1, position: 'relative' }}>
            <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--t3)', width: 14, height: 14 }} />
            <input
              type="text"
              placeholder="Buscar por dirección, inquilino o propietario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%', background: 'var(--surf)', border: '1px solid var(--border)',
                borderRadius: 'var(--r2)', padding: '8px 12px 8px 32px',
                fontSize: 13, color: 'var(--t1)', fontFamily: 'inherit', outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--borderB)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
            />
          </div>
          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 5 }}>
            {[
              { val: 'TODOS', label: 'Todos', cls: 'on' },
              { val: 'ACTIVO', label: 'Activos', cls: 'on-g' },
              { val: 'FINALIZADO', label: 'Finalizados', cls: '' },
            ].map(({ val, label, cls }) => (
              <button
                key={val}
                className={`ct-pill ${filterStatus === val ? (val === 'TODOS' ? 'on' : val === 'ACTIVO' ? 'on-g' : 'on') : ''}`}
                onClick={() => setFilterStatus(val)}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Papelera toggle */}
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className={`ct-pill ${showDeleted ? 'on-r' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Trash2 style={{ width: 12, height: 12 }} />
            Papelera
            {deletedContracts.length > 0 && (
              <span style={{ background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 99, padding: '1px 5px' }}>
                {deletedContracts.length}
              </span>
            )}
          </button>
          {!showDeleted && (
            <>
              <button
                onClick={() => { const f = exportToExcel(contracts, payments, currentUser?.name || 'Usuario'); alert(`Exportado: ${f}`); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--grDim)', color: 'var(--green)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 'var(--r2)', fontSize: 12, fontWeight: 650, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <Download style={{ width: 12, height: 12 }} /> Excel
              </button>
              <button
                onClick={() => setShowNuevoModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--orange)', color: '#fff', border: '1px solid var(--orange)', borderRadius: 'var(--r2)', fontSize: 12, fontWeight: 650, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <Plus style={{ width: 12, height: 12 }} /> Nuevo Contrato
              </button>
            </>
          )}
        </div>

        {/* Count + selection bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--t3)' }}>
            {filteredContracts.length} de {displayedContracts.length} contratos
            {showDeleted && ' · papelera'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--t3)' }}>Ctrl+clic para seleccionar varios</span>
        </div>

        {selectedContracts.size > 0 && (
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--orDim)', border: '1px solid rgba(237,111,40,0.25)', borderRadius: 'var(--r2)', padding: '8px 14px' }}>
            <span style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--orange)' }}>{selectedContracts.size} seleccionado{selectedContracts.size > 1 ? 's' : ''}</span>
            <button onClick={() => setSelectedContracts(new Set())} style={{ fontSize: 11, color: 'var(--t3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Deseleccionar</button>
            <div style={{ flex: 1 }} />
            <button onClick={handleBulkDelete} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--rDim)', color: 'var(--red)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '5px 10px', fontSize: 11.5, fontWeight: 650, cursor: 'pointer' }}>
              <Trash2 style={{ width: 12, height: 12 }} /> Eliminar seleccionados
            </button>
          </div>
        )}

        {showDeleted && deletedContracts.length === 0 ? (
          <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '48px 24px', textAlign: 'center' }}>
            <Trash2 style={{ width: 40, height: 40, color: 'var(--t3)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 15, fontWeight: 650, color: 'var(--t2)', marginBottom: 4 }}>La papelera está vacía</p>
            <p style={{ fontSize: 12, color: 'var(--t3)' }}>Los contratos eliminados aparecerán aquí</p>
          </div>
        ) : (
          <div>
            {filteredContracts.map((contract, index) => {
              // Calculate contract progress
              const startD = contract.startDate ? new Date(contract.startDate) : null;
              const endD = contract.endDate ? new Date(contract.endDate) : null;
              const now = new Date();
              let progress = 0;
              if (startD && endD) {
                const total = endD - startD;
                const elapsed = now - startD;
                progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
              }
              const daysToEnd = endD ? Math.ceil((endD - now) / (1000*60*60*24)) : null;
              const isExpiringSoon = daysToEnd !== null && daysToEnd >= 0 && daysToEnd <= 30;
              const isOverdueContract = daysToEnd !== null && daysToEnd < 0;

              // Strip color by status
              const stripColor = showDeleted ? 'var(--red)'
                : contract.status === 'ACTIVO' ? (isExpiringSoon ? 'var(--yellow)' : 'var(--green)')
                : contract.status === 'FINALIZADO' ? 'var(--t3)'
                : 'var(--orange)';

              const statusBadge = showDeleted
                ? { bg: 'var(--rDim)', color: 'var(--red)', label: `ELIMINADO · ${getTimeSinceDeleted(contract.deletedAt)}` }
                : contract.status === 'ACTIVO'
                  ? (isExpiringSoon ? { bg: 'var(--ylDim)', color: 'var(--yellow)', label: `POR VENCER · ${daysToEnd}d` }
                    : { bg: 'var(--grDim)', color: 'var(--green)', label: 'ACTIVO' })
                : contract.status === 'FINALIZADO'
                  ? { bg: 'var(--surf3)', color: 'var(--t3)', label: 'FINALIZADO' }
                  : { bg: 'var(--orDim)', color: 'var(--orange)', label: contract.status };

              const isSelected = selectedContracts.has(contract.id);

              return (
                <div
                  key={contract.id}
                  className={`cc ${isExpiringSoon || isOverdueContract ? 'urg' : ''}`}
                  style={isSelected ? { borderColor: 'rgba(237,111,40,0.5)', boxShadow: '0 0 0 1px rgba(237,111,40,0.15)' } : {}}
                >
                  <div className="cc-strip" style={{ background: stripColor }} />
                  <div className="cc-body">
                    {/* Row 1: address + badges + actions */}
                    <div className="cc-row1">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                        {!showDeleted && (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); handleContractSelect(e, contract.id, index); }}
                            style={{
                              width: 16, height: 16, borderRadius: 4, flexShrink: 0, cursor: 'pointer',
                              border: `2px solid ${isSelected ? 'var(--orange)' : 'var(--border)'}`,
                              background: isSelected ? 'var(--orange)' : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s'
                            }}
                          >
                            {isSelected && (
                              <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        )}
                        <span className="cc-addr">{contract.propertyAddress}</span>
                      </div>
                      <div className="cc-badges">
                        <span className="rtag" style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
                        {contract.propertyType && (
                          <span className="rtag rtag-b">{contract.propertyType}</span>
                        )}
                        <div className="cc-acts">
                          {showDeleted ? (
                            <>
                              <button className="cc-btn" onClick={() => handleRestore(contract.id)} title="Restaurar">
                                <CheckCircle style={{ width: 12, height: 12 }} /> Restaurar
                              </button>
                              <button className="cc-btn d" onClick={() => handlePermanentDelete(contract.id)} title="Eliminar permanentemente">
                                <X style={{ width: 12, height: 12 }} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="cc-btn p" title="Ver detalles" onClick={() => { setEditingItem(contract); setShowModal('contractDetails'); }}>
                                <Eye style={{ width: 12, height: 12 }} /> Ver
                              </button>
                              <button className="cc-btn p" title="Ver pagos" onClick={() => { setSelectedContractForPayments(contract); navigateTo('payments'); }}>
                                <DollarSign style={{ width: 12, height: 12 }} /> Pagos
                              </button>
                              <button className="cc-btn" title="Duplicar" onClick={() => handleDuplicate(contract)}>
                                <FileText style={{ width: 12, height: 12 }} />
                              </button>
                              <button className="cc-btn" title="Editar" onClick={() => { setEditingItem(contract); setShowModal('contract'); }}>
                                <Edit style={{ width: 12, height: 12 }} />
                              </button>
                              <button className="cc-btn d" title="Eliminar" onClick={() => handleDelete(contract.id)}>
                                <Trash2 style={{ width: 12, height: 12 }} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Data grid */}
                    <div className="cc-grid">
                      <div>
                        <div className="cc-fl">Inquilino</div>
                        <div className="cc-fv">{contract.tenantName}</div>
                      </div>
                      <div>
                        <div className="cc-fl">Propietario</div>
                        <div className="cc-fv">{contract.ownerName || '—'}</div>
                      </div>
                      <div>
                        <div className="cc-fl">Monto Base</div>
                        <div className="cc-fv big">{contract.currency} ${parseFloat(contract.baseRent || 0).toLocaleString('es-AR')}</div>
                      </div>
                      <div>
                        <div className="cc-fl">Vencimiento</div>
                        <div className="cc-fv">{contract.endDate ? new Date(contract.endDate).toLocaleDateString('es-AR') : '—'}</div>
                      </div>
                    </div>

                    {/* Footer: progress bar + increase info */}
                    {!showDeleted && (
                      <div className="cc-foot">
                        <div className="cc-prog">
                          <div className="cc-pt">
                            <div className="cc-pf" style={{ width: `${progress}%`, background: stripColor }} />
                          </div>
                          <span className="cc-pl">{Math.round(progress)}%</span>
                        </div>
                        {contractIncreases[contract.id]?.percentage > 0 && (
                          <span className="rtag rtag-o" style={{ fontSize: 10 }}>
                            ↑{contractIncreases[contract.id].percentage}% disponible
                          </span>
                        )}
                        {daysToEnd !== null && daysToEnd >= 0 && (
                          <span className="cc-pl">{daysToEnd}d restantes</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Nuevo Contrato — wizard de 5 pasos */}
        {showNuevoModal && (
          <NuevoContratoModal
            onClose={() => setShowNuevoModal(false)}
            onCreate={(formData) => {
              const freqMap = { 'Mensual': 1, 'Bimestral': 2, 'Trimestral': 3, 'Cuatrimestral (cada 4 meses)': 4, 'Semestral (cada 6 meses)': 6, 'Anual (cada 12 meses)': 12 };
              const cobMap  = { 'Mensual': 1, 'Bimestral': 2, 'Trimestral': 3 };
              saveContract({
                propertyAddress: formData.prop.dir,
                propertyType:    formData.prop.tipo || 'Departamento',
                ownerName:       formData.propietario.nombre,
                ownerDNI:        formData.propietario.dni,
                ownerEmail:      formData.propietario.email,
                ownerPhone:      formData.propietario.tel,
                tenantName:      formData.inquilino.nombre,
                tenantDNI:       formData.inquilino.dni,
                tenantEmail:     formData.inquilino.email,
                tenantPhone:     formData.inquilino.tel,
                startDate:       formData.contrato.inicio,
                endDate:         formData.contrato.fin,
                baseRent:        formData.contrato.monto,
                currency:        formData.contrato.moneda || 'ARS',
                indexType:       formData.contrato.indice || 'ICL',
                increaseFrequencyMonths: freqMap[formData.contrato.frecAjuste] || 4,
                paymentFrequencyMonths:  cobMap[formData.contrato.frecCobro]   || 1,
                commissionPercent: formData.contrato.comision || 10,
                status: 'ACTIVO',
              });
              setShowNuevoModal(false);
            }}
          />
        )}
      </div>
    );
  };

  // Payments View - Sistema completo integrado
  const NotificationsView = () => {
    const AlertCard = ({ alert }) => {
      const contract = contracts.find(c => Number(c.id) === Number(alert.contractId));
      const isDone = alert.isDone;
      const isContratoVencer = alert.type === 'CONTRATO_POR_VENCER';
      const isAlta = alert.priority === 'ALTA';
      const isMedia = alert.priority === 'MEDIA';
      const isAviso = !isAlta; // Era aviso (no urgente) cuando se marca

      let borderColor, bgColor, iconBg, iconColor;
      if (isDone) {
        borderColor = 'border-green-600/30';
        bgColor = darkMode ? 'bg-green-950/10' : 'bg-green-50';
        iconBg = 'bg-green-600/20'; iconColor = 'text-green-400';
      } else if (isAlta) {
        borderColor = 'border-red-500/40';
        bgColor = darkMode ? 'bg-red-950/20' : 'bg-red-50';
        iconBg = 'bg-red-600/20'; iconColor = 'text-red-400';
      } else if (isMedia) {
        borderColor = 'border-yellow-500/40';
        bgColor = darkMode ? 'bg-yellow-950/20' : 'bg-yellow-50';
        iconBg = 'bg-yellow-600/20'; iconColor = 'text-yellow-400';
      } else {
        borderColor = 'border-gray-600/30';
        bgColor = darkMode ? 'bg-gray-800/40' : 'bg-white';
        iconBg = 'bg-gray-600/20'; iconColor = 'text-gray-400';
      }

      const IconComp = isDone ? CheckCircle : isContratoVencer ? Calendar : isAlta ? AlertCircle : Clock;

      let dateLabel;
      if (isContratoVencer) {
        dateLabel = alert.daysUntilDue === 0 ? 'Contrato vence hoy'
          : alert.daysUntilDue === 1 ? 'Contrato vence mañana'
          : `Contrato vence en ${alert.daysUntilDue} días — ${new Date(alert.date).toLocaleDateString('es-AR')}`;
      } else if (alert.daysUntilDue < 0) {
        dateLabel = `Venció el ${new Date(alert.date).toLocaleDateString('es-AR')} (hace ${Math.abs(alert.daysUntilDue)} días)`;
      } else if (alert.daysUntilDue === 0) {
        dateLabel = 'Vence hoy';
      } else if (alert.daysUntilDue === 1) {
        dateLabel = 'Vence mañana';
      } else {
        dateLabel = `Vence el ${new Date(alert.date).toLocaleDateString('es-AR')} — en ${alert.daysUntilDue} días`;
      }

      return (
        <div className={`border ${borderColor} ${bgColor} rounded-xl p-4 ${isDone ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-10 h-10 ${iconBg} rounded-full flex items-center justify-center shrink-0`}>
                <IconComp className={`w-5 h-5 ${iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className={`font-semibold truncate ${isDone ? (darkMode ? 'text-gray-400 line-through' : 'text-slate-400 line-through') : darkMode ? 'text-white' : 'text-slate-800'}`}>
                  {alert.message}
                </p>
                {alert.tenant && (
                  <p className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>{alert.tenant}</p>
                )}
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {isDone ? (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-green-400 font-medium">✓ Marcado como hecho — desaparece en 24hs</span>
                      <button
                        onClick={() => undismissPaymentAlert(alert.key)}
                        className={`text-xs underline ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        Deshacer
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={`text-xs font-medium ${isAlta ? 'text-red-400' : isMedia ? 'text-yellow-400' : darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        {dateLabel}
                      </span>
                      {alert.amount > 0 && (
                        <span className={`text-xs font-semibold ${darkMode ? 'text-gray-200' : 'text-slate-700'}`}>
                          ${parseFloat(alert.amount).toLocaleString('es-AR')}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {!isDone && !isContratoVencer && (
              <div className="flex flex-col gap-2 shrink-0 items-end">
                <button
                  onClick={() => dismissPaymentAlert(alert.key, isAviso)}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
                  title="Marcar como hecho — no volverá como urgente"
                >
                  <CheckCircle className="w-3.5 h-3.5" /> Hecho
                </button>
                <div className="flex gap-1">
                  <button onClick={() => sendReminder(contract, 'email')} className="bg-[#ed6f28] hover:bg-[#c45e1e] p-1.5 rounded" title="Email">
                    <Mail className="w-3.5 h-3.5 text-white" />
                  </button>
                  <button onClick={() => sendReminder(contract, 'whatsapp')} className="bg-green-600 hover:bg-green-700 p-1.5 rounded" title="WhatsApp">
                    <MessageSquare className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };

    const urgentes  = paymentAlerts.filter(a => a.priority === 'ALTA');
    const proximos  = paymentAlerts.filter(a => a.priority === 'MEDIA');
    const futuros   = paymentAlerts.filter(a => a.priority === 'BAJA');
    const hechos    = paymentAlerts.filter(a => a.priority === 'DONE');
    const activeCount = urgentes.length + proximos.length + futuros.length;
    const noContratos = contracts.length === 0;

    return (
      <div className="page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold flex items-center gap-3 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              <Bell className="w-8 h-8 text-yellow-400" />
              Notificaciones
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              Se generan automáticamente según la frecuencia de cobro de cada contrato
            </p>
          </div>
          {activeCount > 0 && (
            <span className="bg-[#ed6f28] text-white text-sm font-bold px-3 py-1 rounded-full">
              {activeCount} pendiente{activeCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {!alertsReady ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#ed6f28] border-t-transparent rounded-full animate-spin" />
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Cargando notificaciones...</p>
            </div>
          </div>
        ) : noContratos ? (
          <div className={`border rounded-xl p-8 text-center ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-slate-200'}`}>
            <Building2 className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className={`font-medium ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>No hay contratos activos</p>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>Creá un contrato para empezar a recibir notificaciones de cobro.</p>
          </div>
        ) : activeCount === 0 && hechos.length === 0 ? (
          <div className={`border rounded-xl p-10 text-center ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-slate-200'}`}>
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>¡Todo al día!</h3>
            <p className={darkMode ? 'text-gray-400' : 'text-slate-500'}>No hay cobros próximos ni vencidos.</p>
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-600' : 'text-slate-400'}`}>{contracts.length} contrato{contracts.length !== 1 ? 's' : ''} activo{contracts.length !== 1 ? 's' : ''}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {urgentes.length > 0 && (
              <div>
                <h2 className="text-red-400 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Urgente — cobro vencido ({urgentes.length})
                </h2>
                <div className="space-y-3">{urgentes.map(a => <AlertCard key={a.id} alert={a} />)}</div>
              </div>
            )}
            {proximos.length > 0 && (
              <div>
                <h2 className="text-yellow-400 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Aviso — cobro próximo ({proximos.length})
                </h2>
                <div className="space-y-3">{proximos.map(a => <AlertCard key={a.id} alert={a} />)}</div>
              </div>
            )}
            {futuros.length > 0 && (
              <div>
                <h2 className={`font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                  <Calendar className="w-4 h-4" /> Próximamente ({futuros.length})
                </h2>
                <div className="space-y-3">{futuros.map(a => <AlertCard key={a.id} alert={a} />)}</div>
              </div>
            )}
            {hechos.length > 0 && (
              <div>
                <h2 className={`font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>
                  <CheckCircle className="w-4 h-4" /> Marcados como hechos ({hechos.length})
                </h2>
                <div className="space-y-3">{hechos.map(a => <AlertCard key={a.id} alert={a} />)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const PaymentsView = () => {
    const [showAddPayment, setShowAddPayment] = useState(false);
    const [showApplyIncrease, setShowApplyIncrease] = useState(false);
    const [showIndicesHistory, setShowIndicesHistory] = useState(false);
    const [showIncreaseHistory, setShowIncreaseHistory] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('TODOS');
    const [editingPayment, setEditingPayment] = useState(null);
    const [manualIndexEntry, setManualIndexEntry] = useState(false);

    const [newPayment, setNewPayment] = useState({
      amount: '',
      date: new Date().toISOString().split('T')[0],
      method: 'Transferencia',
      receipt: null,
      notes: '',
      isParcial: false,
      parcialAmount: ''
    });

    const [increaseData, setIncreaseData] = useState({
      indexType: 'ICL',
      previousIndex: '',
      currentIndex: '',
      percentage: '',
      currentAmount: '',
      newAmount: '',
      observations: '',
      manualEntry: false,
      canApply: true,
      validationMessage: ''
    });

    // Funciones helper
    const getContractPaymentSummary = (contractId) => {
      const contractPayments = payments.filter(p => p.contractId === contractId);
      const total = contractPayments.length;
      const paid = contractPayments.filter(p => p.status === 'PAGADO').length;
      const parcial = contractPayments.filter(p => p.status === 'PAGO PARCIAL').length;
      const pending = contractPayments.filter(p => p.status === 'PENDIENTE').length;
      const overdue = contractPayments.filter(p => p.status === 'VENCIDO').length;
      const totalPaid = contractPayments.filter(p => p.status === 'PAGADO').reduce((sum, p) => {
        const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
      const totalParcial = contractPayments.filter(p => p.status === 'PAGO PARCIAL').reduce((sum, p) => {
        const amount = parseFloat(p.paidAmount || 0);
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
      const totalPending = contractPayments.filter(p => p.status !== 'PAGADO' && p.status !== 'PAGO PARCIAL').reduce((sum, p) => {
        const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
      
      return { total, paid, parcial, pending, overdue, totalPaid: totalPaid + totalParcial, totalPending };
    };

    const calculateSuggestedIncrease = (contract) => {
      if (!contract?.startDate) return null;

      const startDate = new Date(contract.startDate);
      if (isNaN(startDate.getTime())) return null;

      const today = new Date();
      const monthsSinceStart = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());

      // Determinar la fecha base para el cálculo según la frecuencia de ajuste
      const frequencyMonths = contract.increaseFrequencyMonths || 12;

      // Calcular cuántos períodos de ajuste han pasado
      const completedPeriods = Math.floor(monthsSinceStart / frequencyMonths);

      // Si no ha pasado un período completo, no sugerir aumento
      if (completedPeriods === 0) {
        return {
          percentage: 0,
          newAmount: contract.baseRent,
          lastUpdate: null,
          message: `Próximo ajuste en ${frequencyMonths - monthsSinceStart} meses`
        };
      }

      // Calcular la fecha del último ajuste teórico (basado en la frecuencia)
      const lastAdjustmentDate = new Date(startDate);
      lastAdjustmentDate.setMonth(lastAdjustmentDate.getMonth() + (completedPeriods * frequencyMonths));

      // Buscar índices para el período actual
      const lastIndex = indices[indices.length - 1];
      const adjustmentBaseIndex = indices.find(i => {
        const indexDate = i.date.split('-');
        return parseInt(indexDate[0]) === lastAdjustmentDate.getFullYear() &&
               parseInt(indexDate[1]) === lastAdjustmentDate.getMonth() + 1;
      }) || indices.find(i => i.date === `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`);

      if (!adjustmentBaseIndex || !lastIndex) return null;

      const indexValue = contract.indexType === 'ICL'
        ? (lastIndex.ICL / adjustmentBaseIndex.ICL - 1) * 100
        : (lastIndex.IPC / adjustmentBaseIndex.IPC - 1) * 100;

      return {
        percentage: indexValue.toFixed(2),
        newAmount: Math.round(contract.baseRent * (1 + indexValue / 100)),
        lastUpdate: lastIndex.date,
        periodsCompleted: completedPeriods,
        frequencyMonths: frequencyMonths
      };
    };

    const generateIncreaseReceipt = (increase, contract) => {
  alert(`Generando recibo de aumento...\n\nPropiedad: ${contract.propertyAddress}\nAumento: +${increase.percentage.toFixed(2)}%\nNuevo monto: $${increase.newAmount.toLocaleString('es-AR')}\nFecha: ${new Date(increase.date).toLocaleDateString('es-AR')}\n\nEn un sistema real, esto descargaría un PDF.`);
};

        const applyIncrease = async () => {
      if (!selectedContractForPayments) return;

      try {
        const customAmount = increaseData.customPercentage ? parseFloat(increaseData.newAmount) : increaseData.newAmount;
        const observations = increaseData.observations || 'Aumento aplicado automáticamente según ICL';

        const response = await increasesService.applyAutomatic(
          selectedContractForPayments.id,
          customAmount,
          observations
        );

        if (response.success) {
          alert(`AUMENTO APLICADO EXITOSAMENTE\n\nDetalles:\n• Anterior: ${response.details.previousAmount.toLocaleString('es-AR')}\n• Nuevo: ${response.details.newAmount.toLocaleString('es-AR')}\n• Porcentaje: +${response.details.percentage}%\n• Fecha efectiva: ${new Date(response.details.effectiveDate).toLocaleDateString('es-AR')}\n\nEl contrato y los pagos futuros han sido actualizados`);

          // Actualizar contratos localmente
          setContracts(prev => prev.map(c =>
            c.id === selectedContractForPayments.id
              ? { ...c, baseRent: response.details.newAmount, lastUpdate: new Date().toISOString().split('T')[0] }
              : c
          ));
          // Los pagos se actualizarán al recargar
setShowApplyIncrease(false);
          setIncreaseData({
            indexType: 'ICL',
            percentage: '',
            customPercentage: false,
            newAmount: '',
            notes: '',
            observations: '',
            manualEntry: false,
            canApply: true,
            validationMessage: ''
          });
        } else {
          alert(`Error al aplicar aumento: ${response.error || 'Error desconocido'}`);
        }
      } catch (error) {
        console.error('Error al aplicar aumento:', error);
        alert(`Error al aplicar aumento: ${error.message}`);
      }
    };

    const registerPayment = async () => {  // 👈 Agregar async
  if (!selectedContractForPayments || !newPayment.amount) {
    alert('Por favor complete los campos requeridos');
    return;
  }
  
  try {
    const amount = parseFloat(newPayment.amount);
    const parcialAmount = newPayment.isParcial ? parseFloat(newPayment.parcialAmount) : amount;
    
    const paymentData = {
      contractId: selectedContractForPayments.id,
      amount: amount,
      date: newPayment.date,
      method: newPayment.method,
      notes: newPayment.notes,
      isParcial: newPayment.isParcial,
      parcialAmount: newPayment.isParcial ? parcialAmount : 0
    };
    
    // 👇 GUARDAR EN POSTGRESQL
    const response = await paymentsService.registerManual(paymentData);
    
    if (response.success) {
      // Recargar desde la base de datos
      await refreshPayments();
      
      setShowAddPayment(false);
      setNewPayment({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        method: 'Transferencia',
        receipt: null,
        notes: '',
        isParcial: false,
        parcialAmount: ''
      });
      
      alert('Pago guardado en PostgreSQL exitosamente');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('Error al guardar: ' + error.message);
  }
};

const updatePayment = async () => {
  if (!editingPayment) return;
  
  try {
    const updateData = {
      adjustedAmount: editingPayment.adjustedAmount,
      notes: editingPayment.notes
    };
    
    const response = await paymentsService.update(editingPayment.id, updateData);
    
    if (response.success) {
      await refreshPayments();
      setEditingPayment(null);
      alert('Pago actualizado en PostgreSQL');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('Error al actualizar: ' + error.message);
  }
};

  const deletePayment = async (paymentId) => {
    if (window.confirm('¿Está seguro de eliminar este pago? Esta acción no se puede deshacer.')) {
      try {
        await paymentsService.delete(paymentId);
        await refreshPayments();
        alert('Pago eliminado correctamente');
      } catch (error) {
        console.error('Error al eliminar pago:', error);
        alert('Error al eliminar pago: ' + error.message);
      }
    }
  };

const handlePaymentStatusChange = async (payment) => {
  try {
    const statusCycle = {
      'PAGADO': 'PENDIENTE',
      'PENDIENTE': 'PAGO PARCIAL',
      'PAGO PARCIAL': 'VENCIDO',
      'VENCIDO': 'PAGADO'
    };
    
    const newStatus = statusCycle[payment.status];
    
    // Usar el nuevo endpoint de updateStatus
    const response = await paymentsService.updateStatus(payment.id, newStatus);
    
    if (response.success) {
      await refreshPayments();
    }
  } catch (error) {
    console.error('Error al cambiar estado del pago:', error);
    alert('Error al cambiar el estado: ' + error.message);
    await refreshPayments();
  }
};

const getContractUrgency = (contractId) => {
      const s = getContractPaymentSummary(contractId);
      if (s.overdue > 0) return 'VENCIDO';
      if (s.parcial > 0) return 'PARCIAL';
      if (s.pending > 0) return 'PENDIENTE';
      return 'PAGADO';
    };

    const filteredContracts = contracts.filter(c => {
      const matchesSearch = c.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           c.tenantName.toLowerCase().includes(searchTerm.toLowerCase());
      const summary = getContractPaymentSummary(c.id);
      const matchesFilter = filterStatus === 'TODOS' ||
                          (filterStatus === 'PENDIENTE' && (summary.pending > 0 || summary.parcial > 0)) ||
                          (filterStatus === 'VENCIDO' && summary.overdue > 0) ||
                          (filterStatus === 'AL DIA' && summary.overdue === 0 && summary.pending === 0 && summary.parcial === 0);
      return matchesSearch && matchesFilter;
    }).sort((a, b) => {
      const order = { VENCIDO: 0, PARCIAL: 1, PENDIENTE: 2, PAGADO: 3 };
      return (order[getContractUrgency(a.id)] ?? 9) - (order[getContractUrgency(b.id)] ?? 9);
    });

    // Vista de historico de Í­ndices
    if (showIndicesHistory) {
      return (
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white">Histórico de índices</h1>
              <p className="text-gray-400 text-sm mt-1">Índices oficiales para actualización de alquileres</p>
              {indicesLastUpdate && (
                <p className="text-gray-500 text-xs mt-1">
                  Última actualización: {new Date(indicesLastUpdate).toLocaleString('es-AR')}
                  {' • '}
                  Fuente: {
                    indicesSource === 'argly' ? 'Argly API (ICL + IPC)' :
                    indicesSource === 'argly-icl' ? 'Argly API (solo ICL)' :
                    indicesSource === 'argly-ipc' ? 'Argly API (solo IPC)' :
                    indicesSource === 'mock' ? 'Datos de referencia' :
                    'Datos de referencia'
                  }
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  await refreshIndices();
                  alert('Índices actualizados');
                }}
                className="bg-[#ed6f28] hover:bg-[#c45e1e] text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Actualizar
              </button>
              <button
                onClick={() => setShowIndicesHistory(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Volver
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                ICL — Índice Contratos de Locación
              </h3>
              <p className="text-gray-500 text-xs mb-1">Fuente: Argly API • Base 30/06/2020 = 1</p>
              <p className="text-gray-400 text-sm mb-4">
                {indices.length > 0 && indices[indices.length - 1].ICL ? (
                  <>Último valor: <span className="text-white font-semibold">{indices[indices.length - 1].ICL}</span> ({indices[indices.length - 1].date})</>
                ) : (
                  <>No hay datos disponibles</>
                )}
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {[...indices].reverse().map((index, i) => (
                  <div key={i} className="flex justify-between items-center py-2 px-3 bg-gray-900/20 rounded-lg">
                    <span className="text-gray-300">{index.date}</span>
                    <span className="text-gray-400 font-semibold">{index.ICL ?? '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#ed6f28]" />
                IPC — Índice Precios al Consumidor
              </h3>
              <p className="text-gray-500 text-xs mb-1">Fuente: Argly API • Variación mensual %</p>
              <p className="text-gray-400 text-sm mb-4">
                {(() => {
                  const last = [...indices].reverse().find(i => i.IPC_var != null);
                  if (!last) return <>No hay datos disponibles</>;
                  return <>Último mes: <span className="text-white font-semibold">+{last.IPC_var}%</span> ({last.date})</>;
                })()}
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {[...indices].reverse().map((index, i) => (
                  <div key={i} className="flex justify-between items-center py-2 px-3 bg-gray-900/20 rounded-lg">
                    <span className="text-gray-300">{index.date}</span>
                    <span className="text-[#ed6f28] font-semibold">
                      {index.IPC_var != null ? `+${index.IPC_var}%` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <h3 style={{ color: "var(--t1)", fontWeight: 600, marginBottom: 16 }}>¿Cómo leer estos valores?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-400">
              <div>
                <p className="text-gray-300 font-semibold mb-2">ICL (base 30/06/2020 = 1)</p>
                <p className="mb-2">El valor actual (~30) significa que los alquileres se multiplicaron <span className="text-white">~30 veces</span> desde julio 2020.</p>
                <p>Para calcular un aumento: <span className="text-gray-300">(ICL actual ÷ ICL al inicio del contrato − 1) × 100</span></p>
                <p className="mt-2 text-gray-500">Ej: contrato en ene/2024 (ICL 15.10) → hoy (ICL 30.56) = <span className="text-white">+102% de aumento</span></p>
              </div>
              <div>
                <p className="text-gray-300 font-semibold mb-2">IPC — Variación mensual %</p>
                <p className="mb-2">Cada valor muestra cuánto subieron los precios ese mes. Ej: <span className="text-white">+2.4%</span> en marzo 2026.</p>
                <p>Para aumentos de contrato, el sistema calcula internamente el IPC acumulado entre dos fechas.</p>
                <p className="mt-2 text-gray-500">El IPC sube más que el ICL porque el ICL también incluye salarios (RIPTE), que subieron menos que los precios.</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700 space-y-1 text-xs text-gray-500">
              <p>• El ICL es el índice <span className="text-gray-400">oficial</span> para actualizar alquileres según la Ley 27.551 — publicado diariamente por el BCRA</p>
              <p>• El IPC lo publica el INDEC mensualmente con ~1 mes de demora</p>
              <p>• Los valores son índices acumulados, <span className="text-gray-400">no porcentajes directos</span> — lo que importa es la relación entre dos fechas</p>
            </div>
          </div>
        </div>
      );
    }

    // Vista de detalle del contrato (cuando viene de Contratos)
    if (selectedContractForPayments) {
      const contractPayments = payments.filter(p => p.contractId === selectedContractForPayments.id)
        .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
      const summary = getContractPaymentSummary(selectedContractForPayments.id);
      const suggestedIncrease = contractIncreases[selectedContractForPayments.id];

      const PAY_STYLE = {
        'PAGADO':      { color: 'var(--green)',  bg: 'var(--grDim)',  dot: 'var(--green)',  label: 'Pagado'    },
        'PENDIENTE':   { color: 'var(--yellow)', bg: 'var(--ylDim)', dot: 'var(--yellow)', label: 'Pendiente' },
        'VENCIDO':     { color: 'var(--red)',    bg: 'var(--rDim)',  dot: 'var(--red)',    label: 'Vencido'   },
        'PAGO PARCIAL':{ color: 'var(--orange)', bg: 'var(--orDim)', dot: 'var(--orange)', label: 'Parcial'   },
      };

      const cobradoFmt = summary.totalPaid >= 1000000
        ? `$${(summary.totalPaid / 1000000).toFixed(1)}M`
        : `$${(summary.totalPaid / 1000).toFixed(0)}K`;
      const pendienteFmt = summary.totalPending >= 1000000
        ? `$${(summary.totalPending / 1000000).toFixed(1)}M`
        : `$${(summary.totalPending / 1000).toFixed(0)}K`;

      return (
        <div className="page">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <div className="hd-title">Historial de Pagos</div>
              <div className="hd-sub">{selectedContractForPayments.propertyAddress}</div>
              <div className="hd-sub" style={{ marginTop: 2 }}>Inquilino: {selectedContractForPayments.tenantName} · ${selectedContractForPayments.baseRent?.toLocaleString('es-AR')}/mes</div>
            </div>
            <button className="hd-back" onClick={() => setSelectedContractForPayments(null)}>
              <ChevronRight style={{ width: 12, height: 12 }} />
              Volver a la lista
            </button>
          </div>

          {/* KPIs */}
          <div className="kpi-grid" style={{ marginBottom: 18 }}>
            {[
              { label: 'Total Pagos',    value: summary.total,    sub: `${summary.paid} completos`,                            color: 'var(--blue)'   },
              { label: 'Total Cobrado',  value: cobradoFmt,       sub: cobradoFmt,                                             color: 'var(--green)', big: true },
              { label: 'Pagos Parciales',value: summary.parcial,  sub: 'Requiere seguimiento',                                 color: 'var(--orange)' },
              { label: 'Pendiente',      value: summary.pending,  sub: summary.totalPending > 0 ? pendienteFmt : '-',          color: 'var(--yellow)' },
              { label: 'Vencidos',       value: summary.overdue,  sub: summary.overdue > 0 ? 'Atención urgente' : 'Sin atrasos', color: summary.overdue > 0 ? 'var(--red)' : 'var(--t3)', urgent: summary.overdue > 0 },
            ].map((k, i) => (
              <div key={i} className={`kpi-card${k.urgent ? ' urgent' : ''}`} style={{ '--kpi-color': k.color }}>
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-val" style={{ fontSize: k.big ? 28 : 25, color: k.urgent ? 'var(--red)' : k.big ? 'var(--green)' : 'var(--t1)' }}>{k.value}</div>
                <div className="kpi-foot" style={{ fontSize: 11 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Acciones */}
          <div className="hd-actions">
            <button className="hd-act solid" onClick={() => setShowAddPayment(true)}>
              <Plus style={{ width: 13, height: 13 }} /> Registrar Pago
            </button>
            <button className="hd-act yl" title="Calcula automáticamente el aumento según el índice ICL del BCRA"
              onClick={async () => {
                try {
                  const response = await increasesService.calculateAvailable(selectedContractForPayments.id);
                  if (!response || typeof response !== 'object') { alert('Error: Respuesta inválida del servidor'); return; }
                  if (response.success === true && response.canApply === true && response.data) {
                    setIncreaseData({ ...increaseData, percentage: response.data.percentage, newAmount: response.data.newAmount, currentAmount: response.data.currentAmount, previousIndex: response.data.previousIndex, currentIndex: response.data.currentIndex, referenceDate: response.data.referenceDate, lastUpdate: response.data.currentDate, canApply: true });
                    setShowApplyIncrease(true);
                  } else if (response.success && !response.canApply) {
                    setIncreaseData({ ...increaseData, canApply: false, validationMessage: response.message });
                    setShowApplyIncrease(true);
                  } else {
                    alert('Error: ' + (response.error || 'No se pudo calcular el aumento'));
                  }
                } catch (error) { alert('Error al conectar con el servidor'); }
              }}>
              <TrendingUp style={{ width: 13, height: 13 }} /> Aplicar Aumento (ICL)
            </button>
            <button className="hd-act ghost" onClick={() => setShowIncreaseHistory(true)}>
              <Calendar style={{ width: 13, height: 13 }} /> Historial de Aumentos
            </button>
            <button className="hd-act ghost" onClick={() => sendReminder(selectedContractForPayments, 'email')}>
              <Mail style={{ width: 13, height: 13 }} /> Enviar Email
            </button>
            <button className="hd-act gr" onClick={() => sendReminder(selectedContractForPayments, 'whatsapp')}>
              <MessageSquare style={{ width: 13, height: 13 }} /> WhatsApp
            </button>
            <button className="hd-act ghost" style={{ marginLeft: 'auto' }}
              onClick={() => {
                const csvContent = contractPayments.map(p => `${p.period},${p.dueDate},${p.status},${p.adjustedAmount || p.baseAmount},${p.paidDate || ''},${p.paymentMethod || ''}`).join('\n');
                const blob = new Blob([`Periodo,Vencimiento,Estado,Monto,Fecha Pago,Método\n${csvContent}`], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `pagos-${selectedContractForPayments.propertyAddress}.csv`; a.click();
              }}>
              <Download style={{ width: 13, height: 13 }} /> Exportar CSV
            </button>
          </div>

          {/* Banner aumento disponible */}
          {suggestedIncrease && increaseData.percentage > 0 && (
            <div style={{ background: 'var(--orDim)', border: '1px solid rgba(237,111,40,0.28)', borderRadius: 'var(--r)', padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <TrendingUp style={{ width: 16, height: 16, color: 'var(--orange)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--t1)', fontWeight: 650 }}>
                  Aumento disponible: <span style={{ color: 'var(--orange)', fontWeight: 760 }}>+{increaseData.percentage}%</span>
                  {' · '}Nuevo monto: <span style={{ color: 'var(--green)', fontWeight: 760 }}>${Number(increaseData.newAmount).toLocaleString('es-AR')}</span>
                </span>
              </div>
              <button className="hd-act yl" style={{ flexShrink: 0 }} onClick={() => setShowApplyIncrease(true)}>
                Aplicar ahora
              </button>
            </div>
          )}

          {/* Banner ajuste pendiente */}
          {suggestedIncrease && increaseData.percentage === 0 && suggestedIncrease.message && (
            <div style={{ background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Clock style={{ width: 14, height: 14, color: 'var(--t3)', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--t3)' }}>{suggestedIncrease.message}</span>
            </div>
          )}

          {/* Tabla de pagos */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="hd-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Periodo</th>
                  <th>Vencimiento</th>
                  <th>Monto</th>
                  <th>Fecha Pago</th>
                  <th>Método</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {contractPayments.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--t3)' }}>Sin historial registrado</td></tr>
                ) : contractPayments.map((payment) => {
                  const s = PAY_STYLE[payment.status] || PAY_STYLE['PENDIENTE'];
                  const isVencido = payment.status === 'VENCIDO';
                  const isPendiente = payment.status === 'PENDIENTE';
                  const isPagado = payment.status === 'PAGADO';
                  const monto = parseFloat(payment.adjustedAmount || payment.baseAmount || 0);
                  return (
                    <tr key={payment.id} className={isVencido ? 'r-urg' : ''}>
                      <td>
                        <button
                          onClick={() => handlePaymentStatusChange(payment)}
                          title="Click para cambiar estado"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, borderRadius: 4, padding: '3px 9px', fontSize: 11, fontWeight: 650, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                          {s.label.toUpperCase()}
                        </button>
                      </td>
                      <td style={{ color: 'var(--t2)', fontWeight: 500 }}>{payment.period}</td>
                      <td style={{ color: 'var(--t3)' }}>{new Date(payment.dueDate).toLocaleDateString('es-AR')}</td>
                      <td>
                        <span className="hd-monto">${monto.toLocaleString('es-AR')}</span>
                        {payment.status === 'PAGO PARCIAL' && (
                          <div style={{ fontSize: 10.5, color: 'var(--orange)', marginTop: 1 }}>Pagado: ${payment.paidAmount?.toLocaleString('es-AR')}</div>
                        )}
                      </td>
                      <td style={{ color: payment.paidDate ? 'var(--t2)' : 'var(--t3)' }}>
                        {payment.paidDate ? new Date(payment.paidDate).toLocaleDateString('es-AR') : '-'}
                      </td>
                      <td><span className="hd-metodo">{payment.paymentMethod || '-'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                          {(isVencido || isPendiente) && (
                            <button className="hd-icn o" title="Registrar cobro" onClick={() => setShowAddPayment(true)}>
                              <DollarSign style={{ width: 11, height: 11 }} />
                            </button>
                          )}
                          {isPagado && (
                            <button className="hd-icn g" title="Descargar recibo" onClick={() => generateReceipt(payment)}>
                              <Download style={{ width: 11, height: 11 }} />
                            </button>
                          )}
                          <button className="hd-icn" title="Editar" onClick={() => setEditingPayment(payment)}>
                            <Edit style={{ width: 11, height: 11 }} />
                          </button>
                          <button className="hd-icn r" title="Eliminar" onClick={() => deletePayment(payment.id)}>
                            <Trash2 style={{ width: 11, height: 11 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>


          {/* Modal: Registrar Pago */}
          {showAddPayment && (
            <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddPayment(false)}>
              <div className="modal">
                <div className="modal-head">
                  <span className="modal-title">Registrar Pago</span>
                  <button className="modal-close" onClick={() => setShowAddPayment(false)}><X size={14}/></button>
                </div>
                <div className="modal-body">
                  <div className="form-field">
                    <label>Monto Total *</label>
                    <input type="number" value={newPayment.amount} onChange={e => setNewPayment({...newPayment, amount: e.target.value})} placeholder="350000"/>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'var(--orDim)', borderRadius:'var(--r2)', border:'1px solid rgba(237,111,40,0.25)' }}>
                    <input type="checkbox" id="isParcial" checked={newPayment.isParcial} onChange={e => setNewPayment({...newPayment, isParcial: e.target.checked})} style={{ width:15, height:15, accentColor:'var(--orange)', cursor:'pointer' }}/>
                    <label htmlFor="isParcial" style={{ fontSize:13, color:'var(--t1)', cursor:'pointer' }}>Este es un pago parcial</label>
                  </div>
                  {newPayment.isParcial && (
                    <div className="form-field">
                      <label>Monto Pagado *</label>
                      <input type="number" value={newPayment.parcialAmount} onChange={e => setNewPayment({...newPayment, parcialAmount: e.target.value})} placeholder="200000"/>
                    </div>
                  )}
                  <div className="form-row col2">
                    <div className="form-field">
                      <label>Fecha de Pago *</label>
                      <input type="date" value={newPayment.date} onChange={e => setNewPayment({...newPayment, date: e.target.value})}/>
                    </div>
                    <div className="form-field">
                      <label>Método de Pago</label>
                      <select value={newPayment.method} onChange={e => setNewPayment({...newPayment, method: e.target.value})}>
                        <option>Transferencia</option>
                        <option>Efectivo</option>
                        <option>Cheque</option>
                        <option>Mercado Pago</option>
                        <option>Depósito</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Observaciones</label>
                    <textarea value={newPayment.notes} onChange={e => setNewPayment({...newPayment, notes: e.target.value})} placeholder="Notas adicionales..." style={{ height:80, resize:'none' }}/>
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="modal-btn cancel" onClick={() => setShowAddPayment(false)}>Cancelar</button>
                  <button className="modal-btn create" onClick={registerPayment}><Save size={13}/> Registrar Pago</button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Editar Pago */}
          {editingPayment && (
            <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditingPayment(null)}>
              <div className="modal">
                <div className="modal-head">
                  <span className="modal-title">Editar Pago · {editingPayment.period}</span>
                  <button className="modal-close" onClick={() => setEditingPayment(null)}><X size={14}/></button>
                </div>
                <div className="modal-body">
                  <div className="form-field">
                    <label>Monto</label>
                    <input type="number" value={editingPayment.adjustedAmount || editingPayment.baseAmount} onChange={e => setEditingPayment({...editingPayment, adjustedAmount: parseFloat(e.target.value)})}/>
                  </div>
                  <div className="form-field">
                    <label>Notas</label>
                    <textarea value={editingPayment.notes || ''} onChange={e => setEditingPayment({...editingPayment, notes: e.target.value})} style={{ height:80, resize:'none' }}/>
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="modal-btn cancel" onClick={() => setEditingPayment(null)}>Cancelar</button>
                  <button className="modal-btn next" onClick={updatePayment}>Guardar Cambios</button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Historial de Aumentos */}
          {showIncreaseHistory && (
            <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowIncreaseHistory(false)}>
              <div className="modal" style={{ maxWidth: 860 }}>
                <div className="modal-head">
                  <div>
                    <div className="modal-title">Historial de Aumentos</div>
                    <div style={{ fontSize:11, color:'var(--t3)', marginTop:2 }}>{selectedContractForPayments.propertyAddress}</div>
                  </div>
                  <button className="modal-close" onClick={() => setShowIncreaseHistory(false)}><X size={14}/></button>
                </div>
                <div className="modal-body">
                  {/* KPI resumen */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                    {[
                      { label:'Total Aumentos', value: increaseHistory.filter(h => h.contractId === selectedContractForPayments.id).length, color:'var(--t1)' },
                      { label:'Incremento Acumulado', value: (() => { const h = increaseHistory.filter(h => h.contractId === selectedContractForPayments.id); if (!h.length) return '0%'; const acc = ((h[h.length-1].newAmount - h[0].previousAmount) / h[0].previousAmount * 100).toFixed(2); return `+${acc}%`; })(), color:'var(--orange)' },
                      { label:'Monto Actual', value: `$${selectedContractForPayments.baseRent.toLocaleString('es-AR')}`, color:'var(--green)' },
                    ].map((k,i) => (
                      <div key={i} style={{ background:'var(--surf2)', border:'1px solid var(--border)', borderRadius:'var(--r2)', padding:'12px 14px' }}>
                        <div style={{ fontSize:10.5, color:'var(--t3)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{k.label}</div>
                        <div style={{ fontSize:20, fontWeight:760, color:k.color }}>{k.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Lista */}
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {increaseHistory
                      .filter(h => h.contractId === selectedContractForPayments.id)
                      .sort((a,b) => new Date(b.date) - new Date(a.date))
                      .map((inc, idx, arr) => (
                        <div key={inc.id} style={{ background:'var(--surf2)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 16px' }}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                              <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--orDim)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <TrendingUp size={15} color="var(--orange)"/>
                              </div>
                              <div>
                                <div style={{ fontSize:13, fontWeight:650, color:'var(--t1)' }}>Aumento #{arr.length - idx}</div>
                                <div style={{ fontSize:11, color:'var(--t3)' }}>{new Date(inc.date).toLocaleDateString('es-AR', { day:'numeric', month:'long', year:'numeric' })}</div>
                              </div>
                            </div>
                            <span style={{ padding:'2px 8px', background: inc.manualEntry ? 'var(--ylDim)' : 'var(--orDim)', color: inc.manualEntry ? 'var(--yellow)' : 'var(--orange)', borderRadius:4, fontSize:10.5, fontWeight:650 }}>
                              {inc.manualEntry ? 'Manual' : `${inc.type} Automático`}
                            </span>
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6, marginBottom:8 }}>
                            {[
                              { l:'Índice Anterior', v: inc.previousIndex.toFixed(4), c:'var(--t1)' },
                              { l:'Índice Actual',   v: inc.currentIndex.toFixed(4),  c:'var(--green)' },
                              { l:'Variación',       v: `+${inc.percentage.toFixed(2)}%`, c:'var(--orange)' },
                              { l:'Monto Anterior',  v: `$${inc.previousAmount.toLocaleString('es-AR')}`, c:'var(--t1)' },
                              { l:'Nuevo Monto',     v: `$${inc.newAmount.toLocaleString('es-AR')}`, c:'var(--green)' },
                            ].map((f,i) => (
                              <div key={i} style={{ background:'var(--surf)', border:'1px solid var(--border)', borderRadius:'var(--r2)', padding:'8px 10px' }}>
                                <div style={{ fontSize:9.5, color:'var(--t3)', marginBottom:3, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.l}</div>
                                <div style={{ fontSize:13, fontWeight:720, color:f.c }}>{f.v}</div>
                              </div>
                            ))}
                          </div>
                          {inc.observations && (
                            <div style={{ fontSize:11.5, color:'var(--t3)', padding:'6px 10px', background:'var(--surf)', borderRadius:'var(--r2)', border:'1px solid var(--border)' }}>
                              {inc.observations}
                            </div>
                          )}
                          <div style={{ marginTop:8 }}>
                            <button className="hd-act ghost" style={{ width:'100%', justifyContent:'center' }} onClick={() => generateIncreaseReceipt(inc, selectedContractForPayments)}>
                              <Download size={12}/> Descargar Recibo
                            </button>
                          </div>
                        </div>
                      ))}
                    {increaseHistory.filter(h => h.contractId === selectedContractForPayments.id).length === 0 && (
                      <div style={{ textAlign:'center', padding:'40px 20px', color:'var(--t3)' }}>
                        <TrendingUp size={40} style={{ opacity:0.3, margin:'0 auto 10px' }}/>
                        <div style={{ fontWeight:650, color:'var(--t2)', marginBottom:6 }}>Sin aumentos registrados</div>
                        <div style={{ fontSize:12 }}>Este contrato aún no tiene aumentos aplicados</div>
                        <button className="hd-act yl" style={{ marginTop:12 }} onClick={() => { setShowIncreaseHistory(false); setShowApplyIncrease(true); }}>
                          Aplicar Primer Aumento
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="modal-btn cancel" onClick={() => setShowIncreaseHistory(false)}>Cerrar</button>
                  <button className="modal-btn next" onClick={() => {
                    const csvContent = increaseHistory.filter(h => h.contractId === selectedContractForPayments.id).map(h => `${h.date},${h.type},${h.previousIndex},${h.currentIndex},${h.percentage},${h.previousAmount},${h.newAmount},"${h.observations}"`).join('\n');
                    const blob = new Blob([`Fecha,Tipo,Índice Anterior,Índice Actual,Variación %,Monto Anterior,Nuevo Monto,Observaciones\n${csvContent}`], { type:'text/csv' });
                    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `aumentos-${selectedContractForPayments.propertyAddress}.csv`; a.click();
                  }}><Download size={13}/> Exportar CSV</button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Aplicar Aumento */}
          {showApplyIncrease && (
            <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowApplyIncrease(false)}>
              <div className="modal" style={{ maxWidth: 680 }}>
                <div className="modal-head">
                  <div>
                    <div className="modal-title">Aplicar Aumento (ICL)</div>
                    <div style={{ fontSize:11, color:'var(--t3)', marginTop:2 }}>Cálculo automático según índice oficial del BCRA</div>
                  </div>
                  <button className="modal-close" onClick={() => setShowApplyIncrease(false)}><X size={14}/></button>
                </div>
                <div className="modal-body">
                  {!increaseData.canApply ? (
                    <div style={{ background:'var(--rDim)', border:'1px solid rgba(248,113,113,0.25)', borderRadius:'var(--r)', padding:'16px', display:'flex', gap:12 }}>
                      <AlertCircle size={18} color="var(--red)" style={{ flexShrink:0, marginTop:1 }}/>
                      <div>
                        <div style={{ fontSize:13, fontWeight:650, color:'var(--red)', marginBottom:4 }}>No se puede aplicar el aumento</div>
                        <div style={{ fontSize:12, color:'var(--t2)' }}>{increaseData.validationMessage}</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Cálculo automático */}
                      <div style={{ background:'var(--surf2)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 16px' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'var(--t3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
                          <TrendingUp size={11}/> Cálculo Automático (BCRA)
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8 }}>
                          {[
                            { l:'Índice anterior', v: increaseData.previousIndex, c:'var(--t1)' },
                            { l:'Índice actual',   v: increaseData.currentIndex,  c:'var(--green)' },
                            { l:'Variación',       v: `+${increaseData.percentage}%`, c:'var(--orange)', big:true },
                            { l:'Monto actual',    v: `$${Number(increaseData.currentAmount).toLocaleString('es-AR')}`, c:'var(--t1)' },
                          ].map((f,i) => (
                            <div key={i} style={{ background:'var(--surf)', border:'1px solid var(--border)', borderRadius:'var(--r2)', padding:'8px 10px' }}>
                              <div style={{ fontSize:9.5, color:'var(--t3)', marginBottom:3, fontWeight:600, textTransform:'uppercase' }}>{f.l}</div>
                              <div style={{ fontSize: f.big ? 18 : 13, fontWeight:720, color:f.c }}>{f.v}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Entrada manual toggle */}
                      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'var(--ylDim)', borderRadius:'var(--r2)', border:'1px solid rgba(245,186,69,0.25)' }}>
                        <input type="checkbox" id="manualEntry" checked={increaseData.manualEntry}
                          onChange={e => { const m = e.target.checked; setIncreaseData({...increaseData, manualEntry: m, previousIndex: m ? '' : increaseData.previousIndex, currentIndex: m ? '' : increaseData.currentIndex, percentage: m ? '' : increaseData.percentage, newAmount: m ? '' : increaseData.newAmount.toString() }); }}
                          style={{ width:14, height:14, accentColor:'var(--yellow)', cursor:'pointer' }}/>
                        <label htmlFor="manualEntry" style={{ fontSize:12, color:'var(--t1)', cursor:'pointer' }}>
                          <span style={{ fontWeight:650 }}>Entrada manual de índices</span>
                          <span style={{ color:'var(--t3)', marginLeft:6 }}>— para valores del BCRA personalizados</span>
                        </label>
                      </div>

                      {/* Campos manuales */}
                      {increaseData.manualEntry && (
                        <div className="form-row col2">
                          <div className="form-field">
                            <label>Índice Anterior *</label>
                            <input type="number" step="0.0001" value={increaseData.previousIndex} placeholder="2.1420"
                              onChange={e => { const prev = parseFloat(e.target.value)||0; const curr = parseFloat(increaseData.currentIndex)||0; const pct = curr&&prev ? ((curr-prev)/prev*100).toFixed(2) : ''; const amt = pct ? Math.round(increaseData.currentAmount*(1+parseFloat(pct)/100)) : ''; setIncreaseData({...increaseData, previousIndex:e.target.value, percentage:pct.toString(), newAmount:amt.toString()}); }}/>
                          </div>
                          <div className="form-field">
                            <label>Índice Actual *</label>
                            <input type="number" step="0.0001" value={increaseData.currentIndex} placeholder="2.3982"
                              onChange={e => { const curr = parseFloat(e.target.value)||0; const prev = parseFloat(increaseData.previousIndex)||0; const pct = curr&&prev ? ((curr-prev)/prev*100).toFixed(2) : ''; const amt = pct ? Math.round(increaseData.currentAmount*(1+parseFloat(pct)/100)) : ''; setIncreaseData({...increaseData, currentIndex:e.target.value, percentage:pct.toString(), newAmount:amt.toString()}); }}/>
                          </div>
                          {increaseData.percentage && (
                            <div style={{ gridColumn:'span 2', padding:'8px 12px', background:'var(--surf2)', borderRadius:'var(--r2)', border:'1px solid var(--border)', fontSize:12, color:'var(--t2)' }}>
                              Variación calculada: <strong style={{ color:'var(--orange)' }}>+{increaseData.percentage}%</strong>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Nuevo monto */}
                      <div className="form-field">
                        <label>Nuevo Monto (editable) *</label>
                        <input type="number" value={increaseData.newAmount || ''} onChange={e => setIncreaseData({...increaseData, newAmount: e.target.value})} placeholder="134340" style={{ fontSize:16, fontWeight:650 }}/>
                        <span style={{ fontSize:11, color:'var(--t3)', marginTop:2 }}>Podés ajustar o redondear según acuerdo con el propietario</span>
                      </div>

                      {/* Observaciones */}
                      <div className="form-field">
                        <label>Observaciones</label>
                        <textarea value={increaseData.observations} onChange={e => setIncreaseData({...increaseData, observations: e.target.value})} placeholder="Ej: Ajuste redondeado por acuerdo con propietario..." style={{ height:70, resize:'none' }}/>
                      </div>

                      {/* Info */}
                      <div style={{ fontSize:11.5, color:'var(--t3)', padding:'10px 12px', background:'var(--ylDim)', borderRadius:'var(--r2)', border:'1px solid rgba(245,186,69,0.2)', display:'flex', gap:8 }}>
                        <AlertCircle size={13} color="var(--yellow)" style={{ flexShrink:0, marginTop:1 }}/>
                        <span>Se actualizará el monto base del contrato · Los pagos futuros usarán el nuevo monto · El próximo ajuste puede aplicarse en 12 meses</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-foot">
                  <button className="modal-btn cancel" onClick={() => setShowApplyIncrease(false)}>Cancelar</button>
                  {increaseData.canApply && (
                    <button className="modal-btn create" onClick={() => { setTimeout(() => applyIncrease(), 100); }}>
                      <TrendingUp size={13}/> Aplicar Aumento
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Vista principal - Lista de contratos (cuando no hay contrato seleccionado)
    const totalCobrado = payments.filter(p => p.status === 'PAGADO').reduce((sum, p) => {
      const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    const totalCobradoFmt = totalCobrado >= 1000000 ? `$${(totalCobrado/1000000).toFixed(1)}M` : `$${(totalCobrado/1000).toFixed(0)}K`;

    return (
      <div className="page">
        {/* Mini KPI strip */}
        <div className="ct-kpis">
          <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}>
            <div className="ct-kpi-n">{totalCobradoFmt}</div>
            <div className="ct-kpi-l">Total cobrado</div>
          </div>
          <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}>
            <div className="ct-kpi-n">{payments.filter(p=>p.status==='PENDIENTE').length}</div>
            <div className="ct-kpi-l">Pendientes</div>
          </div>
          <div className="ct-kpi" style={{'--ck-c':'var(--red)'}}>
            <div className="ct-kpi-n">{payments.filter(p=>p.status==='VENCIDO').length}</div>
            <div className="ct-kpi-l">Vencidos</div>
          </div>
          <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}>
            <div className="ct-kpi-n">{contracts.filter(c=>c.status==='ACTIVO').length}</div>
            <div className="ct-kpi-l">Contratos activos</div>
          </div>
        </div>

        {/* Acciones */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <button
            onClick={() => {
              const fileName = exportPaymentsToExcel(payments, contracts, currentUser?.name || 'Usuario');
              alert(`Exportación exitosa!\n\nArchivo descargado: ${fileName}`);
            }}
            className="btn-o"
            style={{ padding: '8px 14px', fontSize: 12.5 }}
            title="Exportar pagos a Excel"
          >
            <Download style={{ width: 13, height: 13 }} />
            Exportar
          </button>
          <button
            onClick={() => navigateTo('notifications')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--ylDim)', color: 'var(--yellow)', border: '1px solid rgba(245,186,69,0.28)', borderRadius: 'var(--r2)', fontSize: 12.5, fontWeight: 650, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <Bell style={{ width: 13, height: 13 }} />
            Recordatorios ({paymentAlerts.length})
          </button>
          <button
            onClick={() => setShowIndicesHistory(true)}
            className="btn-o"
            style={{ padding: '8px 14px', fontSize: 12.5, marginLeft: 'auto' }}
          >
            <TrendingUp style={{ width: 13, height: 13 }} />
            Ver índices
          </button>
        </div>

        {/* Búsqueda y filtros */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div className="ct-search" style={{ flex: 1 }}>
            <div className="ct-s-ico"><Search style={{ width: 13, height: 13 }} /></div>
            <input
              type="text"
              placeholder="Buscar por dirección o inquilino..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--r2)', padding: '8px 12px', fontSize: 12.5, color: 'var(--t1)', fontFamily: 'inherit', outline: 'none' }}
          >
            <option value="TODOS">Todos los contratos</option>
            <option value="AL DIA">Al día</option>
            <option value="PENDIENTE">Con pendientes</option>
            <option value="VENCIDO">Con vencidos</option>
          </select>
        </div>

        {/* Lista de contratos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredContracts.map((contract) => {
            const summary = getContractPaymentSummary(contract.id);
            const suggestedIncrease = contractIncreases[contract.id];
            const urgency = getContractUrgency(contract.id);
            const STRIP = { VENCIDO: 'var(--red)', PARCIAL: 'var(--orange)', PENDIENTE: 'var(--yellow)', PAGADO: 'var(--green)' };
            const BADGE_STYLE = {
              VENCIDO:   { color: 'var(--red)',    bg: 'var(--rDim)',  label: 'VENCIDO'   },
              PARCIAL:   { color: 'var(--orange)', bg: 'var(--orDim)', label: 'PARCIAL'   },
              PENDIENTE: { color: 'var(--yellow)', bg: 'var(--ylDim)', label: 'PENDIENTE' },
              PAGADO:    { color: 'var(--green)',  bg: 'var(--grDim)', label: 'AL DÍA'   },
            };
            const badge = BADGE_STYLE[urgency] || BADGE_STYLE.PAGADO;
            const cobradoFmt = summary.totalPaid >= 1000000
              ? `$${(summary.totalPaid / 1000000).toFixed(1)}M`
              : `$${(summary.totalPaid / 1000).toFixed(0)}K`;

            return (
              <div key={contract.id} className="pg-contract">
                <div className="pg-head">
                  <div style={{ width: 3, height: 40, borderRadius: 2, background: STRIP[urgency], flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pg-addr">{contract.propertyAddress}</div>
                    <div className="pg-tenant">
                      {contract.tenantName} · ${contract.baseRent.toLocaleString('es-AR')}/mes
                      {suggestedIncrease && suggestedIncrease.percentage && parseFloat(suggestedIncrease.percentage) > 0 && (
                        <span style={{ marginLeft: 8, padding: '1px 7px', background: 'var(--orDim)', color: 'var(--orange)', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                          +{suggestedIncrease.percentage}% disponible
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="pg-stats">
                    {summary.paid > 0 && (
                      <div className="pg-stat">
                        <span className="pg-stat-n" style={{ color: 'var(--green)' }}>{summary.paid}</span>
                        <span className="pg-stat-l">Pagados</span>
                      </div>
                    )}
                    {summary.pending > 0 && (
                      <div className="pg-stat">
                        <span className="pg-stat-n" style={{ color: 'var(--yellow)' }}>{summary.pending}</span>
                        <span className="pg-stat-l">Pendientes</span>
                      </div>
                    )}
                    {summary.overdue > 0 && (
                      <div className="pg-stat">
                        <span className="pg-stat-n" style={{ color: 'var(--red)' }}>{summary.overdue}</span>
                        <span className="pg-stat-l">Vencidos</span>
                      </div>
                    )}
                    {summary.parcial > 0 && (
                      <div className="pg-stat">
                        <span className="pg-stat-n" style={{ color: 'var(--orange)' }}>{summary.parcial}</span>
                        <span className="pg-stat-l">Parciales</span>
                      </div>
                    )}
                    <div style={{ paddingLeft: 14, borderLeft: '1px solid var(--border)', textAlign: 'right' }}>
                      <div className="pg-cobrado">{cobradoFmt}</div>
                      <div className="pg-cobrado-l">cobrado</div>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: badge.bg, color: badge.color, borderRadius: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 650, flexShrink: 0 }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: badge.color, flexShrink: 0 }} />
                      {badge.label}
                    </span>
                    <button
                      className="btn-o"
                      style={{ padding: '5px 11px', fontSize: 11.5, borderRadius: 'var(--r2)', flexShrink: 0 }}
                      onClick={() => setSelectedContractForPayments(contract)}
                    >
                      Ver Historial →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Contract Details Modal
  const ContractDetailsModal = () => {
    if (!editingItem) return null;
    const c = editingItem;
    const cPagos = payments.filter(p => p.contractId === c.id);
    const freqLabel = { 1: 'Mensual', 2: 'Bimestral', 3: 'Trimestral', 4: 'Cuatrimestral', 6: 'Semestral', 12: 'Anual' };
    const cobLabel  = { 1: 'Mensual', 2: 'Bimestral', 3: 'Trimestral', 6: 'Semestral', 12: 'Anual' };

    const S = {
      overlay: { position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:20, background:'rgba(0,0,0,0.72)' },
      modal:   { background:_C.surf, border:`1px solid ${_C.borderB}`, borderRadius:14, boxShadow:'0 32px 100px rgba(0,0,0,0.7)', width:'100%', maxWidth:640, maxHeight:'88vh', display:'flex', flexDirection:'column' },
      header:  { padding:'17px 22px 15px', borderBottom:`1px solid ${_C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 },
      body:    { overflowY:'auto', padding:'18px 22px', display:'flex', flexDirection:'column', gap:14 },
      footer:  { padding:'14px 22px', borderTop:`1px solid ${_C.border}`, display:'flex', gap:10, flexShrink:0 },
      section: { background:_C.surf2, border:`1px solid ${_C.border}`, borderRadius:10, padding:'13px 16px' },
      sTitle:  { fontSize:11, fontWeight:700, color:_C.t3, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10, display:'flex', alignItems:'center', gap:6 },
      grid:    { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 20px' },
      label:   { fontSize:11, color:_C.t3, marginBottom:2 },
      value:   { fontSize:13, fontWeight:600, color:_C.t1 },
    };

    const Row = ({ label, value }) => (
      <div>
        <div style={S.label}>{label}</div>
        <div style={S.value}>{value || '—'}</div>
      </div>
    );

    return ReactDOM.createPortal(
      <div style={S.overlay} onClick={e => e.target === e.currentTarget && (setShowModal(null), setEditingItem(null))}>
        <div style={S.modal}>
          {/* Header */}
          <div style={S.header}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:_C.orange, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:900, fontSize:12 }}>R</div>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:_C.t1 }}>Detalles del Contrato</div>
                <div style={{ fontSize:11, color:_C.t3 }}>{c.propertyAddress}</div>
              </div>
            </div>
            <button onClick={() => { setShowModal(null); setEditingItem(null); }}
              style={{ width:28, height:28, borderRadius:6, background:_C.surf2, border:`1px solid ${_C.border}`, color:_C.t2, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div style={S.body}>
            {/* Propiedad */}
            <div style={S.section}>
              <div style={S.sTitle}><Building2 size={12} /> Propiedad</div>
              <div style={S.grid}>
                <Row label="Dirección" value={c.propertyAddress} />
                <Row label="Tipo" value={c.propertyType} />
                <Row label="Estado" value={
                  <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background: c.status === 'ACTIVO' ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.06)', color: c.status === 'ACTIVO' ? _C.green : _C.t3 }}>
                    {c.status}
                  </span>
                } />
                <Row label="Creado" value={new Date(c.createdAt).toLocaleDateString('es-AR')} />
              </div>
            </div>

            {/* Propietario */}
            <div style={S.section}>
              <div style={S.sTitle}><User size={12} /> Propietario</div>
              <div style={S.grid}>
                <Row label="Nombre" value={c.ownerName} />
                <Row label="DNI / CUIT" value={c.ownerDNI} />
                <Row label="Email" value={c.ownerEmail} />
                <Row label="Teléfono" value={c.ownerPhone} />
              </div>
            </div>

            {/* Inquilino */}
            <div style={S.section}>
              <div style={S.sTitle}><Users size={12} /> Inquilino</div>
              <div style={S.grid}>
                <Row label="Nombre" value={c.tenantName} />
                <Row label="DNI" value={c.tenantDNI} />
                <Row label="Email" value={c.tenantEmail} />
                <Row label="Teléfono" value={c.tenantPhone} />
              </div>
            </div>

            {/* Contrato */}
            <div style={S.section}>
              <div style={S.sTitle}><FileText size={12} /> Contrato</div>
              <div style={S.grid}>
                <Row label="Inicio" value={new Date(c.startDate).toLocaleDateString('es-AR')} />
                <Row label="Fin" value={new Date(c.endDate).toLocaleDateString('es-AR')} />
                <Row label="Monto base" value={`${c.currency} $${c.baseRent?.toLocaleString('es-AR')}`} />
                <Row label="Moneda" value={c.currency === 'USD' ? 'Dólar' : 'Peso Argentino'} />
                <Row label="Índice" value={c.indexType} />
                <Row label="Comisión" value={`${c.commissionPercent}%`} />
                <Row label="Ajuste" value={freqLabel[c.increaseFrequencyMonths] || `${c.increaseFrequencyMonths} meses`} />
                <Row label="Cobro" value={cobLabel[c.paymentFrequencyMonths] || `${c.paymentFrequencyMonths} meses`} />
              </div>
            </div>

            {/* Estadísticas */}
            <div style={{ ...S.section, display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {[
                { n: cPagos.length, l: 'Total pagos', c: _C.t1 },
                { n: cPagos.filter(p => p.status === 'PAGADO').length, l: 'Realizados', c: _C.green },
                { n: cPagos.filter(p => p.status === 'PENDIENTE' || p.status === 'VENCIDO').length, l: 'Pendientes', c: '#facc15' },
              ].map(x => (
                <div key={x.l} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800, color:x.c }}>{x.n}</div>
                  <div style={{ fontSize:11, color:_C.t3, marginTop:2 }}>{x.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div style={S.footer}>
            <button onClick={() => { setShowModal(null); setEditingItem(null); }}
              style={{ flex:1, padding:'9px 0', borderRadius:8, background:_C.surf2, border:`1px solid ${_C.border}`, color:_C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              Cerrar
            </button>
            <button onClick={() => setShowModal('contract')}
              style={{ flex:1, padding:'9px 0', borderRadius:8, background:_C.orange, border:'none', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <Edit size={14} /> Editar Contrato
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Contract Modal
const ContractModal = () => {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-8">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">{editingItem ? 'Editar' : 'Nuevo'} Contrato</h2>
          <button onClick={() => {setShowModal(null); setEditingItem(null);}} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* FORM con onSubmit que llama a saveContract */}
        <form
          key={editingItem?.id || 'new-contract'}
          onSubmit={(e) => {
            e.preventDefault();
            const formData = Object.fromEntries(new FormData(e.currentTarget));
            saveContract(formData);
          }}
        >
          <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="col-span-2">
              <h3 style={{ color: "var(--t1)", fontWeight: 600, marginBottom: 12 }}>Datos de la Propiedad</h3>
            </div>
            
            <div className="col-span-2">
              <label className="text-gray-400 text-sm block mb-2">Dirección *</label>
              <input
                type="text"
                name="propertyAddress"
                defaultValue={editingItem?.propertyAddress || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Tipo de Propiedad</label>
              <select
                name="propertyType"
                defaultValue={editingItem?.propertyType || 'Departamento'}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              >
                <option>Departamento</option>
                <option>Casa</option>
                <option>Local Comercial</option>
                <option>Oficina</option>
              </select>
            </div>

            <div className="col-span-2 border-t border-gray-700 pt-4 mt-2">
              <h3 style={{ color: "var(--t1)", fontWeight: 600, marginBottom: 12 }}>Datos del Propietario</h3>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Nombre Completo *</label>
              <input
                type="text"
                name="ownerName"
                defaultValue={editingItem?.ownerName || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">DNI/CUIT</label>
              <input
                type="text"
                name="ownerDNI"
                defaultValue={editingItem?.ownerDNI || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Email</label>
              <input
                type="email"
                name="ownerEmail"
                defaultValue={editingItem?.ownerEmail || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Teléfono</label>
              <input
                type="tel"
                name="ownerPhone"
                defaultValue={editingItem?.ownerPhone || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div className="col-span-2 border-t border-gray-700 pt-4 mt-2">
              <h3 style={{ color: "var(--t1)", fontWeight: 600, marginBottom: 12 }}>Datos del Inquilino</h3>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Nombre Completo *</label>
              <input
                type="text"
                name="tenantName"
                defaultValue={editingItem?.tenantName || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">DNI</label>
              <input
                type="text"
                name="tenantDNI"
                defaultValue={editingItem?.tenantDNI || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Email</label>
              <input
                type="email"
                name="tenantEmail"
                defaultValue={editingItem?.tenantEmail || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Teléfono</label>
              <input
                type="tel"
                name="tenantPhone"
                defaultValue={editingItem?.tenantPhone || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div className="col-span-2 border-t border-gray-700 pt-4 mt-2">
              <h3 style={{ color: "var(--t1)", fontWeight: 600, marginBottom: 12 }}>Datos del Contrato</h3>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Fecha Inicio *</label>
              <input
                type="date"
                name="startDate"
                defaultValue={editingItem?.startDate ? editingItem.startDate.split('T')[0] : ''}
                required
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Fecha Fin *</label>
              <input
                type="date"
                name="endDate"
                defaultValue={editingItem?.endDate ? editingItem.endDate.split('T')[0] : ''}
                required
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Monto Base *</label>
              <input
                type="number"
                name="baseRent"
                defaultValue={editingItem?.baseRent || ''}
                required
                step="0.01"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Moneda</label>
              <select 
                name="currency"
                defaultValue={editingItem?.currency || 'ARS'}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              >
                <option value="ARS">ARS - Peso Argentino</option>
                <option value="USD">USD - Dólar</option>
              </select>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Índice de Ajuste</label>
              <select
                name="indexType"
                defaultValue={editingItem?.indexType || 'ICL'}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              >
                <option value="ICL">ICL - Índice Contratos Locación</option>
                <option value="IPC">IPC - Índice Precios Consumidor</option>
                <option value="NINGUNO">Sin Índice</option>
              </select>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Frecuencia de Ajuste</label>
              <select
                name="increaseFrequencyMonths"
                defaultValue={editingItem?.increaseFrequencyMonths || 12}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              >
                <option value="3">Cada 3 meses (trimestral)</option>
                <option value="4">Cada 4 meses (cuatrimestral)</option>
                <option value="6">Cada 6 meses (semestral)</option>
                <option value="12">Cada 12 meses (anual)</option>
              </select>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Frecuencia de Cobro</label>
              <select
                name="paymentFrequencyMonths"
                defaultValue={editingItem?.paymentFrequencyMonths || 1}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              >
                <option value="1">Mensual (cada 1 mes)</option>
                <option value="2">Bimestral (cada 2 meses)</option>
                <option value="3">Trimestral (cada 3 meses)</option>
                <option value="6">Semestral (cada 6 meses)</option>
                <option value="12">Anual (cada 12 meses)</option>
              </select>
            </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">% Comisión</label>
              <input
                type="number"
                name="commissionPercent"
                defaultValue={editingItem?.commissionPercent || 10}
                step="0.01"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#ed6f28]"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={() => {setShowModal(null); setEditingItem(null);}}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 bg-[#ed6f28] hover:bg-[#c45e1e] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingItem ? 'Guardar Cambios' : 'Crear Contrato'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


    // NUEVO: Vista de Incidencias Completa
  const IncidentsView = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('TODOS');
    const [filterPriority, setFilterPriority] = useState('TODOS');
    const [editingIncident, setEditingIncident] = useState(null);
    const [showIncidentModal, setShowIncidentModal] = useState(false);
    const [incidentForm, setIncidentForm] = useState({
      contractId: '',
      title: '',
      category: 'Plomería',
      description: '',
      priority: 'Media',
      status: 'PENDIENTE',
      createdAt: new Date().toISOString().split('T')[0],
      resolvedAt: null
    });

    const categories = ['Plomería', 'Electricidad', 'Carpintería', 'Pintura', 'Limpieza', 'Electrodomésticos', 'Otro'];
    const priorities = ['Baja', 'Media', 'Alta', 'Urgente'];
    const statuses = ['PENDIENTE', 'EN PROCESO', 'RESUELTA', 'CANCELADA'];

    const INC_PRIO = {
      Urgente: { color: 'var(--red)',    bg: 'var(--rDim)',  label: 'Urgente' },
      Alta:    { color: 'var(--red)',    bg: 'var(--rDim)',  label: 'Alta'    },
      Media:   { color: 'var(--yellow)', bg: 'var(--ylDim)', label: 'Media'   },
      Baja:    { color: 'var(--green)',  bg: 'var(--grDim)', label: 'Baja'    },
    };
    const INC_EST = {
      'PENDIENTE':   { color: 'var(--yellow)', bg: 'var(--ylDim)', label: 'Pendiente'  },
      'EN PROCESO':  { color: 'var(--orange)', bg: 'var(--orDim)', label: 'En proceso' },
      'RESUELTA':    { color: 'var(--green)',  bg: 'var(--grDim)', label: 'Resuelta'   },
      'CANCELADA':   { color: 'var(--t3)',     bg: 'var(--surf3)', label: 'Cancelada'  },
    };

    const filteredIncidents = incidents.filter(incident => {
      const contract = contracts.find(c => c.id === incident.contractId);
      const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           incident.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           contract?.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'TODOS' || incident.status === filterStatus;
      const matchesPriority = filterPriority === 'TODOS' || incident.priority === filterPriority;
      return matchesSearch && matchesStatus && matchesPriority;
    });

    const handleSubmit = async () => {
  if (!incidentForm.contractId || !incidentForm.title || !incidentForm.description) {
    alert('Complete los campos obligatorios');
    return;
  }

  try {
    if (editingIncident) {
      const updateData = {
        title: incidentForm.title,
        category: incidentForm.category,
        description: incidentForm.description,
        priority: incidentForm.priority,
        status: incidentForm.status
      };
      await incidentsService.update(editingIncident.id, updateData);
      await refreshIncidents();
      alert('Incidencia actualizada');
    } else {
      const createData = {
        contractId: parseInt(incidentForm.contractId),
        title: incidentForm.title,
        category: incidentForm.category,
        description: incidentForm.description,
        priority: incidentForm.priority,
        status: incidentForm.status || 'PENDIENTE'
      };
      await incidentsService.create(createData);
      await refreshIncidents();
      alert('Incidencia creada');
    }
    setShowIncidentModal(false);
    setEditingIncident(null);
    setIncidentForm({
      contractId: '', title: '', category: 'Plomería', description: '',
      priority: 'Media', status: 'PENDIENTE',
      createdAt: new Date().toISOString().split('T')[0], resolvedAt: null
    });
  } catch (error) {
    alert('Error: ' + error.message);
  }
};

    const stats = {
      total: incidents.length,
      pendientes: incidents.filter(i => i.status === 'PENDIENTE').length,
      enProceso: incidents.filter(i => i.status === 'EN PROCESO').length,
      resueltas: incidents.filter(i => i.status === 'RESUELTA').length,
      urgentes: incidents.filter(i => (i.priority === 'Urgente' || i.priority === 'Alta') && i.status !== 'RESUELTA').length
    };

    return (
      <div className="page">
        <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 16 }}>
          <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}><div className="ct-kpi-n">{stats.total}</div><div className="ct-kpi-l">Total</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}><div className="ct-kpi-n">{stats.pendientes}</div><div className="ct-kpi-l">Pendientes</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--orange)'}}><div className="ct-kpi-n">{stats.enProceso}</div><div className="ct-kpi-l">En proceso</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}><div className="ct-kpi-n">{stats.resueltas}</div><div className="ct-kpi-l">Resueltas</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--red)'}}><div className="ct-kpi-n">{stats.urgentes}</div><div className="ct-kpi-l">Alta prioridad</div></div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
          <div className="ct-filters" style={{ flex: 1 }}>
            {[
              { k: 'TODOS', l: 'Todas' },
              { k: 'PENDIENTE', l: 'Pendientes' },
              { k: 'EN PROCESO', l: 'En proceso' },
              { k: 'Alta', l: 'Alta prioridad', isPrio: true },
              { k: 'RESUELTA', l: 'Resueltas' },
            ].map(f => (
              <button
                key={f.k}
                className={`ct-pill ${(f.isPrio ? filterPriority === f.k : filterStatus === f.k) ? 'on' : ''}`}
                onClick={() => f.isPrio ? setFilterPriority(f.k === filterPriority ? 'TODOS' : f.k) : setFilterStatus(f.k)}
              >{f.l}</button>
            ))}
          </div>
          <button
            className="btn-o"
            style={{ padding: '8px 14px', fontSize: 12.5, borderRadius: 'var(--r2)', flexShrink: 0 }}
            onClick={() => {
              setEditingIncident(null);
              setIncidentForm({ contractId: '', title: '', category: 'Plomería', description: '', priority: 'Media', status: 'PENDIENTE', createdAt: new Date().toISOString().split('T')[0], resolvedAt: null });
              setShowIncidentModal(true);
            }}
          >
            <Plus style={{ width: 13, height: 13 }} /> Nueva Incidencia
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredIncidents.map(incident => {
            const contract = contracts.find(c => c.id === incident.contractId);
            const pr = INC_PRIO[incident.priority] || INC_PRIO.Media;
            const es = INC_EST[incident.status] || INC_EST.PENDIENTE;
            return (
              <div key={incident.id} className="card" style={{ overflow: 'hidden', display: 'flex' }}>
                <div style={{ width: 3, flexShrink: 0, background: pr.color }} />
                <div style={{ padding: '14px 16px', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 660, marginBottom: 2, color: 'var(--t1)' }}>{incident.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--t3)' }}>{contract?.propertyAddress || 'Sin contrato'} · {incident.category}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                      <span style={{ background: pr.bg, color: pr.color, borderRadius: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 650 }}>{pr.label}</span>
                      <span style={{ background: es.bg, color: es.color, borderRadius: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 650 }}>{es.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 4 }}>{new Date(incident.createdAt).toLocaleDateString('es-AR')}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--t3)', lineHeight: 1.55, marginBottom: 10 }}>{incident.description}</div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    {incident.status !== 'RESUELTA' && (
                      <button
                        onClick={async () => {
                          try {
                            await incidentsService.update(incident.id, { ...incident, status: 'RESUELTA' });
                            await refreshIncidents();
                          } catch (e) { alert('Error: ' + e.message); }
                        }}
                        style={{ background: 'var(--grDim)', color: 'var(--green)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 'var(--r2)', padding: '5px 11px', fontSize: 11.5, fontWeight: 650, cursor: 'pointer', fontFamily: 'inherit' }}
                      >Marcar resuelta</button>
                    )}
                    <button
                      onClick={() => { setEditingIncident(incident); setIncidentForm(incident); setShowIncidentModal(true); }}
                      style={{ background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 'var(--r2)', padding: '5px 11px', fontSize: 11.5, color: 'var(--t2)', cursor: 'pointer', fontFamily: 'inherit' }}
                    >Editar</button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Eliminar incidencia?')) {
                          try { await incidentsService.delete(incident.id); await refreshIncidents(); } catch (e) { alert('Error: ' + e.message); }
                        }
                      }}
                      style={{ background: 'var(--rDim)', color: 'var(--red)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--r2)', padding: '5px 11px', fontSize: 11.5, fontWeight: 650, cursor: 'pointer', fontFamily: 'inherit' }}
                    >Eliminar</button>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredIncidents.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--t3)', fontSize: 13 }}>
              No hay incidencias{filterStatus !== 'TODOS' ? ` con estado "${filterStatus}"` : ''}
            </div>
          )}
        </div>

        {showIncidentModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowIncidentModal(false)}>
            <div className="modal">
              <div className="modal-head">
                <span className="modal-title">{editingIncident ? 'Editar Incidencia' : 'Nueva Incidencia'}</span>
                <button className="modal-close" onClick={() => setShowIncidentModal(false)}><X size={14}/></button>
              </div>
              <div className="modal-body">
                <div className="form-field">
                  <label>Contrato *</label>
                  <select value={incidentForm.contractId} onChange={e => setIncidentForm({...incidentForm, contractId: e.target.value})}>
                    <option value="">Seleccionar contrato</option>
                    {contracts.map(c => <option key={c.id} value={c.id}>{c.propertyAddress} — {c.tenantName}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Título *</label>
                  <input type="text" value={incidentForm.title} onChange={e => setIncidentForm({...incidentForm, title: e.target.value})} placeholder="Ej: Pérdida de agua en baño"/>
                </div>
                <div className="form-row col2">
                  <div className="form-field">
                    <label>Categoría</label>
                    <select value={incidentForm.category} onChange={e => setIncidentForm({...incidentForm, category: e.target.value})}>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Prioridad</label>
                    <select value={incidentForm.priority} onChange={e => setIncidentForm({...incidentForm, priority: e.target.value})}>
                      {priorities.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>Estado</label>
                  <select value={incidentForm.status} onChange={e => setIncidentForm({...incidentForm, status: e.target.value})}>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Descripción *</label>
                  <textarea rows={3} value={incidentForm.description} onChange={e => setIncidentForm({...incidentForm, description: e.target.value})} placeholder="Describir el problema..."/>
                </div>
              </div>
              <div className="modal-foot">
                <button className="modal-btn cancel" onClick={() => setShowIncidentModal(false)}>Cancelar</button>
                <button className="modal-btn create" onClick={handleSubmit}><Save size={13}/> {editingIncident ? 'Guardar' : 'Crear'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // NUEVO: Vista de Reportes Completa
  const ReportsView = () => {
    const [activeTab, setActiveTab] = useState('contratos');
    const [dateRange, setDateRange] = useState({
      start: '2024-01-01',
      end: '2024-12-31'
    });
    const [selectedOwner, setSelectedOwner] = useState('TODOS');
    const [selectedContract, setSelectedContract] = useState('TODOS');

    // Colores para gráficos
    const COLORS = {
      primary: '#3b82f6',
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444',
      purple: '#8b5cf6',
      pink: '#ec4899'
    };

    const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
   
    // Función para generar PDF completo (sin autoTable)
    const generateCompletePDF = () => {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 20;

      // Helper para agregar nueva página si es necesario
      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      // ========== PORTADA ==========
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, pageWidth, 60, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.setFont(undefined, 'bold');
      doc.text('RENTIA', pageWidth / 2, 30, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text('Sistema de Gestión Inmobiliaria', pageWidth / 2, 45, { align: 'center' });

      yPosition = 80;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text('REPORTE COMPLETO', pageWidth / 2, yPosition, { align: 'center' });

      yPosition += 20;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80);
      doc.text(`Período: ${new Date(dateRange.start).toLocaleDateString('es-AR')} - ${new Date(dateRange.end).toLocaleDateString('es-AR')}`, pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 8;
      doc.text(`Propietario: ${selectedOwner}`, pageWidth / 2, yPosition, { align: 'center' });

      yPosition += 8;
      doc.text(`Generado: ${new Date().toLocaleDateString('es-AR')} ${new Date().toLocaleTimeString('es-AR')}`, pageWidth / 2, yPosition, { align: 'center' });

      // ========== RESUMEN EJECUTIVO ==========
      doc.addPage();
      yPosition = 20;

      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text('RESUMEN EJECUTIVO', 14, yPosition);
      
      yPosition += 3;
      doc.setDrawColor(59, 130, 246);
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, pageWidth - 14, yPosition);
      yPosition += 15;

      const stats = calculateFinancialStats();
      
      // Tarjetas de estadísticas
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0);
      
      const statsData = [
        { label: 'Total Cobrado:', value: `$${stats.totalCobrado.toLocaleString('es-AR')}`, color: [16, 185, 129] },
        { label: 'Honorarios Generados:', value: `$${stats.honorarios.toLocaleString('es-AR')}`, color: [139, 92, 246] },
        { label: 'Pendiente de Cobro:', value: `$${stats.totalPendiente.toLocaleString('es-AR')}`, color: [245, 158, 11] },
        { label: 'Contratos Activos:', value: contracts.filter(c => c.status === 'ACTIVO').length, color: [59, 130, 246] },
        { label: 'Pagos Realizados:', value: stats.pagosRealizados, color: [16, 185, 129] },
        { label: 'Pagos Pendientes:', value: stats.pagosPendientes, color: [245, 158, 11] },
        { label: 'Pagos Vencidos:', value: stats.pagosVencidos, color: [239, 68, 68] }
      ];

      statsData.forEach(stat => {
        doc.setTextColor(100);
        doc.text(stat.label, 20, yPosition);
        doc.setTextColor(stat.color[0], stat.color[1], stat.color[2]);
        doc.setFont(undefined, 'bold');
        doc.text(String(stat.value), 100, yPosition);
        doc.setFont(undefined, 'normal');
        yPosition += 8;
      });

      yPosition += 10;

      // ========== CONTRATOS ==========
      checkNewPage(40);
      
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text('REPORTE DE CONTRATOS', 14, yPosition);
      yPosition += 3;
      doc.line(14, yPosition, pageWidth - 14, yPosition);
      yPosition += 12;

      const filteredContracts = contracts.filter(c => 
        selectedOwner === 'TODOS' || c.ownerName === selectedOwner
      );

      doc.setFontSize(9);
      doc.setTextColor(0);
      filteredContracts.forEach((contract, index) => {
        checkNewPage(35);
        
        const lastIncrease = increaseHistory
          .filter(h => h.contractId === contract.id)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        // Fondo alternado
        if (index % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(14, yPosition - 4, pageWidth - 28, 30, 'F');
        }

        doc.setFont(undefined, 'bold');
        doc.setTextColor(59, 130, 246);
        doc.text(`${index + 1}. ${contract.propertyAddress}`, 16, yPosition);
        
        yPosition += 6;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0);
        doc.text(`Inquilino: ${contract.tenantName}`, 20, yPosition);
        
        yPosition += 5;
        doc.text(`Propietario: ${contract.ownerName}`, 20, yPosition);
        
        yPosition += 5;
        doc.text(`Monto: $${contract.baseRent.toLocaleString('es-AR')} | Vence: ${new Date(contract.endDate).toLocaleDateString('es-AR')}`, 20, yPosition);
        
        yPosition += 5;
        doc.setTextColor(100);
        doc.text(`Último aumento: ${lastIncrease ? `+${lastIncrease.percentage.toFixed(2)}%` : 'N/A'} | Estado: ${contract.status}`, 20, yPosition);
        
        yPosition += 10;
      });

      yPosition += 5;

      // ========== FINANCIERO ==========
      doc.addPage();
      yPosition = 20;

      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text('REPORTE FINANCIERO', 14, yPosition);
      yPosition += 3;
      doc.line(14, yPosition, pageWidth - 14, yPosition);
      yPosition += 12;

      const monthlyData = getMonthlyEvolution();
      
      doc.setFontSize(9);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Mes', 20, yPosition);
      doc.text('Ingresos', 80, yPosition);
      doc.text('Honorarios', 130, yPosition);
      yPosition += 2;
      doc.line(14, yPosition, pageWidth - 14, yPosition);
      yPosition += 6;

      doc.setFont(undefined, 'normal');
      monthlyData.forEach((month, index) => {
        checkNewPage(8);
        
        if (index % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(14, yPosition - 4, pageWidth - 28, 7, 'F');
        }

        doc.text(month.month, 20, yPosition);
        doc.setTextColor(16, 185, 129);
        doc.text(`$${month.ingresos.toLocaleString('es-AR')}`, 80, yPosition);
        doc.setTextColor(139, 92, 246);
        doc.text(`$${month.honorarios.toLocaleString('es-AR')}`, 130, yPosition);
        doc.setTextColor(0);
        yPosition += 7;
      });

      yPosition += 10;

      // ========== HISTORIAL DE AUMENTOS ==========
      if (increaseHistory.length > 0) {
        checkNewPage(40);

        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(59, 130, 246);
        doc.text('HISTORIAL DE AUMENTOS', 14, yPosition);
        yPosition += 3;
        doc.line(14, yPosition, pageWidth - 14, yPosition);
        yPosition += 10;

        doc.setFontSize(9);
        increaseHistory.forEach((increase, index) => {
          checkNewPage(20);
          
          const contract = contracts.find(c => c.id === increase.contractId);
          
          if (index % 2 === 0) {
            doc.setFillColor(245, 247, 250);
            doc.rect(14, yPosition - 4, pageWidth - 28, 18, 'F');
          }

          doc.setFont(undefined, 'bold');
          doc.setTextColor(0);
          doc.text(`${new Date(increase.date).toLocaleDateString('es-AR')} - ${contract?.propertyAddress || 'N/A'}`, 16, yPosition);
          
          yPosition += 6;
          doc.setFont(undefined, 'normal');
          doc.text(`Índice: ${increase.type} | Variación: `, 20, yPosition);
          doc.setTextColor(245, 158, 11);
          doc.setFont(undefined, 'bold');
          doc.text(`+${increase.percentage.toFixed(2)}%`, 70, yPosition);
          
          yPosition += 6;
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0);
          doc.text(`Anterior: $${increase.previousAmount.toLocaleString('es-AR')} → Nuevo: `, 20, yPosition);
          doc.setTextColor(16, 185, 129);
          doc.setFont(undefined, 'bold');
          doc.text(`$${increase.newAmount.toLocaleString('es-AR')}`, 100, yPosition);
          
          yPosition += 10;
        });
      }

      // ========== PAGOS E INQUILINOS ==========
      doc.addPage();
      yPosition = 20;

      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text('REPORTE DE PAGOS E INQUILINOS', 14, yPosition);
      yPosition += 3;
      doc.line(14, yPosition, pageWidth - 14, yPosition);
      yPosition += 12;

      doc.setFontSize(9);
      contracts.forEach((contract, index) => {
        checkNewPage(25);
        
        const contractPayments = payments.filter(p => p.contractId === contract.id);
        const paid = contractPayments.filter(p => p.status === 'PAGADO').length;
        const total = contractPayments.length;
        const punctuality = total > 0 ? ((paid / total) * 100).toFixed(1) : 0;

        if (index % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(14, yPosition - 4, pageWidth - 28, 22, 'F');
        }

        doc.setFont(undefined, 'bold');
        doc.setTextColor(0);
        doc.text(`${contract.tenantName}`, 16, yPosition);
        
        yPosition += 6;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100);
        doc.text(contract.propertyAddress, 20, yPosition);
        
        yPosition += 6;
        doc.setTextColor(0);
        doc.text(`Total: ${total} | Pagados: `, 20, yPosition);
        doc.setTextColor(16, 185, 129);
        doc.text(`${paid}`, 57, yPosition);
        doc.setTextColor(0);
        doc.text(` | Pendientes: `, 64, yPosition);
        doc.setTextColor(245, 158, 11);
        doc.text(`${contractPayments.filter(p => p.status === 'PENDIENTE').length}`, 95, yPosition);
        doc.setTextColor(0);
        doc.text(` | Vencidos: `, 102, yPosition);
        doc.setTextColor(239, 68, 68);
        doc.text(`${contractPayments.filter(p => p.status === 'VENCIDO').length}`, 127, yPosition);
        
        yPosition += 6;
        doc.setTextColor(0);
        doc.text(`Puntualidad: `, 20, yPosition);
        
        const color = punctuality >= 80 ? [16, 185, 129] : punctuality >= 50 ? [245, 158, 11] : [239, 68, 68];
        doc.setTextColor(color[0], color[1], color[2]);
        doc.setFont(undefined, 'bold');
        doc.text(`${punctuality}%`, 48, yPosition);
        doc.setFont(undefined, 'normal');
        
        yPosition += 12;
      });

      // Footer en todas las páginas
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Rentia - Generado el ${new Date().toLocaleDateString('es-AR')} - Página ${i} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Guardar PDF
      const fileName = `Rentia_Reporte_Completo_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    };
    
    // Obtener lista única de propietarios
    const owners = ['TODOS', ...new Set(contracts.map(c => c.ownerName))];

    // Calcular estadísticas financieras
    const calculateFinancialStats = () => {
      const filteredPayments = payments.filter(p => {
        const paymentDate = new Date(p.dueDate);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        return paymentDate >= startDate && paymentDate <= endDate;
      });

      const totalCobrado = filteredPayments
        .filter(p => p.status === 'PAGADO')
        .reduce((sum, p) => {
          const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

      const totalParcial = filteredPayments
        .filter(p => p.status === 'PAGO PARCIAL')
        .reduce((sum, p) => {
          const amount = parseFloat(p.paidAmount || 0);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

      const totalPendiente = filteredPayments
        .filter(p => p.status === 'PENDIENTE' || p.status === 'VENCIDO')
        .reduce((sum, p) => {
          const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

      const honorarios = filteredPayments
        .filter(p => p.status === 'PAGADO')
        .reduce((sum, p) => {
          const contract = contracts.find(c => c.id === p.contractId);
          const amount = parseFloat(p.adjustedAmount || p.baseAmount || 0);
          const validAmount = isNaN(amount) ? 0 : amount;
          const commissionPercent = parseFloat(contract?.commissionPercent || 0);
          return sum + (contract ? (validAmount * (isNaN(commissionPercent) ? 0 : commissionPercent) / 100) : 0);
        }, 0);

      return {
        totalCobrado: totalCobrado + totalParcial,
        totalPendiente,
        honorarios,
        pagosRealizados: filteredPayments.filter(p => p.status === 'PAGADO').length,
        pagosParciales: filteredPayments.filter(p => p.status === 'PAGO PARCIAL').length,
        pagosPendientes: filteredPayments.filter(p => p.status === 'PENDIENTE').length,
        pagosVencidos: filteredPayments.filter(p => p.status === 'VENCIDO').length
      };
    };

    // Generar datos para gráfico de evolución mensual
    const getMonthlyEvolution = () => {
      const monthlyData = {};
      
      payments.forEach(payment => {
        if (payment.status === 'PAGADO' || payment.status === 'PAGO PARCIAL') {
          const month = payment.period;
          const amount = payment.status === 'PAGADO'
            ? parseFloat(payment.adjustedAmount || payment.baseAmount || 0)
            : parseFloat(payment.paidAmount || 0);
          
          if (!monthlyData[month]) {
            monthlyData[month] = { month, ingresos: 0, honorarios: 0 };
          }
          
          const contract = contracts.find(c => c.id === payment.contractId);
          const commission = contract ? (amount * contract.commissionPercent / 100) : 0;
          
          monthlyData[month].ingresos += amount;
          monthlyData[month].honorarios += commission;
        }
      });

      return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
    };

    // Datos para gráfico de torta de estados de pago
    const getPaymentStatusData = () => {
      const stats = calculateFinancialStats();
      return [
        { name: 'Pagados', value: stats.pagosRealizados, color: COLORS.success },
        { name: 'Parciales', value: stats.pagosParciales, color: COLORS.warning },
        { name: 'Pendientes', value: stats.pagosPendientes, color: COLORS.primary },
        { name: 'Vencidos', value: stats.pagosVencidos, color: COLORS.danger }
      ].filter(item => item.value > 0);
    };

    // Exportar a CSV
    const exportToCSV = (data, filename) => {
      const csvContent = data.map(row => typeof row === 'string' ? row : Object.values(row).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.csv`;
      a.click();
    };

    const stats = calculateFinancialStats();

    // TAB 1: Reportes de Contratos
    const ContractsReportTab = () => {
      const filteredContracts = contracts.filter(c =>
        selectedOwner === 'TODOS' || c.ownerName === selectedOwner
      );

      const contractsData = filteredContracts.map(contract => {
        const lastIncrease = increaseHistory
          .filter(h => h.contractId === contract.id)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        const daysUntilEnd = Math.ceil((new Date(contract.endDate) - new Date()) / (1000 * 60 * 60 * 24));

        return {
          ...contract,
          lastIncrease,
          daysUntilEnd,
          status: daysUntilEnd < 0 ? 'VENCIDO' : daysUntilEnd < 60 ? 'POR VENCER' : contract.status
        };
      });

      const contractsByStatus = {
        ACTIVO: contractsData.filter(c => c.status === 'ACTIVO').length,
        'POR VENCER': contractsData.filter(c => c.status === 'POR VENCER').length,
        VENCIDO: contractsData.filter(c => c.status === 'VENCIDO').length,
        FINALIZADO: contractsData.filter(c => c.status === 'FINALIZADO').length
      };

      const barChartData = Object.entries(contractsByStatus).map(([name, value]) => ({ name, value }));

      const ST = {
        ACTIVO:       { bg: 'var(--grDim)', c: 'var(--green)'  },
        'POR VENCER': { bg: 'var(--ylDim)', c: 'var(--yellow)' },
        VENCIDO:      { bg: 'var(--rDim)',  c: 'var(--red)'    },
        FINALIZADO:   { bg: 'var(--surf3)', c: 'var(--t3)'     },
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}><div className="ct-kpi-n">{filteredContracts.length}</div><div className="ct-kpi-l">Total contratos</div></div>
            <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}><div className="ct-kpi-n">{contractsByStatus.ACTIVO}</div><div className="ct-kpi-l">Activos</div></div>
            <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}><div className="ct-kpi-n">{contractsByStatus['POR VENCER']}</div><div className="ct-kpi-l">Por vencer</div></div>
            <div className="ct-kpi" style={{'--ck-c':'var(--red)'}}><div className="ct-kpi-n">{contractsByStatus.VENCIDO}</div><div className="ct-kpi-l">Vencidos</div></div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--t1)', marginBottom: 14 }}>Distribución de Contratos por Estado</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" stroke="var(--t3)" tick={{ fill: 'var(--t3)', fontSize: 11 }} />
                <YAxis stroke="var(--t3)" tick={{ fill: 'var(--t3)', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--surf2)', border: '1px solid var(--border)', color: 'var(--t1)', borderRadius: 8 }} labelStyle={{ color: 'var(--t1)' }} />
                <Bar dataKey="value" fill="var(--orange)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, fontWeight: 650, color: 'var(--t1)' }}>Detalle de Contratos</span>
              <button className="hd-act ghost" style={{ fontSize: 11.5 }} onClick={() => {
                const csvData = contractsData.map(c => ({ Dirección: c.propertyAddress, Inquilino: c.tenantName, Propietario: c.ownerName, 'Monto Base': c.baseRent, 'Fecha Inicio': c.startDate, 'Fecha Fin': c.endDate, Estado: c.status, 'Último Aumento': c.lastIncrease ? `${c.lastIncrease.percentage.toFixed(2)}%` : 'N/A' }));
                const headers = Object.keys(csvData[0]).join(',');
                exportToCSV([headers, ...csvData], 'reporte-contratos');
              }}><Download size={12}/> Exportar CSV</button>
            </div>
            <table className="hd-table">
              <thead>
                <tr>
                  {['Propiedad','Inquilino','Propietario','Monto','Vencimiento','Último Aumento','Estado'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {contractsData.map(c => {
                  const st = ST[c.status] || ST.FINALIZADO;
                  return (
                    <tr key={c.id}>
                      <td style={{ color: 'var(--t1)', fontWeight: 600 }}>{c.propertyAddress}</td>
                      <td style={{ color: 'var(--t2)' }}>{c.tenantName}</td>
                      <td style={{ color: 'var(--t2)' }}>{c.ownerName}</td>
                      <td><span className="hd-monto">${c.baseRent.toLocaleString('es-AR')}</span></td>
                      <td style={{ color: 'var(--t3)' }}>{new Date(c.endDate).toLocaleDateString('es-AR')}</td>
                      <td>
                        {c.lastIncrease
                          ? <span style={{ color: 'var(--green)', fontWeight: 650 }}>+{c.lastIncrease.percentage.toFixed(2)}%</span>
                          : <span style={{ color: 'var(--t3)' }}>Sin aumentos</span>}
                      </td>
                      <td><span style={{ background: st.bg, color: st.c, borderRadius: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 650 }}>{c.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {contractsData.filter(c => c.status === 'POR VENCER').length > 0 && (
            <div style={{ background: 'var(--ylDim)', border: '1px solid rgba(180,83,9,0.2)', borderRadius: 'var(--r)', padding: '14px 16px', display: 'flex', gap: 12 }}>
              <AlertCircle size={16} style={{ color: 'var(--yellow)', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--yellow)', marginBottom: 6 }}>Contratos próximos a vencer</div>
                {contractsData.filter(c => c.status === 'POR VENCER').map(c => (
                  <div key={c.id} style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 2 }}>· {c.propertyAddress} — vence en {c.daysUntilEnd} días</div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };

    const FinancialReportTab = () => {
      const monthlyData = getMonthlyEvolution();

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}>
              <div className="ct-kpi-n">${(stats.totalCobrado / 1000000).toFixed(2)}M</div>
              <div className="ct-kpi-l">Total cobrado</div>
            </div>
            <div className="ct-kpi" style={{'--ck-c':'var(--orange)'}}>
              <div className="ct-kpi-n">${(stats.honorarios / 1000).toFixed(0)}K</div>
              <div className="ct-kpi-l">Honorarios</div>
            </div>
            <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}>
              <div className="ct-kpi-n">${(stats.totalPendiente / 1000).toFixed(0)}K</div>
              <div className="ct-kpi-l">Pendiente cobro</div>
            </div>
            <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}>
              <div className="ct-kpi-n">{contracts.filter(c => c.status === 'ACTIVO').length}</div>
              <div className="ct-kpi-l">Contratos activos</div>
            </div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 650, color: 'var(--t1)' }}>Evolución de Ingresos Mensuales</span>
              <button className="hd-act ghost" style={{ fontSize: 11.5 }} onClick={() => {
                const csvData = monthlyData.map(m => ({ Mes: m.month, Ingresos: m.ingresos, Honorarios: m.honorarios }));
                const headers = Object.keys(csvData[0]).join(',');
                exportToCSV([headers, ...csvData], 'evolucion-mensual');
              }}><Download size={12}/> Exportar</button>
            </div>
            {(() => {
              const mNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
              const aData = monthlyData.map(d => {
                const [yr, mo] = (d.month || '').split('-');
                return { m: mo ? mNames[parseInt(mo)-1] + ' ' + yr.slice(2) : d.month, v: d.ingresos };
              });
              return (
                <div>
                  <AreaChartSVG data={aData} />
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--t3)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 2, background: 'var(--orange)', display: 'inline-block', borderRadius: 1 }}/> Ingresos mensuales</span>
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 650, color: 'var(--t1)' }}>Historial de Aumentos Aplicados</div>
            <table className="hd-table">
              <thead>
                <tr>
                  {['Fecha','Contrato','Índice','Porcentaje','Monto Anterior','Monto Nuevo'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {increaseHistory.map(inc => {
                  const contract = contracts.find(c => c.id === inc.contractId);
                  return (
                    <tr key={inc.id}>
                      <td style={{ color: 'var(--t3)' }}>{new Date(inc.date).toLocaleDateString('es-AR')}</td>
                      <td style={{ color: 'var(--t1)', fontWeight: 600 }}>{contract?.propertyAddress || 'N/A'}</td>
                      <td><span style={{ background: 'var(--orDim)', color: 'var(--orange)', borderRadius: 4, padding: '2px 7px', fontSize: 10.5, fontWeight: 650 }}>{inc.type}</span></td>
                      <td style={{ color: 'var(--orange)', fontWeight: 650 }}>+{inc.percentage.toFixed(2)}%</td>
                      <td style={{ color: 'var(--t2)' }}>${inc.previousAmount.toLocaleString('es-AR')}</td>
                      <td style={{ color: 'var(--green)', fontWeight: 650 }}>${inc.newAmount.toLocaleString('es-AR')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    const PaymentsReportTab = () => {
      const paymentStatusData = getPaymentStatusData();

      const tenantPayments = contracts.map(contract => {
        const contractPayments = payments.filter(p => p.contractId === contract.id);
        const paid = contractPayments.filter(p => p.status === 'PAGADO').length;
        const total = contractPayments.length;
        const punctuality = total > 0 ? ((paid / total) * 100).toFixed(1) : 0;
        return {
          contract,
          totalPayments: total,
          paidPayments: paid,
          pendingPayments: contractPayments.filter(p => p.status === 'PENDIENTE').length,
          overduePayments: contractPayments.filter(p => p.status === 'VENCIDO').length,
          punctuality
        };
      });

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}><div className="ct-kpi-n">{payments.length}</div><div className="ct-kpi-l">Total pagos</div></div>
            <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}><div className="ct-kpi-n">{stats.pagosRealizados}</div><div className="ct-kpi-l">Pagados</div></div>
            <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}><div className="ct-kpi-n">{stats.pagosPendientes}</div><div className="ct-kpi-l">Pendientes</div></div>
            <div className="ct-kpi" style={{'--ck-c':'var(--red)'}}><div className="ct-kpi-n">{stats.pagosVencidos}</div><div className="ct-kpi-l">Vencidos</div></div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--t1)', marginBottom: 16 }}>Distribución de Estados de Pago</div>
            {(() => {
              const total = paymentStatusData.reduce((s, e) => s + e.value, 0);
              const slices = paymentStatusData.map(e => ({ pct: total > 0 ? (e.value / total) * 100 : 0, color: e.color, label: e.name, count: e.value }));
              const paidCount = payments.filter(p => p.status === 'PAGADO').length;
              return (
                <div className="donut-wrap">
                  <DonutChartSVG slices={slices} centerLabel={paidCount} centerSub="PAGADOS" />
                  <div className="donut-legend">
                    {slices.map((s, i) => (
                      <div key={i} className="dl-row">
                        <div className="dl-dot" style={{ background: s.color }} />
                        <span className="dl-lbl">{s.label}</span>
                        <span className="dl-val">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, fontWeight: 650, color: 'var(--t1)' }}>Comportamiento de Pago por Inquilino</span>
              <button className="hd-act ghost" style={{ fontSize: 11.5 }} onClick={() => {
                const csvData = tenantPayments.map(t => ({ Inquilino: t.contract.tenantName, Propiedad: t.contract.propertyAddress, 'Total Pagos': t.totalPayments, Pagados: t.paidPayments, Pendientes: t.pendingPayments, Vencidos: t.overduePayments, 'Puntualidad %': t.punctuality }));
                const headers = Object.keys(csvData[0]).join(',');
                exportToCSV([headers, ...csvData], 'reporte-inquilinos');
              }}><Download size={12}/> Exportar CSV</button>
            </div>
            <table className="hd-table">
              <thead>
                <tr>
                  {['Inquilino','Propiedad','Total','Pagados','Pendientes','Vencidos','Puntualidad'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {tenantPayments.map((t, i) => {
                  const pct = parseFloat(t.punctuality);
                  const barColor = pct >= 80 ? 'var(--green)' : pct >= 50 ? 'var(--yellow)' : 'var(--red)';
                  return (
                    <tr key={i}>
                      <td style={{ color: 'var(--t1)', fontWeight: 600 }}>{t.contract.tenantName}</td>
                      <td style={{ color: 'var(--t2)' }}>{t.contract.propertyAddress}</td>
                      <td style={{ color: 'var(--t1)' }}>{t.totalPayments}</td>
                      <td style={{ color: 'var(--green)', fontWeight: 650 }}>{t.paidPayments}</td>
                      <td style={{ color: 'var(--yellow)' }}>{t.pendingPayments}</td>
                      <td style={{ color: 'var(--red)' }}>{t.overduePayments}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 5, background: 'var(--surf3)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 650, color: barColor, minWidth: 38, textAlign: 'right' }}>{t.punctuality}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className="page">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 760, color: 'var(--t1)', letterSpacing: '-0.4px', marginBottom: 4 }}>Reportes y Análisis</div>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>Control financiero y operativo completo</div>
          </div>
          <button className="btn-o" style={{ padding: '9px 16px', fontSize: 12.5, gap: 6 }} onClick={generateCompletePDF}>
            <Download size={13}/> Generar PDF Completo
          </button>
        </div>

        <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
          <div className="form-row col2" style={{ alignItems: 'flex-end', gap: 10 }}>
            <div className="form-field">
              <label>Fecha inicio</label>
              <input type="date" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} />
            </div>
            <div className="form-field">
              <label>Fecha fin</label>
              <input type="date" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} />
            </div>
            <div className="form-field" style={{ flex: 2 }}>
              <label>Propietario</label>
              <select value={selectedOwner} onChange={e => setSelectedOwner(e.target.value)}>
                {owners.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="ct-filters" style={{ marginBottom: 20 }}>
          {[
            { k: 'contratos', l: 'Contratos' },
            { k: 'financiero', l: 'Financiero' },
            { k: 'pagos', l: 'Pagos e Inquilinos' },
          ].map(t => (
            <button key={t.k} className={`ct-pill ${activeTab === t.k ? 'on' : ''}`} onClick={() => setActiveTab(t.k)}>{t.l}</button>
          ))}
        </div>

        <div>
          {activeTab === 'contratos' && <ContractsReportTab />}
          {activeTab === 'financiero' && <FinancialReportTab />}
          {activeTab === 'pagos' && <PaymentsReportTab />}
        </div>
      </div>
    );
  };


  // NUEVO: Vista de Índices Completa
  const IndicesView = ({ indices, setIndices }) => {
    const [activeTab, setActiveTab] = useState('consulta');
    const [showAddCustomIndex, setShowAddCustomIndex] = useState(false);
    const [customIndexForm, setCustomIndexForm] = useState({
      date: new Date().toISOString().slice(0, 7),
      type: 'CUSTOM',
      name: '',
      value: '',
      description: ''
    });

    const allIndices = [
      ...indices.map(i => ({ ...i, source: 'BCRA' })),
      ...customIndices
    ].sort((a, b) => b.date.localeCompare(a.date));

    const getLatestIndex = (type) => {
      const filtered = type === 'CUSTOM'
        ? customIndices
        : indices.filter(i => i[type] !== undefined);
      if (filtered.length === 0) return null;
      const latest = filtered[filtered.length - 1];
      const previous = filtered[filtered.length - 2];
      const currentValue = type === 'CUSTOM' ? latest.value : latest[type];
      const previousValue = previous ? (type === 'CUSTOM' ? previous.value : previous[type]) : currentValue;
      const variation = previousValue ? ((currentValue - previousValue) / previousValue * 100) : 0;
      return { value: currentValue, date: latest.date, variation: variation.toFixed(2), previous: previousValue };
    };

    const latestICL = getLatestIndex('ICL');
    const latestIPC = getLatestIndex('IPC');

    const getChartData = (indexType) => {
      if (indexType === 'CUSTOM') {
        return customIndices.map(i => ({ date: i.date, value: i.value, name: i.name }));
      }
      return indices
        .map((item, idx, arr) => {
          if (idx === 0) return null;
          const prev = arr[idx - 1];
          if (indexType === 'IPC') {
            if (item.IPC_var != null) return { date: item.date, value: item.IPC_var };
            if (prev.IPC && item.IPC) return { date: item.date, value: parseFloat(((item.IPC / prev.IPC - 1) * 100).toFixed(2)) };
          } else {
            if (prev.ICL && item.ICL) return { date: item.date, value: parseFloat(((item.ICL / prev.ICL - 1) * 100).toFixed(2)) };
          }
          return null;
        })
        .filter(Boolean);
    };

    const handleAddCustomIndex = () => {
      if (!customIndexForm.name || !customIndexForm.value || !customIndexForm.date) {
        alert('Complete todos los campos obligatorios');
        return;
      }
      const newIndex = {
        id: Date.now(),
        date: customIndexForm.date,
        type: 'CUSTOM',
        name: customIndexForm.name,
        value: parseFloat(customIndexForm.value),
        description: customIndexForm.description,
        source: 'MANUAL',
        createdAt: new Date().toISOString()
      };
      setCustomIndices(prev => [...prev, newIndex]);
      setShowAddCustomIndex(false);
      setCustomIndexForm({ date: new Date().toISOString().slice(0, 7), type: 'CUSTOM', name: '', value: '', description: '' });
      alert('Índice personalizado agregado exitosamente');
    };

    const updateFromBCRA = async () => {
      alert('Consultando Argly API...\n\nPor favor espera mientras obtenemos los datos actualizados.');
      try {
        const iclResponse = await fetch('https://api.argly.com.ar/api/icl');
        if (!iclResponse.ok) throw new Error('Error al obtener ICL de Argly');
        const iclJson = await iclResponse.json();
        const ipcResponse = await fetch('https://api.argly.com.ar/api/ipc/history');
        if (!ipcResponse.ok) throw new Error('Error al obtener IPC de Argly');
        const ipcJson = await ipcResponse.json();
        const [dd, mm, yyyy] = iclJson.data.fecha.split('/');
        const iclDate = `${yyyy}-${mm}`;
        const iclValue = parseFloat(iclJson.data.valor);
        const IPC_ANCHOR = 1879.53;
        const ipcSorted = [...ipcJson.data].sort((a, b) => a.anio !== b.anio ? a.anio - b.anio : a.mes - b.mes);
        let accumulated = IPC_ANCHOR;
        const ipcAccum = ipcSorted.map((item, idx) => {
          if (idx > 0) accumulated = parseFloat((accumulated * (1 + item.valor / 100)).toFixed(2));
          return { date: `${item.anio}-${String(item.mes).padStart(2, '0')}`, IPC: accumulated, IPC_var: item.valor };
        });
        const indicesMap = {};
        ipcAccum.forEach(item => { indicesMap[item.date] = { date: item.date, IPC: item.IPC, IPC_var: item.IPC_var }; });
        if (!indicesMap[iclDate]) indicesMap[iclDate] = { date: iclDate };
        indicesMap[iclDate].ICL = iclValue;
        const updatedIndices = Object.values(indicesMap).filter(item => item.ICL || item.IPC).sort((a, b) => a.date.localeCompare(b.date));
        setIndices(prevIndices => {
          const combined = [...prevIndices];
          updatedIndices.forEach(newIdx => {
            const existing = combined.findIndex(i => i.date === newIdx.date);
            if (existing >= 0) combined[existing] = { ...combined[existing], ...newIdx };
            else combined.push(newIdx);
          });
          return combined.sort((a, b) => a.date.localeCompare(b.date));
        });
        setIndicesSource('argly');
        setIndicesLastUpdate(new Date().toISOString());
        alert(`Actualizacion exitosa!\n\nICL actual: ${iclValue} (${iclJson.data.fecha})\n${ipcAccum.length} meses de IPC cargados\n\nFuente: api.argly.com.ar`);
      } catch (error) {
        console.error('Error al actualizar desde Argly:', error);
        alert('Error al actualizar indices\n\n' + error.message + '\n\nVerifica tu conexion a internet e intenta nuevamente.');
      }
    };

    const exportHistory = () => {
      const csvData = allIndices.map(index => {
        if (index.source === 'BCRA') return `${index.date},ICL,${index.ICL || 'N/A'},BCRA\n${index.date},IPC,${index.IPC || 'N/A'},BCRA`;
        else return `${index.date},${index.name},${index.value},MANUAL`;
      }).join('\n');
      const blob = new Blob([`Fecha,Indice,Valor,Fuente\n${csvData}`], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `historico-indices-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    };

    const TABS_IND = [
      { k: 'consulta',      l: 'Consulta Oficial' },
      { k: 'historico',     l: 'Historico' },
      { k: 'personalizados',l: 'Personalizados', badge: customIndices.length > 0 ? customIndices.length : null },
      { k: 'calculadora',   l: 'Calculadora' },
    ];

    // ── TAB: Consulta ──────────────────────────────────────────
    const ConsultaTab = () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Indices Oficiales</p>
            <p style={{ color: 'var(--t3)', fontSize: 13 }}>Valores actualizados desde Argly (BCRA)</p>
          </div>
          <button className="btn-o" onClick={updateFromBCRA} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <TrendingUp style={{ width: 16, height: 16 }} />
            Actualizar desde BCRA
          </button>
        </div>

        <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {/* ICL */}
          <div className="ct-kpi">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <p style={{ color: 'var(--t3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>ICL</p>
                <p style={{ color: 'var(--t2)', fontSize: 13 }}>Indice Contratos de Locacion</p>
              </div>
              <div style={{ background: 'var(--orDim)', borderRadius: 8, padding: 8 }}>
                <TrendingUp style={{ width: 18, height: 18, color: 'var(--orange)' }} />
              </div>
            </div>
            {latestICL ? (
              <>
                <p style={{ color: 'var(--t1)', fontSize: 36, fontWeight: 800, lineHeight: 1, marginBottom: 4 }}>{latestICL.value}</p>
                <p style={{ color: 'var(--t3)', fontSize: 12, marginBottom: 12 }}>
                  Publicado: {new Date(latestICL.date + '-01').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surf2)', borderRadius: 8, padding: '8px 12px' }}>
                  <span style={{ color: parseFloat(latestICL.variation) >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700, fontSize: 15 }}>
                    {parseFloat(latestICL.variation) >= 0 ? '+' : ''}{latestICL.variation}%
                  </span>
                  <span style={{ color: 'var(--t3)', fontSize: 12 }}>var. mensual · anterior: {latestICL.previous}</span>
                </div>
              </>
            ) : (
              <p style={{ color: 'var(--t3)', fontSize: 13 }}>Sin datos disponibles</p>
            )}
          </div>

          {/* IPC */}
          <div className="ct-kpi">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <p style={{ color: 'var(--t3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>IPC</p>
                <p style={{ color: 'var(--t2)', fontSize: 13 }}>Indice Precios al Consumidor</p>
              </div>
              <div style={{ background: 'var(--orDim)', borderRadius: 8, padding: 8 }}>
                <BarChart3 style={{ width: 18, height: 18, color: 'var(--orange)' }} />
              </div>
            </div>
            {latestIPC ? (
              <>
                <p style={{ color: 'var(--t1)', fontSize: 36, fontWeight: 800, lineHeight: 1, marginBottom: 4 }}>{latestIPC.value}</p>
                <p style={{ color: 'var(--t3)', fontSize: 12, marginBottom: 12 }}>
                  Publicado: {new Date(latestIPC.date + '-01').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surf2)', borderRadius: 8, padding: '8px 12px' }}>
                  <span style={{ color: parseFloat(latestIPC.variation) >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700, fontSize: 15 }}>
                    {parseFloat(latestIPC.variation) >= 0 ? '+' : ''}{latestIPC.variation}%
                  </span>
                  <span style={{ color: 'var(--t3)', fontSize: 12 }}>var. mensual · anterior: {latestIPC.previous}</span>
                </div>
              </>
            ) : (
              <p style={{ color: 'var(--t3)', fontSize: 13 }}>Sin datos disponibles</p>
            )}
          </div>
        </div>

        {/* Info boxes */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle style={{ width: 16, height: 16, color: 'var(--t3)' }} />
            Informacion sobre los Indices
          </p>
          {[
            { dot: 'var(--orange)', title: 'ICL - Indice de Contratos de Locacion', desc: 'Promedio de variaciones del IPC y la Remuneracion Imponible Promedio de los Trabajadores Estables (RIPTE). Es el indice oficial para actualizar contratos de alquiler segun la Ley 27.551.' },
            { dot: 'var(--violet)', title: 'IPC - Indice de Precios al Consumidor', desc: 'Mide la variacion de precios de una canasta de bienes y servicios representativos del consumo de los hogares. Publicado mensualmente por el INDEC.' },
            { dot: 'var(--green)', title: 'Frecuencia de actualizacion', desc: 'Los indices se publican mensualmente, generalmente entre el dia 10 y 15 del mes siguiente al periodo medido.' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.dot, marginTop: 5, flexShrink: 0 }} />
              <div>
                <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{item.title}</p>
                <p style={{ color: 'var(--t3)', fontSize: 13, lineHeight: 1.5 }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Ultimos 6 meses */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
            <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 14 }}>Ultimos 6 Meses</p>
          </div>
          <table className="hd-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Mes</th>
                <th>ICL</th>
                <th>Var. ICL</th>
                <th>Var. IPC %</th>
              </tr>
            </thead>
            <tbody>
              {indices.slice(-6).reverse().map((index, i, arr) => {
                const prevIndex = arr[i + 1];
                const iclVar = prevIndex ? ((index.ICL - prevIndex.ICL) / prevIndex.ICL * 100).toFixed(2) : '0.00';
                return (
                  <tr key={index.date}>
                    <td style={{ color: 'var(--t1)', fontWeight: 500 }}>
                      {new Date(index.date + '-01').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                    </td>
                    <td style={{ color: 'var(--t2)', fontWeight: 600 }}>{index.ICL}</td>
                    <td>
                      <span style={{ color: parseFloat(iclVar) >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                        {parseFloat(iclVar) >= 0 ? '+' : ''}{iclVar}%
                      </span>
                    </td>
                    <td>
                      <span style={{ color: 'var(--orange)', fontWeight: 600 }}>
                        {index.IPC_var != null ? `+${index.IPC_var}%` : '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );

    // ── TAB: Historico ─────────────────────────────────────────
    const HistoricoTab = () => {
      const [selectedIndex, setSelectedIndex] = useState('ICL');
      const rawData = getChartData(selectedIndex);
      const mNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      const aData = rawData.map(d => {
        const [yr, mo] = (d.date || '').split('-');
        return { m: mo ? mNames[parseInt(mo) - 1] + ' ' + yr.slice(2) : d.date, v: d.value };
      });
      const lineColor = selectedIndex === 'ICL' ? 'var(--blue)' : selectedIndex === 'IPC' ? 'var(--violet)' : 'var(--green)';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div className="form-field" style={{ minWidth: 160 }}>
              <label>Indice</label>
              <select value={selectedIndex} onChange={e => setSelectedIndex(e.target.value)}>
                <option value="ICL">ICL</option>
                <option value="IPC">IPC</option>
                {customIndices.length > 0 && <option value="CUSTOM">Indices Personalizados</option>}
              </select>
            </div>
            <button className="btn-o" onClick={exportHistory} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Download style={{ width: 15, height: 15 }} />
              Exportar Historico
            </button>
          </div>

          <div className="card">
            <p style={{ color: 'var(--t1)', fontWeight: 600, marginBottom: 16 }}>
              Evolucion de {selectedIndex === 'ICL' ? 'ICL' : selectedIndex === 'IPC' ? 'IPC' : 'Indices Personalizados'} (variacion % mensual)
            </p>
            <AreaChartSVG data={aData} color={lineColor} format={v => `${v.toFixed(2)}%`} />
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 14 }}>Historico Completo</p>
            </div>
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              <table className="hd-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Indice</th>
                    <th>Valor</th>
                    <th>Fuente</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedIndex === 'CUSTOM' ? (
                    customIndices.map(index => (
                      <tr key={index.id}>
                        <td style={{ color: 'var(--t1)' }}>{new Date(index.date + '-01').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</td>
                        <td style={{ color: 'var(--green)', fontWeight: 600 }}>{index.name}</td>
                        <td style={{ color: 'var(--t1)', fontWeight: 600 }}>{index.value}</td>
                        <td><span style={{ padding: '2px 8px', borderRadius: 20, background: 'var(--ylDim)', color: 'var(--yellow)', fontSize: 11, fontWeight: 700 }}>MANUAL</span></td>
                      </tr>
                    ))
                  ) : (
                    indices.map(index => (
                      <tr key={index.date}>
                        <td style={{ color: 'var(--t1)' }}>{new Date(index.date + '-01').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</td>
                        <td style={{ color: selectedIndex === 'ICL' ? 'var(--blue)' : 'var(--violet)', fontWeight: 600 }}>{selectedIndex}</td>
                        <td style={{ color: 'var(--t1)', fontWeight: 600 }}>{index[selectedIndex]}</td>
                        <td><span style={{ padding: '2px 8px', borderRadius: 20, background: 'var(--orDim)', color: 'var(--orange)', fontSize: 11, fontWeight: 700 }}>BCRA</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    };

    // ── TAB: Personalizados ────────────────────────────────────
    const PersonalizadosTab = () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Indices Personalizados</p>
            <p style={{ color: 'var(--t3)', fontSize: 13 }}>Gestioná tus propios indices de ajuste</p>
          </div>
          <button className="hd-act solid" onClick={() => setShowAddCustomIndex(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus style={{ width: 15, height: 15 }} /> Agregar Indice
          </button>
        </div>

        {customIndices.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <Calculator style={{ width: 48, height: 48, color: 'var(--t3)', margin: '0 auto 16px' }} />
            <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Sin indices personalizados</p>
            <p style={{ color: 'var(--t3)', fontSize: 13, marginBottom: 16 }}>Agrega indices personalizados para usar en tus contratos</p>
            <button className="hd-act solid" onClick={() => setShowAddCustomIndex(true)}>Crear Primer Indice</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {customIndices.map(index => (
              <div key={index.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <Calculator style={{ width: 18, height: 18, color: 'var(--green)' }} />
                    <span style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 15 }}>{index.name}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 20, background: 'var(--ylDim)', color: 'var(--yellow)', fontSize: 11, fontWeight: 700 }}>MANUAL</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    <div>
                      <p style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 2 }}>Valor</p>
                      <p style={{ color: 'var(--t1)', fontWeight: 800, fontSize: 20 }}>{index.value}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 2 }}>Fecha</p>
                      <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 13 }}>
                        {new Date(index.date + '-01').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 2 }}>Creado</p>
                      <p style={{ color: 'var(--t3)', fontSize: 12 }}>{new Date(index.createdAt).toLocaleDateString('es-AR')}</p>
                    </div>
                  </div>
                  {index.description && (
                    <div style={{ marginTop: 10, background: 'var(--surf2)', borderRadius: 8, padding: '8px 12px' }}>
                      <p style={{ color: 'var(--t3)', fontSize: 12 }}>{index.description}</p>
                    </div>
                  )}
                </div>
                <button
                  className="hd-act gr"
                  onClick={() => { if (window.confirm('Eliminar este indice personalizado?')) { setCustomIndices(prev => prev.filter(i => i.id !== index.id)); } }}
                  title="Eliminar"
                  style={{ marginLeft: 12 }}
                >
                  <Trash2 style={{ width: 15, height: 15 }} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="card" style={{ background: 'var(--surf2)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <AlertCircle style={{ width: 18, height: 18, color: 'var(--t3)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ color: 'var(--t2)', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Para que usar indices personalizados?</p>
              <ul style={{ color: 'var(--t3)', fontSize: 13, lineHeight: 1.7, paddingLeft: 16 }}>
                <li>Cuando necesites un indice diferente al ICL o IPC oficial</li>
                <li>Para contratos con acuerdos especiales de ajuste</li>
                <li>Cuando el indice oficial no este actualizado</li>
                <li>Para pruebas o simulaciones de aumentos</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );

    // ── TAB: Calculadora ───────────────────────────────────────
    const CalculadoraTab = () => {
      const [monto, setMonto] = useState('');
      const [fechaInicio, setFechaInicio] = useState('');
      const [fechaActualizacion, setFechaActualizacion] = useState('');
      const [resultado, setResultado] = useState(null);
      const [cargando, setCargando] = useState(false);

      const calcular = async () => {
        if (!monto || !fechaInicio || !fechaActualizacion) { alert('Completa todos los campos'); return; }
        const montoNum = parseFloat(monto.replace(/\./g, '').replace(',', '.'));
        if (isNaN(montoNum) || montoNum <= 0) { alert('Monto invalido'); return; }
        setCargando(true);
        try {
          const [resInicio, resActualizacion] = await Promise.all([
            indicesService.getICLForDate(fechaInicio),
            indicesService.getICLForDate(fechaActualizacion),
          ]);
          if (!resInicio?.success || !resActualizacion?.success) { alert('No se pudo obtener el ICL para las fechas seleccionadas.'); return; }
          if (resInicio.fecha === resActualizacion.fecha) { alert(`Ambas fechas corresponden al mismo dia de ICL (${resInicio.fecha}). Ingresa fechas distintas.`); return; }
          const coeficiente = resActualizacion.valor / resInicio.valor;
          const nuevoMonto = montoNum * coeficiente;
          const aumento = (coeficiente - 1) * 100;
          setResultado({ montoInicial: montoNum, nuevoMonto, aumento, coeficiente, iclInicio: resInicio.valor, iclActualizacion: resActualizacion.valor, fechaInicioReal: resInicio.fecha, fechaActualizacionReal: resActualizacion.fecha, aproximadoInicio: !resInicio.exact, aproximadoActualizacion: !resActualizacion.exact });
        } catch (err) {
          alert('Error al consultar los indices: ' + err.message);
        } finally {
          setCargando(false);
        }
      };

      const formatPesos = n => n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card">
            <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Calculadora ICL</p>
            <p style={{ color: 'var(--t3)', fontSize: 13, marginBottom: 2 }}>Nuevo Monto = Monto Actual × (ICL fecha actualización ÷ ICL fecha inicio)</p>
            <p style={{ color: 'var(--t3)', fontSize: 12, marginBottom: 20, opacity: 0.7 }}>El ICL disponible es mensual — se usa el valor del mes correspondiente a cada fecha ingresada.</p>

            <div className="form-row col2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="form-field">
                <label>Monto actual ($)</label>
                <input type="text" value={monto} onChange={e => setMonto(e.target.value)} placeholder="Ej: 150000" />
              </div>
              <div className="form-field">
                <label>Fecha inicio / ultima actualizacion</label>
                <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Fecha de actualizacion</label>
                <input type="date" value={fechaActualizacion} onChange={e => setFechaActualizacion(e.target.value)} />
              </div>
            </div>

            <button className="btn-o" onClick={calcular} disabled={cargando} style={{ marginTop: 8 }}>
              {cargando ? 'Consultando Argly...' : 'Calcular'}
            </button>
          </div>

          {resultado && (
            <div className="card" style={{ borderColor: 'var(--orange)' }}>
              <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Resultado</p>

              <div style={{ background: 'var(--surf2)', borderRadius: 12, padding: '20px 24px', textAlign: 'center', marginBottom: 16 }}>
                <p style={{ color: 'var(--t3)', fontSize: 13, marginBottom: 6 }}>Nuevo monto de alquiler</p>
                <p style={{ color: 'var(--t1)', fontSize: 44, fontWeight: 800, lineHeight: 1 }}>${formatPesos(resultado.nuevoMonto)}</p>
                <p style={{ color: 'var(--orange)', fontSize: 18, fontWeight: 700, marginTop: 6 }}>+{resultado.aumento.toFixed(2)}% de aumento</p>
              </div>

              <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                {[
                  { l: 'Monto inicial', v: '$' + formatPesos(resultado.montoInicial) },
                  { l: 'Coeficiente', v: resultado.coeficiente.toFixed(6) },
                  { l: `ICL inicio (${resultado.fechaInicioReal})`, v: resultado.iclInicio, warn: resultado.aproximadoInicio },
                  { l: `ICL actual (${resultado.fechaActualizacionReal})`, v: resultado.iclActualizacion, warn: resultado.aproximadoActualizacion },
                ].map((item, i) => (
                  <div key={i} className="ct-kpi" style={{ padding: '12px 16px' }}>
                    <p style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 4 }}>{item.l}</p>
                    <p style={{ color: 'var(--t1)', fontWeight: 700 }}>{item.v}</p>
                    {item.warn && <p style={{ color: 'var(--yellow)', fontSize: 11, marginTop: 3 }}>Fecha ajustada</p>}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14, background: 'var(--surf2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--t3)' }}>
                <span style={{ color: 'var(--t2)' }}>Formula: </span>
                ${formatPesos(resultado.montoInicial)} × ({resultado.iclActualizacion} ÷ {resultado.iclInicio}) = <span style={{ color: 'var(--t1)', fontWeight: 700 }}>${formatPesos(resultado.nuevoMonto)}</span>
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="page">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ color: 'var(--t1)', fontWeight: 800, fontSize: 26, marginBottom: 4 }}>Gestion de Indices</h1>
          <p style={{ color: 'var(--t3)', fontSize: 14 }}>Control y consulta de indices oficiales y personalizados</p>
        </div>

        <div className="ct-filters" style={{ marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {TABS_IND.map(t => (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              className="ct-pill"
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === t.k ? '2px solid var(--orange)' : '2px solid transparent',
                borderRadius: 0,
                color: activeTab === t.k ? 'var(--orange)' : 'var(--t3)',
                fontWeight: activeTab === t.k ? 700 : 500,
                paddingBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {t.l}
              {t.badge && (
                <span style={{ background: 'var(--green)', color: '#fff', borderRadius: 20, fontSize: 11, padding: '1px 7px', fontWeight: 700 }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ minHeight: 500 }}>
          {activeTab === 'consulta' && <ConsultaTab />}
          {activeTab === 'historico' && <HistoricoTab />}
          {activeTab === 'personalizados' && <PersonalizadosTab />}
          {activeTab === 'calculadora' && <CalculadoraTab />}
        </div>

        {showAddCustomIndex && ReactDOM.createPortal(
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddCustomIndex(false)}>
            <div className="modal">
              <div className="modal-head">
                <span>Agregar Indice Personalizado</span>
                <button onClick={() => setShowAddCustomIndex(false)} className="t-btn"><X style={{ width: 18, height: 18 }} /></button>
              </div>
              <div className="modal-body">
                <div className="form-field">
                  <label>Nombre del Indice *</label>
                  <input type="text" value={customIndexForm.name} onChange={e => setCustomIndexForm({ ...customIndexForm, name: e.target.value })} placeholder="Ej: Dolar Blue, Inflacion USA..." />
                </div>
                <div className="form-row col2">
                  <div className="form-field">
                    <label>Fecha (Mes/Ano) *</label>
                    <input type="month" value={customIndexForm.date} onChange={e => setCustomIndexForm({ ...customIndexForm, date: e.target.value })} />
                  </div>
                  <div className="form-field">
                    <label>Valor *</label>
                    <input type="number" step="0.0001" value={customIndexForm.value} onChange={e => setCustomIndexForm({ ...customIndexForm, value: e.target.value })} placeholder="123.4567" />
                  </div>
                </div>
                <div className="form-field">
                  <label>Descripcion (opcional)</label>
                  <textarea value={customIndexForm.description} onChange={e => setCustomIndexForm({ ...customIndexForm, description: e.target.value })} placeholder="Detalles adicionales..." rows={3} style={{ resize: 'none' }} />
                </div>
              </div>
              <div className="modal-foot">
                <button className="modal-btn" onClick={() => setShowAddCustomIndex(false)}>Cancelar</button>
                <button className="modal-btn solid" onClick={handleAddCustomIndex} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Save style={{ width: 15, height: 15 }} /> Guardar Indice
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };


// NUEVO: Vista de Gestión de Usuarios (Solo Admin)
  const UsersManagementView = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [userForm, setUserForm] = useState({
  nombre: '',
  email: '',
  password: '',
  rol: 'user',
  fecha_vencimiento: ''  // 👈 NUEVO
});
    const [error, setError] = useState('');

    // Cargar usuarios
    const loadUsers = async () => {
  try {
    setLoading(true);
    const data = await userService.getAllUsers();
    setUsers(data.users);
  } catch (err) {
    console.error('Error completo:', err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

    useEffect(() => {
      loadUsers();
    }, []);

    // Crear usuario
    const handleCreateUser = async () => {
      if (!userForm.nombre || !userForm.email || !userForm.password) {
        alert('Completa todos los campos');
        return;
      }

      try {
        await userService.createUser(userForm);
        alert('Usuario creado exitosamente');
        setShowCreateUser(false);
        setUserForm({ nombre: '', email: '', password: '', rol: 'user', fecha_vencimiento: '' });
        loadUsers();
      } catch (err) {
        alert('Error: ' + err.message);
      }
    };

    // Resetear contraseña
    const handleResetPassword = async (userId) => {
      const newPassword = prompt('Ingresa la nueva contraseña (mínimo 6 caracteres):');
      if (!newPassword || newPassword.length < 6) {
        alert('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      try {
        await userService.resetPassword(userId, newPassword);
        alert('Contraseña actualizada exitosamente');
      } catch (err) {
        alert('Error: ' + err.message);
      }
    };

    // Activar/desactivar usuario
    const handleToggleActive = async (userId, currentStatus) => {
      try {
        await userService.toggleUserActive(userId, !currentStatus);
        alert(`Usuario ${!currentStatus ? 'activado' : 'desactivado'} exitosamente`);
        loadUsers();
      } catch (err) {
        alert('Error: ' + err.message);
      }
    };

// Eliminar usuario
    const handleDeleteUser = async (userId, userName) => {
      if (!window.confirm(`¿Eliminar permanentemente a ${userName}?`)) return;

      try {
        await userService.deleteUser(userId);
        alert('Usuario eliminado exitosamente');
        loadUsers();
      } catch (err) {
        alert('Error: ' + err.message);
      }
    };

    // Borrar todos los contratos de un usuario
    const handleDeleteAllContracts = async (userId, userName) => {
      if (!window.confirm(`¿Eliminar TODOS los contratos de ${userName}?\n\nEsta acción no se puede deshacer.`)) return;
      try {
        const data = await userService.deleteAllContracts(userId);
        if (data.success) alert(`${data.message}`);
        else alert('Error: ' + data.error);
      } catch (err) {
        alert('Error al eliminar contratos: ' + err.message);
      }
    };

    // Establecer fecha de vencimiento
    const handleSetExpiration = async (userId, currentExpiration) => {
      const defaultDate = currentExpiration 
        ? new Date(currentExpiration).toISOString().split('T')[0] 
        : '';
      
      const fecha = prompt(
        'Ingresa la fecha de vencimiento (YYYY-MM-DD):\n\n' +
        'Ejemplo: 2025-12-31\n\n' +
        'Dejar vacío para quitar el vencimiento:',
        defaultDate
      );
      
      if (fecha === null) return;
      
      if (fecha && !/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        alert('Formato de fecha inválido. Use: YYYY-MM-DD');
        return;
      }
      
      try {
        await userService.setExpiration(userId, fecha || null);
        alert(fecha 
          ? `Fecha de vencimiento establecida: ${fecha}` 
          : 'Fecha de vencimiento eliminada');
        loadUsers();
      } catch (err) {
        alert('Error: ' + err.message);
      }
    };

    if (loading) {
      return (
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="text-white text-xl">Cargando usuarios...</div>
        </div>
      );
    }

    return (
      <div className="page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Gestión de Usuarios</h1>
            <p className="text-gray-400">Administra las cuentas del sistema</p>
          </div>
          <button
            onClick={() => setShowCreateUser(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold"
          >
            <Plus className="w-5 h-5" />
            Crear Usuario
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid gap-4">
          {users.map((user) => (
            <div key={user.id} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-6 h-6 text-gray-400" />
                    <h3 className="text-white font-bold text-lg">{user.nombre}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.rol === 'admin' ? 'bg-[#ed6f28]/20 text-[#ed6f28]' : 'bg-[#ed6f28]/20 text-gray-400'
                    }`}>
                      {user.rol.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.activo ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                    }`}>
                      {user.activo ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.plan_code === 'ANUAL'    ? 'bg-purple-600/20 text-purple-400' :
                      user.plan_code === 'SEMESTRAL'? 'bg-blue-600/20 text-blue-400' :
                      user.plan_code === 'MENSUAL'  ? 'bg-[#ed6f28]/20 text-[#ed6f28]' :
                                                      'bg-gray-600/20 text-gray-400'
                    }`}>
                      {user.plan_code || 'TRIAL'}
                    </span>
                  </div>
<div className="grid grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Email</p>
                      <p className="text-white font-medium">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Contratos</p>
                      {(() => {
                        const count = contracts.filter(c => c.userId === user.id).length;
                        return (
                          <p className={`font-bold text-base ${count > 0 ? 'text-[#ed6f28]' : 'text-gray-500'}`}>
                            {count > 0 ? `${count} contrato${count !== 1 ? 's' : ''}` : 'Sin contratos'}
                          </p>
                        );
                      })()}
                    </div>
                    <div>
                      <p className="text-gray-400">Fecha Creación</p>
                      <p className="text-white font-medium">
                        {new Date(user.fecha_creacion).toLocaleDateString('es-AR')}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400">Último Acceso</p>
                      <p className="text-white font-medium">
                        {user.ultimo_acceso ? new Date(user.ultimo_acceso).toLocaleDateString('es-AR') : 'Nunca'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400">Vencimiento</p>
                      {user.fecha_vencimiento ? (
                        <div>
                          <p className="text-white font-medium">
                            {new Date(user.fecha_vencimiento).toLocaleDateString('es-AR')}
                          </p>
                          {new Date(user.fecha_vencimiento) < new Date() && (
                            <span className="text-xs text-red-400">Vencida</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-500 font-medium">Sin vencimiento</p>
                      )}
                    </div>
                    
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleResetPassword(user.id)}
                    className="bg-orange-600 hover:bg-orange-700 p-2 rounded-lg"
                    title="Resetear contraseña"
                  >
                    <Save className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => handleSetExpiration(user.id, user.fecha_vencimiento)}
                    className="bg-[#ed6f28] hover:bg-[#c45e1e] p-2 rounded-lg"
                    title="Establecer vencimiento"
                  >
                    <Calendar className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(user.id, user.activo)}
                    className={`${user.activo ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} p-2 rounded-lg`}
                    title={user.activo ? 'Desactivar' : 'Activar'}
                  >
                    <AlertCircle className="w-5 h-5 text-white" />
                  </button>
                  {user.rol !== 'admin' && (
                    <button
                      onClick={() => handleDeleteAllContracts(user.id, user.nombre)}
                      className="bg-purple-700 hover:bg-purple-800 p-2 rounded-lg"
                      title="Borrar todos los contratos"
                    >
                      <FileText className="w-5 h-5 text-white" />
                    </button>
                  )}
                  {user.rol !== 'admin' && (
                    <button
                      onClick={() => handleDeleteUser(user.id, user.nombre)}
                      className="bg-red-600 hover:bg-red-700 p-2 rounded-lg"
                      title="Eliminar usuario"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal: Crear Usuario */}
        {showCreateUser && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-8">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Crear Usuario</h2>
                <button onClick={() => setShowCreateUser(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Nombre Completo *</label>
                  <input
                    type="text"
                    value={userForm.nombre}
                    onChange={(e) => setUserForm({...userForm, nombre: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Juan Pérez"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm block mb-2">Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="juan@example.com"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm block mb-2">Contraseña *</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

<div>
                  <label className="text-gray-400 text-sm block mb-2">Rol</label>
                  <select
                    value={userForm.rol}
                    onChange={(e) => setUserForm({...userForm, rol: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                {/* Campo de Fecha de Vencimiento */}
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Fecha de Vencimiento (Opcional)</label>
                  <input
                    type="date"
                    value={userForm.fecha_vencimiento}
                    onChange={(e) => setUserForm({...userForm, fecha_vencimiento: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La cuenta se desactivará automáticamente después de esta fecha
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">

                {/* 👇 CAMPO NUEVO: Fecha de Vencimiento */}
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Fecha de Vencimiento (Opcional)</label>
                  <input
                    type="date"
                    value={userForm.fecha_vencimiento}
                    onChange={(e) => setUserForm({...userForm, fecha_vencimiento: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La cuenta se desactivará automáticamente después de esta fecha
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateUser(false)}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateUser}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Crear Usuario
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Settings View
  const SubscriptionView = () => {
    const [planes, setPlanes] = useState([]);
    const [loadingPlanes, setLoadingPlanes] = useState(true);
    const [payingPlan, setPayingPlan] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    const currentPlanCode = currentUser?.plan_code || 'TRIAL';
    const vencimiento = currentUser?.fecha_vencimiento;
    const planOrder = { TRIAL: 0, MENSUAL: 1, SEMESTRAL: 2, ANUAL: 3 };
    const currentOrder = planOrder[currentPlanCode] ?? 0;

    useEffect(() => {
      mpService.getPlanes()
        .then(data => { if (data.success) setPlanes(data.planes.filter(p => Number(p.price) > 0)); })
        .catch(() => {})
        .finally(() => setLoadingPlanes(false));
    }, []);

    const confirmarPago = async () => {
      const planCode = confirmModal.plan_code;
      setConfirmModal(null);
      setPayingPlan(planCode);
      try {
        const data = await mpService.crearPreferencia(planCode);
        if (!data.success) { alert('Error: ' + (data.error || 'Intenta de nuevo')); return; }
        window.location.href = data.init_point || data.sandbox_init_point;
      } catch (err) {
        alert('Error al iniciar pago. Intenta de nuevo.');
      } finally {
        setPayingPlan(null);
      }
    };

    const diasRestantes = vencimiento
      ? Math.ceil((new Date(vencimiento) - new Date()) / (1000 * 60 * 60 * 24))
      : null;

    const planLabels = { TRIAL: 'Prueba gratuita', MENSUAL: 'Mensual', SEMESTRAL: 'Semestral', ANUAL: 'Anual' };

    return (
      <div className="page" style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ color: 'var(--t1)', fontWeight: 800, fontSize: 26, marginBottom: 4 }}>Mi suscripcion</h1>
          <p style={{ color: 'var(--t3)', fontSize: 14 }}>Gestioná tu plan y metodo de pago</p>
        </div>

        {/* Estado actual */}
        <div className="card" style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <p style={{ color: 'var(--t3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Plan actual</p>
              <p style={{ color: 'var(--t1)', fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{planLabels[currentPlanCode] || currentPlanCode}</p>
              {vencimiento && (
                <p style={{ fontSize: 13, color: diasRestantes !== null && diasRestantes <= 7 ? 'var(--red)' : 'var(--t3)' }}>
                  {diasRestantes !== null && diasRestantes > 0
                    ? `Vence el ${new Date(vencimiento).toLocaleDateString('es-AR')} (${diasRestantes} dia${diasRestantes !== 1 ? 's' : ''})`
                    : diasRestantes === 0 ? 'Vence hoy'
                    : `Vencio el ${new Date(vencimiento).toLocaleDateString('es-AR')}`}
                </p>
              )}
            </div>
            <button
              onClick={() => { refreshCurrentUser(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--t3)', background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
            >
              <RefreshCw style={{ width: 14, height: 14 }} /> Actualizar
            </button>
          </div>
          {currentUser?.in_grace_period && (
            <div style={{ marginTop: 14, background: 'var(--rDim)', border: '1px solid var(--red)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--red)', fontSize: 13, fontWeight: 500 }}>
              <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
              Cuenta en periodo de gracia — regulariza tu suscripcion para no perder el acceso
            </div>
          )}
        </div>

        {/* Planes disponibles */}
        <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 17, marginBottom: 16 }}>
          {currentPlanCode === 'TRIAL' ? 'Elegi un plan' : 'Renovar o cambiar plan'}
        </p>

        {loadingPlanes ? (
          <p style={{ color: 'var(--t3)', fontSize: 13 }}>Cargando planes...</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
            {planes.map(plan => {
              const isCurrent = plan.plan_code === currentPlanCode;
              const isUpgrade = planOrder[plan.plan_code] > currentOrder;
              return (
                <div
                  key={plan.plan_code}
                  className="card"
                  style={{
                    border: isCurrent ? '1.5px solid var(--orange)' : '1px solid var(--border)',
                    background: isCurrent ? 'var(--orDim)' : 'var(--surf)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 15 }}>{plan.plan_name}</p>
                    {isCurrent && <span style={{ background: 'var(--orange)', color: '#fff', borderRadius: 20, fontSize: 11, padding: '2px 8px', fontWeight: 700 }}>Actual</span>}
                    {!isCurrent && isUpgrade && <span style={{ background: 'var(--grDim)', color: 'var(--green)', borderRadius: 20, fontSize: 11, padding: '2px 8px', fontWeight: 700 }}>Upgrade</span>}
                  </div>
                  <p style={{ color: 'var(--t1)', fontSize: 26, fontWeight: 800 }}>
                    ${Number(plan.price).toLocaleString('es-AR')}
                    <span style={{ color: 'var(--t3)', fontSize: 13, fontWeight: 400, marginLeft: 4 }}>
                      {plan.duration_days === 30 ? '/mes' : plan.duration_days === 180 ? '/6 meses' : '/año'}
                    </span>
                  </p>
                  {isCurrent ? (
                    <p style={{ color: 'var(--t3)', fontSize: 12, textAlign: 'center', padding: '8px 0' }}>Plan vigente</p>
                  ) : (
                    <button
                      onClick={() => setConfirmModal(plan)}
                      disabled={!!payingPlan}
                      className="btn-o"
                      style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: payingPlan ? 0.5 : 1 }}
                    >
                      {payingPlan === plan.plan_code ? (
                        <><RefreshCw style={{ width: 14, height: 14 }} /> Procesando...</>
                      ) : (
                        <><CreditCard style={{ width: 14, height: 14 }} /> Suscribirse</>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Modal confirmacion */}
        {confirmModal && ReactDOM.createPortal(
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmModal(null)}>
            <div className="modal" style={{ maxWidth: 400 }}>
              <div style={{ textAlign: 'center', padding: '24px 24px 0' }}>
                <div style={{ width: 48, height: 48, background: 'var(--orDim)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <CreditCard style={{ width: 22, height: 22, color: 'var(--orange)' }} />
                </div>
                <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 17, marginBottom: 8 }}>Confirmar suscripcion</p>
                <p style={{ color: 'var(--t3)', fontSize: 14, marginBottom: 16 }}>
                  Vas a suscribirte al <strong style={{ color: 'var(--t1)' }}>Plan {confirmModal.plan_name}</strong> por{' '}
                  <strong style={{ color: 'var(--t1)' }}>${Number(confirmModal.price).toLocaleString('es-AR')} ARS</strong>.
                </p>
                <div style={{ background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, textAlign: 'left' }}>
                  <p style={{ color: 'var(--t3)', fontSize: 11, marginBottom: 3 }}>La suscripcion se aplicara a</p>
                  <p style={{ color: 'var(--t1)', fontSize: 13, fontWeight: 500 }}>{currentUser?.email}</p>
                </div>
              </div>
              <div className="modal-foot">
                <button className="modal-btn" onClick={() => setConfirmModal(null)}>Cancelar</button>
                <button className="modal-btn solid" onClick={confirmarPago}>Ir a pagar</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };


  const SettingsView = () => {
    const [userLogo, setUserLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    useEffect(() => {
      const fetchLogo = async () => {
        try {
          const result = await logoService.getLogo();
          if (result.success && result.logoUrl) {
            setUserLogo(result.logoUrl);
            setLogoPreview(logoService.getLogoUrl(result.logoUrl));
          }
        } catch (error) {
          console.error('Error loading logo:', error);
        }
      };
      fetchLogo();
    }, []);

    const handleLogoUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { alert('El archivo es demasiado grande. Maximo 5MB.'); return; }
      if (!file.type.startsWith('image/')) { alert('Solo se permiten archivos de imagen.'); return; }
      setIsUploadingLogo(true);
      try {
        const result = await logoService.uploadLogo(file);
        if (result.success) {
          setUserLogo(result.logoUrl);
          setLogoPreview(logoService.getLogoUrl(result.logoUrl));
          alert('Logo subido exitosamente');
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
        alert('Error al subir el logo: ' + error.message);
      } finally {
        setIsUploadingLogo(false);
      }
    };

    const handleDeleteLogo = async () => {
      if (!window.confirm('Estas seguro de que queres eliminar el logo?')) return;
      try {
        const result = await logoService.deleteLogo();
        if (result.success) {
          setUserLogo(null);
          setLogoPreview(null);
          alert('Logo eliminado exitosamente');
        }
      } catch (error) {
        console.error('Error deleting logo:', error);
        alert('Error al eliminar el logo: ' + error.message);
      }
    };

    const SectionCard = ({ icon: Icon, title, children }) => (
      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ color: 'var(--t1)', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
          <Icon style={{ width: 18, height: 18, color: 'var(--orange)' }} />
          {title}
        </p>
        {children}
      </div>
    );

    return (
      <div className="page">
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ color: 'var(--t1)', fontWeight: 800, fontSize: 26, marginBottom: 4 }}>Configuracion</h1>
            <p style={{ color: 'var(--t3)', fontSize: 14 }}>Personaliza tu cuenta y configuracion de la plataforma</p>
          </div>

          {/* Logo */}
          <SectionCard icon={Image} title="Logo para Recibos">
            <p style={{ color: 'var(--t3)', fontSize: 13, marginBottom: 20 }}>
              Sube tu logo personalizado que aparecera en todos los recibos generados en PDF. El logo aparecera en la esquina superior derecha del recibo.
            </p>

            {logoPreview && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={logoPreview} alt="Logo actual" style={{ height: 64, width: 'auto', objectFit: 'contain', background: '#fff', padding: 6, borderRadius: 6 }} />
                  <div>
                    <p style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 14 }}>Logo actual</p>
                    <p style={{ color: 'var(--t3)', fontSize: 12 }}>Este logo aparecera en tus recibos</p>
                  </div>
                </div>
                <button className="hd-act gr" onClick={handleDeleteLogo} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Trash2 style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              </div>
            )}

            <div style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '28px 24px', textAlign: 'center' }}>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" disabled={isUploadingLogo} />
              <label
                htmlFor="logo-upload"
                className="btn-o"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: isUploadingLogo ? 'not-allowed' : 'pointer', opacity: isUploadingLogo ? 0.5 : 1 }}
              >
                <Upload style={{ width: 16, height: 16 }} />
                {isUploadingLogo ? 'Subiendo...' : logoPreview ? 'Cambiar Logo' : 'Subir Logo'}
              </label>
              <p style={{ color: 'var(--t3)', fontSize: 12, marginTop: 10 }}>Formatos: JPG, PNG, GIF, WEBP (max. 5MB)</p>
            </div>
          </SectionCard>

          {/* Info usuario */}
          <SectionCard icon={User} title="Informacion de Usuario">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { l: 'Nombre', v: currentUser?.name || 'N/A' },
                { l: 'Email', v: currentUser?.email || 'N/A' },
                { l: 'Rol', v: currentUser?.role || 'N/A' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surf2)', borderRadius: 8 }}>
                  <span style={{ color: 'var(--t3)', fontSize: 13 }}>{item.l}</span>
                  <span style={{ color: 'var(--t1)', fontWeight: 600, fontSize: 13 }}>{item.v}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Email config */}
          <SectionCard icon={Mail} title="Configuracion de Email">
            <p style={{ color: 'var(--t3)', fontSize: 13, marginBottom: 20 }}>
              Configura tu cuenta de email para enviar mensajes a los inquilinos desde tu propia direccion de correo.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-field">
                <label>Email para Enviar</label>
                <input
                  type="email"
                  placeholder="tu_email@gmail.com"
                  value={emailConfig.email_user}
                  onChange={e => setEmailConfig(prev => ({ ...prev, email_user: e.target.value }))}
                />
                <p style={{ color: 'var(--t3)', fontSize: 11, marginTop: 4 }}>Puede ser diferente al email de tu cuenta Rentia</p>
              </div>

              <div className="form-field">
                <label>Contrasena de Aplicacion</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showEmailPassword ? 'text' : 'password'}
                    placeholder={emailConfig.hasPassword ? '••••••••••••••••' : 'Contrasena de aplicacion de Gmail'}
                    value={emailConfig.email_password}
                    autoComplete="new-password"
                    name="email_app_password"
                    onChange={e => setEmailConfig(prev => ({ ...prev, email_password: e.target.value }))}
                    style={{ paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmailPassword(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--t3)', cursor: 'pointer', display: 'flex' }}
                  >
                    {showEmailPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {emailConfig.hasPassword && !emailConfig.email_password && (
                  <p style={{ color: 'var(--green)', fontSize: 11, marginTop: 4 }}>Contrasena de aplicacion guardada. Deja el campo vacio para mantenerla.</p>
                )}
                <div style={{ marginTop: 10, background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                  <p style={{ color: 'var(--t2)', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Como obtener una contrasena de aplicacion?</p>
                  <ol style={{ color: 'var(--t3)', fontSize: 12, paddingLeft: 16, lineHeight: 1.8 }}>
                    <li>Ve a tu cuenta de Google → Seguridad</li>
                    <li>Activa la Verificacion en 2 pasos</li>
                    <li>Busca "Contrasenas de aplicaciones"</li>
                    <li>Genera una contrasena para "Correo"</li>
                    <li>Copia los 16 caracteres aqui</li>
                  </ol>
                </div>
              </div>

              <div className="form-field">
                <label>Servicio de Email</label>
                <select
                  value={emailConfig.email_service}
                  onChange={e => setEmailConfig(prev => ({ ...prev, email_service: e.target.value }))}
                >
                  <option value="gmail">Gmail</option>
                  <option value="outlook">Outlook</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
                <input
                  type="checkbox"
                  id="email-enabled"
                  checked={emailConfig.email_enabled}
                  onChange={e => setEmailConfig(prev => ({ ...prev, email_enabled: e.target.checked }))}
                  style={{ width: 18, height: 18, accentColor: 'var(--orange)', cursor: 'pointer' }}
                />
                <label htmlFor="email-enabled" style={{ color: 'var(--t1)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  Habilitar envio de emails
                </label>
              </div>

              <button onClick={saveEmailConfig} className="btn-o" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Save style={{ width: 16, height: 16 }} />
                Guardar Configuracion
              </button>

              {emailConfig.email_enabled && emailConfig.email_user && (
                <div style={{ background: 'var(--grDim)', border: '1px solid var(--green)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--green)', fontSize: 13 }}>
                  <CheckCircle style={{ width: 15, height: 15 }} />
                  Configuracion activa — los emails se enviaran desde: {emailConfig.email_user}
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>
    );
  };


  // BOLETAS VIEW
  // ============================================
  const BoletasView = () => {
    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroEstado, setFiltroEstado] = useState('TODOS');
    const [busqueda, setBusqueda] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editando, setEditando] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const [fotoViewer, setFotoViewer] = useState(null);
    const [form, setForm] = useState({
      tipo: 'OTRO', descripcion: '', monto: '', fecha_boleta: '',
      fecha_vencimiento: '', estado: 'PENDIENTE',
      personas_involucradas: '', notas: '', contrato_id: ''
    });

    const TIPOS = ['LUZ', 'GAS', 'AGUA', 'INTERNET', 'EXPENSAS', 'IMPUESTOS', 'OTRO'];
    const ESTADOS = ['PENDIENTE', 'PAGADA', 'VENCIDA'];

    const BOL_ST = {
      PAGADA:   { c: 'var(--green)',  bg: 'var(--grDim)', l: 'Pagada'   },
      PENDIENTE:{ c: 'var(--yellow)', bg: 'var(--ylDim)', l: 'Pendiente'},
      VENCIDA:  { c: 'var(--red)',    bg: 'var(--rDim)',  l: 'Vencida'  },
    };
    const BOL_TIPO = {
      LUZ:       { c: 'var(--yellow)', bg: 'var(--ylDim)' },
      GAS:       { c: 'var(--orange)', bg: 'var(--orDim)' },
      AGUA:      { c: 'var(--blue)',   bg: 'var(--blDim)' },
      INTERNET:  { c: 'var(--t2)',     bg: 'var(--surf3)' },
      EXPENSAS:  { c: 'var(--violet)', bg: 'var(--vlDim)' },
      IMPUESTOS: { c: 'var(--red)',    bg: 'var(--rDim)'  },
      OTRO:      { c: 'var(--t3)',     bg: 'var(--surf3)' },
    };

    const cargar = async () => {
      setLoading(true);
      try {
        const data = await boletasService.getAll();
        setLista(data.boletas || []);
      } catch (e) {
        console.error('Error cargando boletas:', e);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => { cargar(); }, []);

    const abrirNueva = () => {
      setEditando(null);
      setFotoFile(null);
      setFotoPreview(null);
      setForm({ tipo: 'OTRO', descripcion: '', monto: '', fecha_boleta: '',
        fecha_vencimiento: '', estado: 'PENDIENTE',
        personas_involucradas: '', notas: '', contrato_id: '' });
      setShowModal(true);
    };

    const abrirEdicion = (b) => {
      setEditando(b);
      setFotoFile(null);
      setFotoPreview(b.foto_url ? boletasService.getFotoUrl(b.foto_url) : null);
      setForm({
        tipo: b.tipo || 'OTRO',
        descripcion: b.descripcion || '',
        monto: b.monto || '',
        fecha_boleta: b.fecha_boleta ? b.fecha_boleta.split('T')[0] : '',
        fecha_vencimiento: b.fecha_vencimiento ? b.fecha_vencimiento.split('T')[0] : '',
        estado: b.estado || 'PENDIENTE',
        personas_involucradas: b.personas_involucradas || '',
        notas: b.notas || '',
        contrato_id: b.contrato_id || ''
      });
      setShowModal(true);
    };

    const guardar = async () => {
      if (!form.tipo) return;
      setGuardando(true);
      try {
        let boleta;
        if (editando) {
          const res = await boletasService.update(editando.id, form);
          boleta = res.boleta;
        } else {
          const res = await boletasService.create(form);
          boleta = res.boleta;
        }
        if (fotoFile && boleta?.id) {
          await boletasService.uploadFoto(boleta.id, fotoFile);
        }
        await cargar();
        setShowModal(false);
      } catch (e) {
        console.error('Error guardando boleta:', e);
        alert('Error al guardar la boleta');
      } finally {
        setGuardando(false);
      }
    };

    const eliminar = async (id) => {
      if (!window.confirm('Eliminar esta boleta?')) return;
      try {
        await boletasService.delete(id);
        setLista(prev => prev.filter(b => b.id !== id));
      } catch (e) {
        alert('Error al eliminar');
      }
    };

    const onFotoChange = (e) => {
      const f = e.target.files[0];
      if (!f) return;
      setFotoFile(f);
      setFotoPreview(URL.createObjectURL(f));
    };

    const filtradas = lista.filter(b => {
      const matchEstado = filtroEstado === 'TODOS' || b.estado === filtroEstado;
      const q = busqueda.toLowerCase();
      const matchBusq = !q || (b.descripcion || '').toLowerCase().includes(q)
        || (b.tipo || '').toLowerCase().includes(q)
        || (b.contrato_direccion || '').toLowerCase().includes(q)
        || (b.contrato_inquilino || '').toLowerCase().includes(q);
      return matchEstado && matchBusq;
    });

    return (
      <div className="page">
        <div className="ct-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
          <div className="ct-kpi" style={{'--ck-c':'var(--yellow)'}}><div className="ct-kpi-n">{lista.filter(b=>b.estado==='PENDIENTE').length}</div><div className="ct-kpi-l">Pendientes</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--red)'}}><div className="ct-kpi-n">{lista.filter(b=>b.estado==='VENCIDA').length}</div><div className="ct-kpi-l">Vencidas</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--green)'}}><div className="ct-kpi-n">{lista.filter(b=>b.estado==='PAGADA').length}</div><div className="ct-kpi-l">Pagadas</div></div>
          <div className="ct-kpi" style={{'--ck-c':'var(--blue)'}}><div className="ct-kpi-n">{lista.length}</div><div className="ct-kpi-l">Total</div></div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div className="ct-search" style={{ flex: 1, minWidth: 200 }}>
            <div className="ct-s-ico"><Search style={{ width: 13, height: 13 }} /></div>
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar boleta..."/>
          </div>
          <div className="ct-filters">
            {['TODOS', ...ESTADOS].map(e => (
              <button key={e} onClick={() => setFiltroEstado(e)} className={`ct-pill ${filtroEstado === e ? (e==='VENCIDA'?'on-r':e==='PAGADA'?'on-g':'on') : ''}`}>
                {e === 'TODOS' ? 'Todas' : e.charAt(0) + e.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
          <button onClick={abrirNueva} className="btn-o" style={{ padding: '8px 14px', fontSize: 12.5, flexShrink: 0 }}>
            <Plus style={{ width: 13, height: 13 }} /> Nueva Boleta
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--t3)', fontSize: 13 }}>Cargando boletas...</div>
        ) : filtradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--t3)', fontSize: 13 }}>
            No hay boletas{filtroEstado !== 'TODOS' ? ` con estado "${filtroEstado}"` : ''}
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Tipo', 'Descripción', 'Contrato', 'Monto', 'Vencimiento', 'Estado', ''].map(h => (
                    <th key={h} style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--t3)', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid var(--border)', background: 'var(--surf2)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtradas.map((b) => {
                  const st = BOL_ST[b.estado] || BOL_ST.PENDIENTE;
                  const tp = BOL_TIPO[b.tipo] || BOL_TIPO.OTRO;
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.018)'} onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ background: tp.bg, color: tp.c, borderRadius: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 650 }}>{b.tipo}</span>
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12.5, color: 'var(--t1)' }}>
                        {b.descripcion || <span style={{ color: 'var(--t3)' }}>—</span>}
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{b.contrato_direccion || <span style={{ color: 'var(--t3)' }}>—</span>}</div>
                        {b.contrato_inquilino && <div style={{ fontSize: 10.5, color: 'var(--t3)' }}>{b.contrato_inquilino}</div>}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 13.5, fontWeight: 720, letterSpacing: '-0.2px', color: 'var(--t1)' }}>
                        {b.monto ? `$${parseFloat(b.monto).toLocaleString('es-AR')}` : '—'}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: b.estado === 'VENCIDA' ? 'var(--red)' : 'var(--t2)' }}>
                        {b.fecha_vencimiento ? new Date(b.fecha_vencimiento).toLocaleDateString('es-AR') : '—'}
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ background: st.bg, color: st.c, borderRadius: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 650 }}>{st.l}</span>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          {b.foto_url && (
                            <button onClick={() => setFotoViewer(boletasService.getFotoUrl(b.foto_url))}
                              style={{ background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 'var(--r2)', padding: '4px 9px', fontSize: 11, color: 'var(--t2)', cursor: 'pointer', fontFamily: 'inherit' }}>
                              <Image style={{ width: 12, height: 12 }} />
                            </button>
                          )}
                          <button onClick={() => abrirEdicion(b)}
                            style={{ background: 'var(--surf2)', border: '1px solid var(--border)', borderRadius: 'var(--r2)', padding: '4px 9px', fontSize: 11, color: 'var(--t2)', cursor: 'pointer', fontFamily: 'inherit' }}>
                            <Edit style={{ width: 12, height: 12 }} />
                          </button>
                          <button onClick={() => eliminar(b.id)}
                            style={{ background: 'var(--rDim)', color: 'var(--red)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--r2)', padding: '4px 9px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                            <Trash2 style={{ width: 12, height: 12 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <div className="modal">
              <div className="modal-head">
                <span className="modal-title">{editando ? 'Editar Boleta' : 'Nueva Boleta'}</span>
                <button className="modal-close" onClick={() => setShowModal(false)}><X size={14}/></button>
              </div>
              <div className="modal-body">
                <div className="form-row col2">
                  <div className="form-field">
                    <label>Tipo *</label>
                    <select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                      {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Estado</label>
                    <select value={form.estado} onChange={e => setForm({...form, estado: e.target.value})}>
                      {ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <input type="text" value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} placeholder="Ej: Factura de luz marzo 2025"/>
                </div>
                <div className="form-row col2">
                  <div className="form-field">
                    <label>Monto ($)</label>
                    <input type="number" value={form.monto} onChange={e => setForm({...form, monto: e.target.value})} placeholder="0"/>
                  </div>
                  <div className="form-field">
                    <label>Contrato vinculado</label>
                    <select value={form.contrato_id} onChange={e => setForm({...form, contrato_id: e.target.value})}>
                      <option value="">— Ninguno —</option>
                      {contracts.filter(c => c.status === 'ACTIVO').map(c => (
                        <option key={c.id} value={c.id}>{c.propertyAddress}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row col2">
                  <div className="form-field">
                    <label>Fecha de boleta</label>
                    <input type="date" value={form.fecha_boleta} onChange={e => setForm({...form, fecha_boleta: e.target.value})}/>
                  </div>
                  <div className="form-field">
                    <label>Fecha de vencimiento</label>
                    <input type="date" value={form.fecha_vencimiento} onChange={e => setForm({...form, fecha_vencimiento: e.target.value})}/>
                  </div>
                </div>
                <div className="form-field">
                  <label>Personas involucradas</label>
                  <input type="text" value={form.personas_involucradas} onChange={e => setForm({...form, personas_involucradas: e.target.value})} placeholder="Ej: propietario, inquilino..."/>
                </div>
                <div className="form-field">
                  <label>Notas</label>
                  <textarea rows={2} value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} placeholder="Observaciones adicionales..."/>
                </div>
                <div className="form-field">
                  <label>Foto / Comprobante</label>
                  <input type="file" accept="image/*,application/pdf" onChange={onFotoChange}
                    style={{ fontSize: 12, color: 'var(--t2)' }}/>
                  {fotoPreview && (
                    <div style={{ marginTop: 8 }}>
                      <img src={fotoPreview} alt="preview" style={{ height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }}/>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-foot">
                <button className="modal-btn cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                <button className="modal-btn create" onClick={guardar} disabled={guardando}>
                  <Save size={13}/> {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear boleta'}
                </button>
              </div>
            </div>
          </div>
        )}

        {fotoViewer && (
          <div className="modal-overlay" onClick={() => setFotoViewer(null)}>
            <div style={{ position: 'relative', maxWidth: 800, width: '100%' }}>
              <img src={fotoViewer} alt="boleta" style={{ width: '100%', borderRadius: 12, objectFit: 'contain', maxHeight: '85vh' }}/>
              <button onClick={() => setFotoViewer(null)} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                <X size={16}/>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Main Render
  if (!isLoggedIn) {
    return <LoginScreen />;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <div className="rentia-main">
        <Header />
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)' }}>
          <TrialBanner />
          {mpNotification && (
            <div style={{
              margin: '12px 26px 0',
              padding: '12px 16px',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              fontSize: 13,
              background: mpNotification.type === 'success' ? 'rgba(52,211,153,0.1)' : mpNotification.type === 'error' ? 'rgba(248,113,113,0.1)' : 'rgba(245,186,69,0.1)',
              border: `1px solid ${mpNotification.type === 'success' ? 'rgba(52,211,153,0.25)' : mpNotification.type === 'error' ? 'rgba(248,113,113,0.25)' : 'rgba(245,186,69,0.25)'}`,
              color: mpNotification.type === 'success' ? 'var(--green)' : mpNotification.type === 'error' ? 'var(--red)' : 'var(--yellow)',
            }}>
              <span style={{ flex: 1 }}>{mpNotification.msg}</span>
              <button onClick={() => setMpNotification(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6, color: 'inherit', marginTop: 1 }}>✕</button>
            </div>
          )}
          <div key={currentView} className="view-enter">
            {currentView === 'dashboard' && <Dashboard />}
            {currentView === 'contracts' && <ContractsView />}
            {currentView === 'payments' && <PaymentsView />}
            {currentView === 'incidents' && <IncidentsView />}
            {currentView === 'boletas' && <BoletasView />}
            {currentView === 'notifications' && <NotificationsView />}
            {currentView === 'reports' && <ReportsView />}
            {currentView === 'indices' && <IndicesView indices={indices} setIndices={setIndices} />}
            {currentView === 'users' && currentUser?.rol === 'admin' && <UsersManagementView />}
            {currentView === 'suscripcion' && <SubscriptionView />}
            {currentView === 'settings' && <SettingsView />}
          </div>
        </div>
      </div>
{showModal === 'contract' && (
        <NuevoContratoModal
          key={editingItem?.id || 'edit'}
          editingItem={editingItem}
          onClose={() => { setShowModal(null); setEditingItem(null); }}
          onCreate={(formData) => {
            const freqMap = { 'Mensual': 1, 'Bimestral': 2, 'Trimestral': 3, 'Cuatrimestral (cada 4 meses)': 4, 'Semestral (cada 6 meses)': 6, 'Anual (cada 12 meses)': 12 };
            const cobMap  = { 'Mensual': 1, 'Bimestral': 2, 'Trimestral': 3 };
            saveContract({
              propertyAddress: formData.prop.dir,
              propertyType:    formData.prop.tipo || 'Departamento',
              ownerName:       formData.propietario.nombre,
              ownerDNI:        formData.propietario.dni,
              ownerEmail:      formData.propietario.email,
              ownerPhone:      formData.propietario.tel,
              tenantName:      formData.inquilino.nombre,
              tenantDNI:       formData.inquilino.dni,
              tenantEmail:     formData.inquilino.email,
              tenantPhone:     formData.inquilino.tel,
              startDate:       formData.contrato.inicio,
              endDate:         formData.contrato.fin,
              baseRent:        formData.contrato.monto,
              currency:        formData.contrato.moneda || 'ARS',
              indexType:       formData.contrato.indice || 'ICL',
              increaseFrequencyMonths: freqMap[formData.contrato.frecAjuste] || 4,
              paymentFrequencyMonths:  cobMap[formData.contrato.frecCobro]   || 1,
              commissionPercent: formData.contrato.comision || 10,
              status: 'ACTIVO',
            });
          }}
        />
      )}
      {showModal === 'contractDetails' && <ContractDetailsModal />}

      {/* Modal de configuración de mensajes */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowMessageModal(false)}>
          <div className="modal" style={{ maxWidth: 580 }}>
            <div className="modal-head">
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {messageConfig.type === 'email'
                  ? <Mail size={15} color="var(--orange)"/>
                  : <MessageSquare size={15} color="var(--green)"/>}
                <span className="modal-title">{messageConfig.type === 'email' ? 'Enviar Email' : 'Enviar WhatsApp'}</span>
              </div>
              <button className="modal-close" onClick={() => setShowMessageModal(false)}><X size={14}/></button>
            </div>
            <div className="modal-body">
              {/* Destinatario */}
              <div style={{ background:'var(--surf2)', border:'1px solid var(--border)', borderRadius:'var(--r2)', padding:'10px 14px' }}>
                <div style={{ fontSize:10, color:'var(--t3)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Destinatario</div>
                <div style={{ fontSize:13, fontWeight:650, color:'var(--t1)' }}>{messageConfig.contract?.tenantName}</div>
                <div style={{ fontSize:12, color:'var(--t3)', marginTop:2 }}>
                  {messageConfig.type === 'email' ? messageConfig.contract?.tenantEmail : messageConfig.contract?.tenantPhone}
                </div>
                <div style={{ fontSize:11, color:'var(--t3)', marginTop:1 }}>{messageConfig.contract?.propertyAddress}</div>
              </div>

              {/* Asunto — solo email */}
              {messageConfig.type === 'email' && (
                <div className="form-field">
                  <label>Asunto</label>
                  <input type="text" value={messageConfig.subject} onChange={e => setMessageConfig({...messageConfig, subject: e.target.value})}/>
                </div>
              )}

              {/* Mensaje */}
              <div className="form-field">
                <label>Mensaje</label>
                <textarea value={messageConfig.message} onChange={e => setMessageConfig({...messageConfig, message: e.target.value})} rows={8} style={{ resize:'vertical' }} placeholder="Escriba su mensaje aquí..."/>
                <span style={{ fontSize:10.5, color:'var(--t3)' }}>{messageConfig.message.length} caracteres</span>
              </div>
            </div>
            <div className="modal-foot">
              <button className="modal-btn cancel" onClick={() => setShowMessageModal(false)}>Cancelar</button>
              <button
                className="modal-btn"
                style={{ background: messageConfig.type === 'email' ? 'var(--orange)' : 'var(--green)', color:'#fff', borderColor: messageConfig.type === 'email' ? 'var(--orange)' : 'var(--green)', display:'flex', alignItems:'center', gap:6 }}
                onClick={handleSendMessage}
              >
                {messageConfig.type === 'email'
                  ? <><Mail size={13}/> Enviar Email</>
                  : <><MessageSquare size={13}/> Enviar WhatsApp</>}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}; // ESTA LLAVE FALTABA - Cierra el componente App

// ─────────────────────────────────────────────────────────────────────────────
// NuevoContratoModal — wizard de 5 pasos para crear contratos
// ─────────────────────────────────────────────────────────────────────────────

const _C = {
  surf:    '#111116',
  surf2:   '#18181f',
  surf3:   '#1f1f28',
  border:  'rgba(255,255,255,0.07)',
  borderB: 'rgba(255,255,255,0.13)',
  t1:      '#efeff6',
  t2:      '#8f8ea2',
  t3:      '#56556a',
  orange:  '#ed6f28',
  orDim:   'rgba(237,111,40,0.12)',
  green:   '#34d399',
  blue:    '#60a5fa',
};

const _STEPS = [
  { label: 'Propiedad',   Icon: Home },
  { label: 'Propietario', Icon: User },
  { label: 'Inquilino',   Icon: Users },
  { label: 'Contrato',    Icon: FileText },
  { label: 'Resumen',     Icon: Check },
];

const _inputStyle = {
  width: '100%',
  background: _C.surf2,
  border: `1px solid ${_C.border}`,
  borderRadius: 6,
  padding: '8px 11px',
  fontSize: 12.5,
  color: _C.t1,
  outline: 'none',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

function _Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block mb-1.5 uppercase tracking-wider font-bold"
        style={{ fontSize: 9.5, color: _C.t3 }}>
        {label}{required && <span style={{ color: _C.orange }}> *</span>}
      </label>
      {children}
      {hint && <p className="mt-1" style={{ fontSize: 10.5, color: _C.t3 }}>{hint}</p>}
    </div>
  );
}

function _Inp(props) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ..._inputStyle,
        ...(focused ? { borderColor: _C.orange, boxShadow: `0 0 0 3px ${_C.orDim}` } : {}),
        ...(props.readOnly ? { color: _C.t2, cursor: 'default' } : {}),
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function _Sel({ opts, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        ..._inputStyle,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2356556a' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 11px center',
        paddingRight: 30,
        cursor: 'pointer',
        WebkitAppearance: 'none',
        borderColor: focused ? _C.orange : _C.border,
        boxShadow: focused ? `0 0 0 3px ${_C.orDim}` : 'none',
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {opts.map(o =>
        typeof o === 'string'
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.v} value={o.v}>{o.l}</option>
      )}
    </select>
  );
}

function _SectionTitle({ Icon: IconComp, children }) {
  return (
    <div className="flex items-center gap-2 pb-2 mb-3 uppercase font-bold tracking-widest"
      style={{ fontSize: 9.5, color: _C.orange, borderBottom: `1px solid ${_C.border}` }}>
      <IconComp size={11} />
      {children}
    </div>
  );
}

function _StepPropiedad({ d, set }) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <_SectionTitle Icon={Home}>Propiedad</_SectionTitle>
        <div className="grid gap-3">
          <_Field label="Dirección completa" required hint="Calle, número, piso y depto">
            <_Inp placeholder="Ej: Corrientes 4521 Piso 2 Dpto C" value={d.dir} onChange={e => set('dir', e.target.value)} />
          </_Field>
          <_Field label="Tipo" required>
            <_Sel opts={['Departamento', 'Casa', 'PH', 'Local Comercial', 'Oficina', 'Cochera']}
              value={d.tipo} onChange={e => set('tipo', e.target.value)} />
          </_Field>
        </div>
      </div>
    </div>
  );
}

function _StepPropietario({ d, set }) {
  return (
    <div>
      <_SectionTitle Icon={User}>Datos del propietario</_SectionTitle>
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <_Field label="Nombre completo" required>
            <_Inp placeholder="Nombre y apellido" value={d.nombre} onChange={e => set('nombre', e.target.value)} />
          </_Field>
          <_Field label="DNI / CUIT" required>
            <_Inp placeholder="30-12345678-9" value={d.dni} onChange={e => set('dni', e.target.value)} />
          </_Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <_Field label="Email" required hint="Para envío de comprobantes y avisos">
            <_Inp type="email" placeholder="email@ejemplo.com" value={d.email} onChange={e => set('email', e.target.value)} />
          </_Field>
          <_Field label="Teléfono">
            <_Inp placeholder="+54 11 4444-5555" value={d.tel} onChange={e => set('tel', e.target.value)} />
          </_Field>
        </div>
      </div>
    </div>
  );
}

function _StepInquilino({ d, set }) {
  return (
    <div>
      <_SectionTitle Icon={Users}>Datos del inquilino</_SectionTitle>
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <_Field label="Nombre completo" required>
            <_Inp placeholder="Nombre y apellido" value={d.nombre} onChange={e => set('nombre', e.target.value)} />
          </_Field>
          <_Field label="DNI" required>
            <_Inp placeholder="30123456" value={d.dni} onChange={e => set('dni', e.target.value)} />
          </_Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <_Field label="Email" required>
            <_Inp type="email" placeholder="email@ejemplo.com" value={d.email} onChange={e => set('email', e.target.value)} />
          </_Field>
          <_Field label="Teléfono" required>
            <_Inp placeholder="+54 11 4444-5555" value={d.tel} onChange={e => set('tel', e.target.value)} />
          </_Field>
        </div>
      </div>
    </div>
  );
}

function _StepContrato({ d, set }) {
  const duracion = d.inicio && d.fin
    ? (() => {
        const meses = Math.round((new Date(d.fin) - new Date(d.inicio)) / (1000 * 60 * 60 * 24 * 30.44));
        return meses > 0 ? `${meses} meses` : '';
      })()
    : '';

  return (
    <div className="flex flex-col gap-5">
      <div>
        <_SectionTitle Icon={FileText}>Plazo</_SectionTitle>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <_Field label="Fecha de inicio" required>
              <_Inp type="date" value={d.inicio} onChange={e => set('inicio', e.target.value)} />
            </_Field>
            <_Field label="Fecha de finalización" required>
              <_Inp type="date" value={d.fin} onChange={e => set('fin', e.target.value)} />
            </_Field>
          </div>
          <_Field label="Duración" hint="Calculado automáticamente">
            <_Inp value={duracion} readOnly />
          </_Field>
        </div>
      </div>
      <div>
        <_SectionTitle Icon={DollarSign}>Monto</_SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <_Field label="Monto base" required hint="Importe del primer mes">
            <_Inp type="number" placeholder="500000" value={d.monto} onChange={e => set('monto', e.target.value)} />
          </_Field>
          <_Field label="Moneda" required>
            <_Sel opts={[
                { v: 'ARS', l: 'ARS — Peso Argentino' },
                { v: 'USD', l: 'USD — Dólar' },
              ]} value={d.moneda} onChange={e => set('moneda', e.target.value)} />
          </_Field>
        </div>
      </div>
      <div>
        <_SectionTitle Icon={TrendingUp}>Ajuste y comisión</_SectionTitle>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <_Field label="Índice de ajuste" required>
              <_Sel opts={[
                { v: 'ICL', l: 'ICL — Contratos Locación (BCRA)' },
                { v: 'IPC', l: 'IPC — Precios Consumidor (INDEC)' },
                { v: 'NINGUNO', l: 'Sin ajuste' },
              ]} value={d.indice} onChange={e => set('indice', e.target.value)} />
            </_Field>
            <_Field label="Frecuencia de ajuste">
              <_Sel opts={['Mensual', 'Bimestral', 'Trimestral', 'Cuatrimestral (cada 4 meses)', 'Semestral (cada 6 meses)', 'Anual (cada 12 meses)']}
                value={d.frecAjuste} onChange={e => set('frecAjuste', e.target.value)} />
            </_Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <_Field label="% Comisión">
              <_Inp type="number" placeholder="10" value={d.comision} onChange={e => set('comision', e.target.value)} />
            </_Field>
            <_Field label="Frecuencia de cobro">
              <_Sel opts={['Mensual', 'Bimestral', 'Trimestral']}
                value={d.frecCobro} onChange={e => set('frecCobro', e.target.value)} />
            </_Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function _StepResumen({ form }) {
  const { prop, propietario, inquilino, contrato } = form;
  const fmt = v => v ? parseInt(v).toLocaleString('es-AR') : '—';
  const monto = parseInt(contrato.monto || 0);
  const hon = Math.round(monto * (parseInt(contrato.comision || 10) / 100));

  const cardStyle = { background: _C.surf2, border: `1px solid ${_C.border}`, borderRadius: 10, padding: 14 };

  const Row = ({ k, v }) => (
    <div className="flex justify-between items-baseline py-1.5" style={{ borderBottom: `1px solid ${_C.border}` }}>
      <span style={{ fontSize: 11.5, color: _C.t3 }}>{k}</span>
      <span className="font-semibold text-right" style={{ fontSize: 12.5, color: _C.t1 }}>{v}</span>
    </div>
  );

  const CardTitle = ({ Icon: IconComp, children }) => (
    <div className="flex items-center gap-1.5 mb-3 uppercase font-bold tracking-widest"
      style={{ fontSize: 9.5, color: _C.t3 }}>
      <IconComp size={11} />
      {children}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div style={cardStyle}>
          <CardTitle Icon={Home}>Propiedad</CardTitle>
          <Row k="Dirección" v={prop.dir || '—'} />
          <Row k="Tipo" v={prop.tipo || 'Departamento'} />
        </div>
        <div style={cardStyle}>
          <CardTitle Icon={FileText}>Contrato</CardTitle>
          <Row k="Inicio" v={contrato.inicio || '—'} />
          <Row k="Fin" v={contrato.fin || '—'} />
          <Row k="Monto" v={contrato.monto ? `$${fmt(contrato.monto)}` : '—'} />
          <Row k="Índice" v={(contrato.indice || 'ICL').split(' ')[0]} />
          <Row k="Ajuste" v={contrato.frecAjuste ? contrato.frecAjuste.split(' ')[0] : '—'} />
          <Row k="Cobro" v={contrato.frecCobro || '—'} />
          <Row k="Comisión" v={`${contrato.comision || 10}%`} />
        </div>
        <div style={cardStyle}>
          <CardTitle Icon={User}>Propietario</CardTitle>
          <Row k="Nombre" v={propietario.nombre || '—'} />
          <Row k="DNI/CUIT" v={propietario.dni || '—'} />
          <Row k="Email" v={propietario.email || '—'} />
          <Row k="Teléfono" v={propietario.tel || '—'} />
        </div>
        <div style={cardStyle}>
          <CardTitle Icon={Users}>Inquilino</CardTitle>
          <Row k="Nombre" v={inquilino.nombre || '—'} />
          <Row k="DNI" v={inquilino.dni || '—'} />
          <Row k="Email" v={inquilino.email || '—'} />
          <Row k="Teléfono" v={inquilino.tel || '—'} />
        </div>
      </div>
      {monto > 0 && (
        <div style={{ ...cardStyle, background: 'rgba(237,111,40,0.06)', borderColor: 'rgba(237,111,40,0.2)' }}>
          <CardTitle Icon={DollarSign}>Resumen económico</CardTitle>
          <div className="grid grid-cols-2 gap-4 pt-1">
            {[
              { l: 'Alquiler mensual', v: `$${fmt(monto)}`, c: _C.t1 },
              { l: 'Honorarios',       v: `$${fmt(hon)}`,   c: _C.orange },
            ].map((x, i) => (
              <div key={i} className="text-center">
                <div className="mb-1.5 uppercase font-bold tracking-wider" style={{ fontSize: 9.5, color: _C.t3 }}>{x.l}</div>
                <div className="font-black tracking-tight" style={{ fontSize: 20, color: x.c, letterSpacing: '-0.4px' }}>{x.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NuevoContratoModal({ onClose, onCreate, editingItem }) {
  const _freqRevMap = { 1: 'Mensual', 2: 'Bimestral', 3: 'Trimestral', 4: 'Cuatrimestral (cada 4 meses)', 6: 'Semestral (cada 6 meses)', 12: 'Anual (cada 12 meses)' };
  const _cobRevMap  = { 1: 'Mensual', 2: 'Bimestral', 3: 'Trimestral' };
  const _idxRevMap  = { 'ICL': 'ICL', 'IPC': 'IPC', 'NINGUNO': 'NINGUNO' };
  const _curRevMap  = { 'ARS': 'ARS', 'USD': 'USD' };

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => editingItem ? {
    prop:        { dir: editingItem.propertyAddress || '', ciudad: '', prov: 'Buenos Aires (CABA)', cp: '', sup: '', amb: '2', tipo: editingItem.propertyType || 'Departamento', cond: 'Bueno', desc: '' },
    propietario: { nombre: editingItem.ownerName || '', dni: editingItem.ownerDNI || '', email: editingItem.ownerEmail || '', tel: editingItem.ownerPhone || '', cbu: '', iva: 'Consumidor Final' },
    inquilino:   { nombre: editingItem.tenantName || '', dni: editingItem.tenantDNI || '', email: editingItem.tenantEmail || '', tel: editingItem.tenantPhone || '', gNombre: '', gDni: '', gTel: '', gTipo: 'Propietario' },
    contrato:    { inicio: editingItem.startDate ? editingItem.startDate.split('T')[0] : '', fin: editingItem.endDate ? editingItem.endDate.split('T')[0] : '', diaPago: '1', monto: String(editingItem.baseRent || ''), moneda: _curRevMap[editingItem.currency] || 'ARS — Peso Argentino', deposito: '1 mes', indice: _idxRevMap[editingItem.indexType] || 'ICL — Contratos Locación (BCRA)', frecAjuste: _freqRevMap[editingItem.increaseFrequencyMonths] || 'Cuatrimestral (cada 4 meses)', comision: String(editingItem.commissionPercent ?? '10'), frecCobro: _cobRevMap[editingItem.paymentFrequencyMonths] || 'Mensual', gastos: '' },
  } : {
    prop:        { dir: '', ciudad: '', prov: 'Buenos Aires (CABA)', cp: '', sup: '', amb: '2', tipo: 'Departamento', cond: 'Bueno', desc: '' },
    propietario: { nombre: '', dni: '', email: '', tel: '', cbu: '', iva: 'Consumidor Final' },
    inquilino:   { nombre: '', dni: '', email: '', tel: '', gNombre: '', gDni: '', gTel: '', gTipo: 'Propietario' },
    contrato:    { inicio: '', fin: '', diaPago: '1', monto: '', moneda: 'ARS', deposito: '1 mes', indice: 'ICL', frecAjuste: 'Cuatrimestral (cada 4 meses)', comision: '10', frecCobro: 'Mensual', gastos: '' },
  });

  const setF = (sec, k, v) => setForm(f => ({ ...f, [sec]: { ...f[sec], [k]: v } }));

  const pct = (step / (_STEPS.length - 1)) * 100;

  const handleCreate = () => {
    if (onCreate) onCreate(form);
    onClose();
  };

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5"
      style={{ background: 'rgba(0,0,0,0.72)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="flex flex-col w-full"
        style={{
          maxWidth: 700, maxHeight: '88vh',
          background: _C.surf,
          border: `1px solid ${_C.borderB}`,
          borderRadius: 14,
          boxShadow: '0 32px 100px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0"
          style={{ padding: '17px 22px 15px', borderBottom: `1px solid ${_C.border}` }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-black text-xs flex-shrink-0"
              style={{ background: _C.orange }}>R</div>
            <span className="font-black tracking-tight" style={{ fontSize: 15.5 }}>Nuevo Contrato</span>
          </div>
          <button onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center"
            style={{ background: _C.surf2, border: `1px solid ${_C.border}`, color: _C.t2 }}>
            <X size={14} />
          </button>
        </div>

        {/* Steps bar */}
        <div className="flex items-center flex-shrink-0" style={{ padding: '14px 22px 12px' }}>
          {_STEPS.map((s, i) => {
            const st = i < step ? 'done' : i === step ? 'cur' : 'todo';
            return (
              <div key={i} className="flex items-center flex-1">
                <div
                  className="flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all cursor-pointer"
                  style={{
                    width: 24, height: 24, borderRadius: '50%',
                    background: st === 'done' ? _C.green : st === 'cur' ? _C.orDim : _C.surf3,
                    color:      st === 'done' ? '#fff'   : st === 'cur' ? _C.orange : _C.t3,
                    border:     st === 'cur' ? `2px solid ${_C.orange}` : 'none',
                  }}
                  onClick={() => i <= step && setStep(i)}
                >
                  {i < step ? <Check size={11} strokeWidth={3} /> : i + 1}
                </div>
                <span className="ml-1.5 font-semibold whitespace-nowrap"
                  style={{ fontSize: 10.5, color: st === 'todo' ? _C.t3 : st === 'cur' ? _C.t1 : _C.t2 }}>
                  {s.label}
                </span>
                {i < _STEPS.length - 1 && (
                  <div className="flex-1 mx-2 rounded-full transition-all"
                    style={{ height: 2, background: i < step ? 'rgba(237,111,40,0.4)' : _C.surf3 }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress strip */}
        <div className="flex-shrink-0" style={{ height: 2, background: _C.surf3 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: _C.orange, transition: 'width 0.4s cubic-bezier(0.16,1,0.3,1)' }} />
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1" style={{ padding: '20px 22px' }}>
          {step === 0 && <_StepPropiedad   d={form.prop}        set={(k, v) => setF('prop', k, v)} />}
          {step === 1 && <_StepPropietario d={form.propietario} set={(k, v) => setF('propietario', k, v)} />}
          {step === 2 && <_StepInquilino   d={form.inquilino}   set={(k, v) => setF('inquilino', k, v)} />}
          {step === 3 && <_StepContrato    d={form.contrato}    set={(k, v) => setF('contrato', k, v)} />}
          {step === 4 && <_StepResumen form={form} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between flex-shrink-0"
          style={{ padding: '13px 22px', borderTop: `1px solid ${_C.border}` }}>
          <div>
            <div style={{ fontSize: 11.5, color: _C.t3 }}>Paso {step + 1} de {_STEPS.length} — {_STEPS[step].label}</div>
            <div style={{ fontSize: 10.5, color: _C.t3, marginTop: 2 }}>Los datos se guardan automáticamente</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="font-semibold"
              style={{ padding: '7px 14px', borderRadius: 6, background: 'transparent', border: 'none', color: _C.t3, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancelar
            </button>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1.5 font-semibold"
                style={{ padding: '7px 15px', borderRadius: 6, background: _C.surf2, border: `1px solid ${_C.border}`, color: _C.t2, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                <ChevronLeft size={13} /> Anterior
              </button>
            )}
            {step < _STEPS.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)}
                className="flex items-center gap-1.5 font-bold"
                style={{ padding: '7px 17px', borderRadius: 6, background: _C.orange, border: 'none', color: '#fff', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                Siguiente <ChevronRight size={13} />
              </button>
            ) : (
              <button onClick={handleCreate}
                className="flex items-center gap-2 font-bold"
                style={{ padding: '8px 20px', borderRadius: 6, background: _C.orange, border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                <Plus size={14} /> {editingItem ? 'Guardar Cambios' : 'Crear Contrato'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default App;